import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Menu, MoreVertical, 
  BarChart2, User, Users, Filter, Clock, Search,
  MapPin, CheckCircle2, FileText, Plus, Pencil, Briefcase,
  Calendar, PoundSterling, X, Sun, Cloud, CloudRain,
  Phone, Mail, MessageSquare, Paperclip, Link2,
  RefreshCw, ListChecks, CreditCard, ClipboardList, ExternalLink,
  Square, CheckSquare, UserPlus, FolderInput, Bell, SkipForward, XCircle, RotateCcw, Check,
  GripVertical, CircleCheck, Circle, Send, Loader2, Navigation, AlertTriangle, Palmtree,
  ArrowUpDown, ArrowUp, ArrowDown, StickyNote, Crosshair, Sparkles, Hand, Power
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { zoneColors, assigneeColors } from '../data/csvData';
import { getRoundColor, loadRoundColors, subscribeToRoundColors } from '../utils/roundColors';
import CustomerDetailModal from '../components/CustomerDetailModal';
import { ChartWeekStrip, SlimProgressStrip, CompactJobRow } from '../components/PlannerDisplayModes';
import { LocationShareToggle } from '../components/LocationShareToggle';
import JobDetailModal from '../components/JobDetailModal';
import JobDetailPanel from '../components/JobDetailPanel';
import InvoiceSummaryModal from '../components/InvoiceSummaryModal';
import NewJobModal from '../components/NewJobModal';
import SMSComposeModal from '../components/SMSComposeModal';
import NavigationModal from '../components/NavigationModal';
import DayNoteModal from '../components/DayNoteModal';
import DayMapPanel from '../components/DayMapPanel';
import PinCorrectionPromptModal from '../components/PinCorrectionPromptModal';
import RoundOptimizerModal from '../components/RoundOptimizerModal';
import * as api from '../services/api';
import { subscribeToRefresh, customerChanged, Actions } from '../utils/dataSync';
import { cacheJobs, getCachedJobs } from '../utils/offlineCache';
import { addNotification, NotificationType } from '../utils/notificationStore';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// UTILITY FUNCTIONS
// ============================================

// Get week dates starting from Monday
const getWeekDates = (startDate) => {
  const dates = [];
  const start = new Date(startDate);
  start.setDate(start.getDate() - start.getDay() + 1);
  
  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push(date);
  }
  return dates;
};

// Format date for display
const formatDate = (date) => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return `${date.getDate()} ${days[date.getDay()]}`;
};

// Format date nicely
const formatDateNice = (dateStr) => {
  if (!dateStr) return 'Not set';
  try {
    const date = new Date(dateStr);
    const options = { weekday: 'short', day: 'numeric', month: 'short', year: '2-digit' };
    return date.toLocaleDateString('en-GB', options);
  } catch {
    return dateStr;
  }
};

// Get zone color (DB-driven via roundColors util, falls back to legacy zoneColors map)
const getZoneColor = (zone) => getRoundColor(zone);

// Get assignee color
const getAssigneeColor = (assignee) => {
  return assigneeColors[assignee] || assigneeColors['default'];
};


// Format a Date as YYYY-MM-DD in LOCAL time. NEVER use toISOString() for
// calendar dates — it converts to UTC, which shifts the day back by one for
// UK (BST) users when the Date is at local midnight (date-picker jumps).
const toLocalYMD = (d) => {
  if (!d) return d;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Parse a YYYY-MM-DD string as LOCAL midnight (new Date('YYYY-MM-DD') parses UTC).
const parseLocalDate = (v) => {
  if (v instanceof Date) return v;
  const s = String(v);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T00:00:00`) : new Date(v);
};

// Transform API job data to component format
const transformApiJob = (apiJob, weekDates) => {
  const dateStr = apiJob.scheduled_date;
  const dayIndex = weekDates.findIndex(d => toLocalYMD(d) === dateStr);
  
  return {
    id: apiJob.id,
    customerRef: apiJob.customer_id,
    customer_id: apiJob.customer_id, // Add this for compatibility with JobDetailModal
    customerName: apiJob.customer_name || 'Unknown Customer',
    customer_name: apiJob.customer_name || 'Unknown Customer',
    address: apiJob.customer_address || apiJob.address_override || apiJob.address || '',
    customer_address: apiJob.customer_address || apiJob.address_override || apiJob.address || '',
    job_address: apiJob.job_address || apiJob.address_override || '',
    zone: apiJob.customer_zone || apiJob.zone || apiJob.round || '',
    services: apiJob.services || apiJob.service || apiJob.title || 'Window Clean',
    price: apiJob.price || 0,
    frequency: apiJob.frequency || '',
    isAdhoc: !apiJob.is_recurring,
    assignee: apiJob.assignee || apiJob.assigned_to || '',
    duration: apiJob.duration || `00:${apiJob.duration_minutes || 30}`,
    nextDue: apiJob.next_due || dateStr,
    lastDone: apiJob.last_done || null,
    lastDoneWasSkipped: !!apiJob.last_done_was_skipped,
    // Also include snake_case versions for compatibility with JobDetailPanel
    next_due: apiJob.next_due || dateStr,
    last_done: apiJob.last_done || null,
    last_done_was_skipped: !!apiJob.last_done_was_skipped,
    scheduled_date: dateStr,
    date: dateStr,
    dayIndex: dayIndex >= 0 ? dayIndex : 0,
    status: apiJob.status === 'completed' ? 'done' : apiJob.status === 'skipped' ? 'skipped' : 'scheduled',
    skipReason: apiJob.skip_reason,
    invoiceId: apiJob.invoice_id,
    invoice_paid: apiJob.invoice_paid || false,  // Use actual paid status from API
    customerPhone: apiJob.customer_phone || '',
    customerEmail: apiJob.customer_email || '',
    customerBalance: 0,
    customerPaymentMethod: apiJob.customer_payment_method || 'none',
    // Saved-payment indicators — used to show small Stripe/GC badges on the
    // job card so the user can see at a glance which jobs can be auto-charged.
    customer_has_stripe: !!(apiJob.customer_has_stripe || apiJob.has_stripe_card || apiJob.stripe_customer_id),
    customer_has_gocardless: !!(apiJob.customer_has_gocardless || apiJob.gocardless_mandate_id || apiJob.gocardless_customer_id),
    // Coordinates for the day map view
    latitude: apiJob.latitude || apiJob.customer_lat || null,
    longitude: apiJob.longitude || apiJob.customer_lng || null,
    reminder_sent: apiJob.reminder_sent || false,
    reminder_sent_at: apiJob.reminder_sent_at || null,
    reminder_type: apiJob.reminder_type || null,
  };
};

// ============================================
// REDESIGNED COMPONENTS
// ============================================

// Modern Assignee Filter - Horizontal pill buttons (compact)
const AssigneeFilter = ({ selected, onSelect, staffList = [] }) => {
  const defaultColors = ['#10b981', '#84cc16', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];
  
  // Build assignees array dynamically from staffList
  const assignees = [
    { id: 'all', name: 'All', color: '#3b82f6' },
    ...staffList.map((name, idx) => ({
      id: name.toLowerCase().replace(/\s+/g, '_'),
      name: name,
      color: defaultColors[idx % defaultColors.length]
    })),
    { id: 'unassigned', name: 'Unassigned', color: '#94a3b8' },
  ];

  return (
    <div className="flex gap-1.5 px-3 py-2 overflow-x-auto no-scrollbar">
      {assignees.map((a) => (
        <button
          key={a.id}
          onClick={() => onSelect(a.id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-200 whitespace-nowrap ${
            selected === a.id 
              ? 'bg-zinc-900 text-white shadow-lg' 
              : 'bg-white text-zinc-600 hover:bg-zinc-100 border border-zinc-200'
          }`}
        >
          <div 
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: a.color }}
          />
          <span className="text-xs font-medium">{a.name}</span>
        </button>
      ))}
    </div>
  );
};

// ============================================
// MODERN TILE JOB CARD - Fresh, unique design
// ============================================

// Modern Tile Job Card Component
const SwipeableJobCard = ({ job, index = 0, onClick, isSelected, onToggleSelect, replannedDate, onComplete, onDoneAndPaid, onSkip, onUndoComplete, onUndoSkip, isWorker = false, currentStatus, workerPermissions = {}, isOverdue = false, userSettings = {}, onNavigate, onConfirmPinLocation, easyMode = false }) => {
  const [swipeX, setSwipeX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showSkipModal, setShowSkipModal] = useState(false);
  // Handover Mode silent-complete decision. ONE-OFF / Ad-hoc jobs ALWAYS
  // invoice normally (per user instruction: one-offs added to bookings@
  // are NOT part of the rounds sale — e.g. conservatory roof cleans).
  // Explicit per-job override `bypass_handover_mode` also escapes silent
  // mode for edge cases on recurring jobs.
  const _freqLc = (job.frequency || '').toLowerCase();
  const _jobIsOneOff = !!job.is_one_off || ['one-off','one off','ad hoc','ad-hoc'].some(t => _freqLc.includes(t));
  const handoverSilent = (!!userSettings?.handover_mode && !job.bypass_handover_mode && !_jobIsOneOff) || userSettings?.live_mode === false;
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUndoModal, setShowUndoModal] = useState(false);
  const [showUndoSkipModal, setShowUndoSkipModal] = useState(false);
  const [didSwipe, setDidSwipe] = useState(false);
  
  // Charge confirmation state
  const [showChargeConfirmModal, setShowChargeConfirmModal] = useState(false);
  const [chargeAmount, setChargeAmount] = useState(job.price || 0);
  const [pendingSendMethod, setPendingSendMethod] = useState(null);
  // When a customer has BOTH Stripe AND GoCardless on file, the user picks
  // which to charge. Default to whichever the backend pre-selected (Stripe wins).
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  
  // VAT calculation
  const vatEnabled = userSettings.vat_enabled || false;
  const vatPercentage = userSettings.vat_percentage || 20;
  
  // Calculate total with VAT - round to 2 decimal places to avoid floating point issues
  const calculateTotalWithVat = (price) => {
    if (!vatEnabled) return price;
    return Math.round(price * (1 + vatPercentage / 100) * 100) / 100;
  };
  
  // Always use VAT-inclusive amount for display if chargeAmount hasn't been manually edited
  const displayChargeAmount = chargeAmount === (job.price || 0) ? calculateTotalWithVat(job.price || 0) : chargeAmount;
  
  const effectiveStatus = currentStatus?.status || job.status;
  const isCompleted = effectiveStatus === 'done' || effectiveStatus === 'completed';
  const isSkipped = effectiveStatus === 'skipped';
  
  const SWIPE_THRESHOLD = 80;
  
  const hasPaymentMethod = useMemo(() => {
    const pm = job.customerPaymentMethod;
    return pm && pm !== 'none' && pm !== '';
  }, [job.customerPaymentMethod]);
  
  const paymentType = job.customerPaymentMethod || 'none';
  
  // When a customer has BOTH Stripe AND GoCardless on file the modal shows
  // a toggle so the user can pick which gets charged. Without this we always
  // default to whatever the backend pre-selected (which is Stripe — see
  // `routes/jobs.py` `customer_payment_method` priority chain).
  const hasBothPaymentMethods = !!(job.customer_has_stripe && job.customer_has_gocardless);
  // Effective payment type used by the modal labels + on confirm. Falls back
  // to whichever the backend chose so behaviour is unchanged for single-method
  // customers.
  const effectivePaymentType = selectedPaymentType || paymentType;
  
  // Format frequency nicely
  const getFrequencyShort = () => {
    const freq = job.frequency;
    if (!freq || freq === 'one-off') return null;
    const match = freq.match(/(\d+)\s*week/i);
    if (match) return `${match[1]}w`;
    return freq.replace('every ', '').substring(0, 8);
  };
  
  // Touch/Mouse handlers
  const handleStart = (clientX) => {
    setIsDragging(true);
    setStartX(clientX);
    setDidSwipe(false);
  };
  
  const handleMove = (clientX) => {
    if (!isDragging) return;
    const diff = clientX - startX;
    const limitedDiff = Math.max(-150, Math.min(150, diff));
    setSwipeX(limitedDiff);
    if (Math.abs(diff) > 10) setDidSwipe(true);
  };
  
  const handleEnd = () => {
    if (!isDragging) return;
    setIsDragging(false);
    
    // Worker auto-charge gate: owners always pass; workers pass only when
    // the owner has toggled `can_take_payments` ON. This mirrors the backend
    // gate in routes/jobs.py:complete_job — keep them in sync.
    const canCharge = !isWorker || workerPermissions?.can_take_payments;
    
    if (isCompleted && Math.abs(swipeX) > SWIPE_THRESHOLD) {
      setShowUndoModal(true);
    } else if (isSkipped && Math.abs(swipeX) > SWIPE_THRESHOLD) {
      setShowUndoSkipModal(true);
    } else if (swipeX > SWIPE_THRESHOLD) {
      // Tucks the map away so the action sheet has the full screen
      window.dispatchEvent(new Event('streakfree:collapse-day-map'));
      // HANDOVER MODE — silent roll-forward path (see handoverSilent above
      // for one-off / bypass_handover_mode exceptions).
      if (handoverSilent) {
        onComplete(job, {
          sendMethod: 'none',
          hasPaymentMethod: false,
          paymentType: null,
        });
        setSwipeX(0);
        return;
      }
      // Customer has payment method + actor is allowed to charge → show
      // the Confirm-Payment modal (Confirm & Charge OR send invoice
      // instead). On charge failure the backend's shared payment-failure
      // followup runs for owners and permitted workers alike.
      if (hasPaymentMethod && canCharge) {
        // Set charge amount with VAT included
        setChargeAmount(calculateTotalWithVat(job.price || 0));
        setPendingSendMethod('none');
        setShowChargeConfirmModal(true);
      } else if (
        !hasPaymentMethod
        && !isWorker
        && userSettings?.auto_send_invoice_no_payment
      ) {
        // Owner-only fast-path: setting ON + no saved payment method → skip
        // the channel-picker modal and let the backend dispatch via the
        // user's saved default channel.
        onComplete(job, { sendMethod: 'none', hasPaymentMethod, paymentType });
      } else {
        setShowCompleteModal(true);
      }
    } else if (swipeX < -SWIPE_THRESHOLD) {
      setShowSkipModal(true);
    }
    setSwipeX(0);
  };
  
  const handleClick = (e) => {
    if (didSwipe) { e.stopPropagation(); return; }
    onClick && onClick();
  };
  
  const handleComplete = (sendMethod = null, frontOnly = false) => {
    setShowCompleteModal(false);
    // HANDOVER MODE — silent roll-forward path
    // (see handoverSilent in component scope above).
    if (handoverSilent) {
      onComplete(job, {
        sendMethod: 'none',
        hasPaymentMethod: false,
        paymentType: null,
      });
      return;
    }
    // Worker auto-charge gate (see handleEnd for the rationale).
    const canCharge = !isWorker || workerPermissions?.can_take_payments;
    // Default: if customer has payment method (Stripe/GoCardless), no notifications
    // Otherwise, send both SMS and email invoice
    const defaultMethod = hasPaymentMethod ? 'none' : 'both';
    const finalMethod = sendMethod !== null ? sendMethod : defaultMethod;
    
    // FRONT ONLY: skip the charge-confirm modal — the backend bills the
    // minimum front-only price (£20) for the invoice AND any auto-charge.
    if (frontOnly) {
      onComplete(job, { sendMethod: finalMethod, hasPaymentMethod, paymentType, frontOnly: true });
      return;
    }
    
    if (hasPaymentMethod && canCharge) {
      // Set charge amount with VAT included
      setChargeAmount(calculateTotalWithVat(job.price || 0));
      setPendingSendMethod(finalMethod);
      setShowChargeConfirmModal(true);
      return;
    }
    
    // No payment method - proceed directly
    onComplete(job, { sendMethod: finalMethod, hasPaymentMethod, paymentType });
  };
  
  const handleConfirmCharge = () => {
    setShowChargeConfirmModal(false);
    // Pass the displayed charge amount (VAT-inclusive by default) AND the
    // user's chosen payment route (relevant when the customer has both
    // Stripe and GoCardless — without this the backend always picks Stripe).
    onComplete(job, { 
      sendMethod: pendingSendMethod, 
      hasPaymentMethod, 
      paymentType: effectivePaymentType,
      chargeAmount: displayChargeAmount 
    });
  };
  
  const handleCancelCharge = () => {
    setShowChargeConfirmModal(false);
    setPendingSendMethod(null);
    setSelectedPaymentType(null); // reset so next swipe starts fresh on the default
  };
  
  const handleSkip = (reason = 'Customer unavailable') => {
    setShowSkipModal(false);
    onSkip(job, reason);
  };
  
  // Swipe visual calculations
  const rightBgOpacity = Math.min(swipeX / SWIPE_THRESHOLD, 1);
  const leftBgOpacity = Math.min(-swipeX / SWIPE_THRESHOLD, 1);
  const iconScale = Math.min(Math.abs(swipeX) / SWIPE_THRESHOLD, 1);
  
  // Get address parts
  const addressParts = (job.job_address || job.address_override || job.customer_address || job.address || 'No address').split(',').map(p => p.trim());
  const streetAddress = addressParts.slice(0, 2).join(', ');
  
  return (
    <>
      <div className="relative mb-2 mx-2">
        {/* Background swipe actions */}
        <div className="absolute inset-0 rounded-xl overflow-hidden flex">
          {isCompleted || isSkipped ? (
            <>
              <div className="flex-1 bg-gradient-to-r from-slate-700 to-slate-600 flex items-center justify-end px-6" style={{ opacity: leftBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <RotateCcw size={28} className="mx-auto mb-1" />
                  <span className="text-sm font-semibold">Undo</span>
                </div>
              </div>
              <div className="flex-1 bg-gradient-to-l from-slate-700 to-slate-600 flex items-center justify-start px-6" style={{ opacity: rightBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <RotateCcw size={28} className="mx-auto mb-1" />
                  <span className="text-sm font-semibold">Undo</span>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="flex-1 bg-gradient-to-r from-amber-500 to-amber-400 flex items-center justify-end px-6" style={{ opacity: leftBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <SkipForward size={28} className="mx-auto mb-1" />
                  <span className="text-sm font-semibold">Skip</span>
                </div>
              </div>
              <div className="flex-1 bg-gradient-to-l from-emerald-500 to-emerald-400 flex items-center justify-start px-6" style={{ opacity: rightBgOpacity }}>
                <div className="text-white text-center" style={{ transform: `scale(${0.7 + iconScale * 0.3})` }}>
                  <CheckCircle2 size={28} className="mx-auto mb-1" />
                  <span className="text-sm font-semibold">Done</span>
                </div>
              </div>
            </>
          )}
        </div>
        
        {/* Modern Tile Card */}
        <div 
          data-testid={`job-card-${job.id}`}
          className={`relative rounded-xl overflow-hidden cursor-pointer ${
            isSelected ? 'ring-2 ring-blue-500' : ''
          } ${isCompleted ? 'opacity-70' : ''} ${isSkipped ? 'opacity-60' : ''} ${isOverdue ? 'ring-2 ring-red-400' : ''}`}
          style={{ 
            transform: `translateX(${swipeX}px)`,
            transition: isDragging ? 'none' : 'transform 0.2s ease-out',
            background: isCompleted 
              ? '#d1fae5' 
              : isSkipped 
                ? '#fef3c7'
                : isOverdue
                  ? '#fee2e2'
                  : '#ffffff',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
          }}
          onClick={handleClick}
          onTouchStart={(e) => handleStart(e.touches[0].clientX)}
          onTouchMove={(e) => handleMove(e.touches[0].clientX)}
          onTouchEnd={handleEnd}
          onMouseDown={(e) => handleStart(e.clientX)}
          onMouseMove={(e) => isDragging && handleMove(e.clientX)}
          onMouseUp={handleEnd}
          onMouseLeave={() => isDragging && handleEnd()}
        >
          {/* Left status stripe */}
          <div className={`absolute left-0 top-0 bottom-0 w-1 ${
            isCompleted ? 'bg-emerald-500' :
            isSkipped ? 'bg-amber-500' :
            isOverdue ? 'bg-red-500' :
            'bg-slate-300'
          }`} />
          
          {/* Main Content — extra-tall padding keeps tap targets safe and
              gives the address room to wrap onto two lines if needed. */}
          <div className="pl-4 pr-3 py-6">
            {/* Top Row: Number, Name, Assignee, Round, Price */}
            <div className="flex items-start gap-2">
              {/* Numbered Circle */}
              {!isWorker && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleSelect && onToggleSelect(); }}
                  className={`flex-shrink-0 mt-0.5 w-7 h-7 flex items-center justify-center rounded-full font-bold text-xs ${
                    isCompleted ? 'bg-emerald-500 text-white' :
                    isSkipped ? 'bg-amber-500 text-white' :
                    isSelected ? 'bg-blue-500 text-white' : 
                    'bg-slate-700 text-white'
                  }`}
                  data-testid={`job-number-${job.id}`}
                >
                  {index + 1}
                </button>
              )}
              
              {/* Name & Address — address now wraps to as many lines as it
                  needs (no `truncate`) so workers can read the full street
                  on the card without tapping in. */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-semibold ${easyMode ? 'text-lg' : 'text-sm'} ${isCompleted || isSkipped ? 'text-slate-500' : 'text-slate-900'}`}>
                    {job.customer_name || job.customerName}
                  </span>
                  {job.assignee && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded font-medium">
                      {job.assignee.split(' ')[0]}
                    </span>
                  )}
                </div>
                <div className={`${easyMode ? 'text-base text-slate-500' : 'text-xs text-slate-400'} break-words mt-0.5`}>{streetAddress}</div>
                {/* "Reminded" pill — surfaced under the address when a SMS/
                    email reminder has been sent for this visit. Saves the
                    user from having to open the customer card just to check
                    whether they've already chased them. Cleared automatically
                    when the job is completed / rolls forward to a new
                    occurrence. */}
                {job.reminder_sent && !isCompleted && !isSkipped && (
                  <div className="mt-1">
                    <span
                      data-testid={`reminded-pill-${job.id}`}
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700"
                      title={job.reminder_sent_at ? `Reminder sent ${new Date(job.reminder_sent_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : 'Reminder sent'}
                    >
                      <Bell size={9} />
                      Reminded
                    </span>
                  </div>
                )}
              </div>
              
              {/* Round/Zone pill — only rendered for REAL round names.
                  Skip the literal sentinel "Unassigned" string which means
                  "no round set" (vs an actual round called Unassigned). The
                  owner found this misleading: their card looked unassigned
                  even after the job had been assigned to a worker, because
                  this pill rendered the empty-state sentinel verbatim. */}
              {(() => {
                const raw = job.round || job.zone;
                const isRealRound = raw && String(raw).trim().toLowerCase() !== 'unassigned';
                if (!isRealRound) return null;
                return (
                  <span
                    className="flex-shrink-0 mt-0.5 text-xs px-2 py-0.5 rounded font-medium"
                    style={{
                      backgroundColor: `${getZoneColor(raw)}20`,
                      color: getZoneColor(raw)
                    }}
                  >
                    {raw}
                  </span>
                );
              })()}

              {/* Saved-payment badges next to round — at-a-glance which jobs auto-charge */}
              {job.customer_has_stripe && (
                <span
                  data-testid={`stripe-badge-${job.id}`}
                  title="Saved Stripe card on file"
                  className="flex-shrink-0 mt-0.5 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#635BFF] text-white"
                >
                  Stripe
                </span>
              )}
              {job.customer_has_gocardless && (
                <span
                  data-testid={`gc-badge-${job.id}`}
                  title="Active GoCardless direct debit"
                  className="flex-shrink-0 mt-0.5 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#1E3264] text-white"
                >
                  GC
                </span>
              )}
              
              {/* Price - show VAT-inclusive total for completed jobs */}
              {!isWorker && (
                <div className="flex-shrink-0 flex flex-col items-end">
                  <div className={`font-bold text-base ${isCompleted ? 'text-emerald-600' : 'text-slate-900'}`}>
                    £{isCompleted ? calculateTotalWithVat(job.price || 0).toFixed(2) : (job.price || 0).toFixed(2)}
                  </div>
                  {isCompleted && (job.invoice_paid || job.invoicePaid) && (
                    <span className="text-[10px] font-bold text-white bg-emerald-500 px-1.5 py-0.5 rounded mt-0.5">
                      PAID
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {/* Action Buttons Row — tall buttons for safe thumb-hits. */}
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-100">
              {isCompleted ? (
                <button 
                  onClick={(e) => { e.stopPropagation(); onUndoComplete(job); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-semibold"
                >
                  <RotateCcw size={14} />
                  Undo Complete
                </button>
              ) : isSkipped ? (
                <button 
                  onClick={(e) => { e.stopPropagation(); onUndoSkip && onUndoSkip(job); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-xs font-semibold"
                  data-testid={`undo-skip-${job.id}`}
                >
                  <RotateCcw size={14} />
                  Undo Skip
                </button>
              ) : (
                <>
                  <button
                    onClick={(e) => { e.stopPropagation(); setShowSkipModal(true); }}
                    className="flex-1 flex items-center justify-center gap-1 py-3.5 bg-amber-50 hover:bg-amber-100 text-amber-600 rounded-lg text-xs font-semibold"
                  >
                    <SkipForward size={14} />
                    Skip
                  </button>
                  <button
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      // Tucks the map away so the action sheet has the full screen
                      window.dispatchEvent(new Event('streakfree:collapse-day-map'));
                      // HANDOVER MODE — silent roll-forward path
                      // (see handoverSilent in component scope above).
                      if (handoverSilent) {
                        onComplete(job, {
                          sendMethod: 'none',
                          hasPaymentMethod: false,
                          paymentType: null,
                        });
                        return;
                      }
                      // Worker auto-charge gate (see handleEnd) — mirrors
                      // the backend gate in routes/jobs.py:complete_job.
                      const canCharge = !isWorker || workerPermissions?.can_take_payments;
                      if (hasPaymentMethod && canCharge) {
                        // Set charge amount with VAT included
                        setChargeAmount(calculateTotalWithVat(job.price || 0));
                        setPendingSendMethod('none');
                        setShowChargeConfirmModal(true);
                      } else if (
                        !hasPaymentMethod
                        && !isWorker
                        && userSettings?.auto_send_invoice_no_payment
                      ) {
                        // Owner-only fast-path (see handleEnd).
                        onComplete(job, { sendMethod: 'none', hasPaymentMethod, paymentType });
                      } else {
                        setShowCompleteModal(true);
                      }
                    }}
                    className={`flex-1 flex items-center justify-center gap-1 ${easyMode ? 'py-5 text-base' : 'py-3.5 text-xs'} bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold`}
                  >
                    <Check size={easyMode ? 20 : 14} strokeWidth={2.5} />
                    Done
                  </button>
                  {/* Paid button: admin users always see it; workers see it
                      only when the owner has enabled `can_take_payments`. */}
                  {(!isWorker || workerPermissions?.can_take_payments) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowPaymentModal(true); }}
                      className={`flex-1 flex items-center justify-center gap-1 ${easyMode ? 'py-5 text-base' : 'py-3.5 text-xs'} bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg font-semibold`}
                    >
                      <CreditCard size={easyMode ? 20 : 14} />
                      Paid
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Complete Job Modal */}
      {showCompleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={() => setShowCompleteModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            
            <div className="px-6 pb-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">Finished at {streetAddress}?</h2>
              <p className="text-zinc-500 text-center mt-2">
                {job.customerName || job.customer_name} {(!isWorker || workerPermissions?.can_take_payments) && `- £${calculateTotalWithVat(job.price || 0).toFixed(2)}`}
              </p>
            </div>
            
            {/* Payment-status banner — shown to owners always, and to
                workers who hold the `can_take_payments` permission (their
                Done press will attempt the same auto-charge / send-invoice
                flow as the owner, so they need to see what's about to
                happen). Workers without permission see the simplified
                "Mark as Done" button below — no banner needed. */}
            {(!isWorker || workerPermissions?.can_take_payments) && (
              <div className="px-6 pb-4">
                {hasPaymentMethod ? (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <CreditCard size={20} className="text-emerald-600" />
                      <div>
                        <p className="font-medium text-emerald-900">Auto-charge enabled</p>
                        <p className="text-sm text-emerald-700">
                          Payment will be taken via {paymentType === 'stripe' ? 'Stripe' : 'GoCardless'} - Invoice marked as Paid
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <PoundSterling size={20} className="text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-900">No payment method</p>
                        <p className="text-sm text-amber-700">
                          Invoice will be created and added to Owing list
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Worker WITHOUT can_take_payments → simplified "Mark as Done"
                button only (no payment, no invoice channel chooser). Workers
                WITH can_take_payments fall through to the full owner UX
                below (channel chooser + auto-charge confirm). */}
            {isWorker && !workerPermissions?.can_take_payments ? (
              <div className="px-6 pb-6 space-y-3">
                <button
                  onClick={() => handleComplete('none')}
                  className="w-full flex items-center justify-center gap-2 py-5 bg-emerald-500 text-white rounded-xl text-lg font-bold hover:bg-emerald-600 transition-colors"
                  data-testid="worker-mark-done"
                >
                  <CheckCircle2 size={24} />
                  YES
                </button>
                <button
                  onClick={() => handleComplete('none', true)}
                  className="w-full flex items-center justify-center gap-2 py-5 bg-sky-500 text-white rounded-xl text-lg font-bold hover:bg-sky-600 transition-colors"
                  data-testid="worker-mark-done-front-only"
                >
                  <CheckCircle2 size={24} />
                  YES, FRONT ONLY
                </button>
                <button
                  onClick={() => setShowCompleteModal(false)}
                  className="w-full py-5 bg-zinc-100 text-zinc-700 rounded-xl text-lg font-bold hover:bg-zinc-200 transition-colors"
                  data-testid="worker-mark-done-cancel"
                >
                  NO
                </button>
              </div>
            ) : (
              /* For ADMIN - Send invoice options */
              <div className="px-6 pb-6">
                <p className="text-sm font-medium text-zinc-700 mb-3">Send invoice via:</p>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <button
                    onClick={() => handleComplete('sms')}
                    className="flex flex-col items-center justify-center gap-1.5 py-3 bg-[#25d366] text-white rounded-xl font-medium hover:bg-[#1ebe5b] transition-colors"
                    data-testid="complete-send-whatsapp"
                    title="Sends WhatsApp if available, falls back to SMS automatically"
                  >
                    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    <span className="text-sm">WhatsApp</span>
                  </button>
                  <button
                    onClick={() => handleComplete('email')}
                    className="flex flex-col items-center justify-center gap-1.5 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                    data-testid="complete-send-email"
                  >
                    <Mail size={20} />
                    <span className="text-sm">Email</span>
                  </button>
                  <button
                    onClick={() => handleComplete('both')}
                    className="flex flex-col items-center justify-center gap-1.5 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors"
                    data-testid="complete-send-both"
                  >
                    <Send size={18} />
                    <span className="text-sm">Both</span>
                  </button>
                </div>
                <p className="text-[11px] text-zinc-500 text-center mb-3 -mt-1">
                  WhatsApp auto-falls back to SMS if the customer isn't on WhatsApp.
                </p>
                <button
                  onClick={() => handleComplete('none')}
                  className="w-full py-3 bg-zinc-100 text-zinc-700 rounded-xl font-medium hover:bg-zinc-200 transition-colors mb-2"
                  data-testid="complete-no-send"
                >
                  Don't Send
                </button>
                <button
                  onClick={() => handleComplete(null, true)}
                  className="w-full py-3 bg-sky-50 border border-sky-200 text-sky-700 rounded-xl font-semibold hover:bg-sky-100 transition-colors"
                  data-testid="complete-front-only"
                >
                  Front Only Clean — £20 min charge
                </button>
              </div>
            )}
            
            <div className="px-6 pb-8">
              <button
                onClick={() => setShowCompleteModal(false)}
                className="w-full py-3 text-zinc-500 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Charge Confirmation Modal - for customers with saved payment methods */}
      {showChargeConfirmModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={handleCancelCharge}>
          <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            
            <div className="px-6 pb-4">
              <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">Confirm Payment</h2>
              <p className="text-zinc-500 text-center mt-2">
                {job.customerName}
              </p>
            </div>
            
            {/* Payment method info */}
            <div className="px-6 pb-4">
              <div className="bg-zinc-50 rounded-xl p-4 mb-4">
                {hasBothPaymentMethods ? (
                  // Customer has BOTH — let the user pick which to charge.
                  <>
                    <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">
                      Charge with
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-3" data-testid="payment-method-picker">
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentType('stripe')}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          (effectivePaymentType === 'stripe' || effectivePaymentType === 'card')
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-zinc-200 bg-white hover:border-zinc-300'
                        }`}
                        data-testid="select-stripe"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard size={16} className="text-purple-600" />
                          <span className="font-semibold text-sm text-zinc-900">Stripe</span>
                        </div>
                        <p className="text-[11px] text-zinc-500">Card on file</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedPaymentType('gocardless')}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          effectivePaymentType === 'gocardless'
                            ? 'border-cyan-500 bg-cyan-50'
                            : 'border-zinc-200 bg-white hover:border-zinc-300'
                        }`}
                        data-testid="select-gocardless"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <CreditCard size={16} className="text-cyan-600" />
                          <span className="font-semibold text-sm text-zinc-900">GoCardless</span>
                        </div>
                        <p className="text-[11px] text-zinc-500">Direct debit</p>
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${effectivePaymentType === 'stripe' || effectivePaymentType === 'card' ? 'bg-purple-100' : 'bg-cyan-100'}`}>
                      <CreditCard size={18} className={effectivePaymentType === 'stripe' || effectivePaymentType === 'card' ? 'text-purple-600' : 'text-cyan-600'} />
                    </div>
                    <div>
                      <p className="font-medium text-zinc-900">
                        {effectivePaymentType === 'stripe' || effectivePaymentType === 'card' ? 'Charge to Stripe Card' : 'Charge via GoCardless'}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {effectivePaymentType === 'stripe' || effectivePaymentType === 'card' ? 'Card on file will be charged' : 'Direct debit will be collected'}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Editable amount with VAT breakdown */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-zinc-700 mb-2">Amount to charge (inc. VAT):</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 font-medium">£</span>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={displayChargeAmount.toFixed(2)}
                      onChange={(e) => setChargeAmount(parseFloat(e.target.value) || 0)}
                      className="w-full pl-8 pr-4 py-3 text-lg font-bold text-zinc-900 bg-white border border-zinc-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      data-testid="charge-amount-input"
                    />
                  </div>
                  {vatEnabled && (
                    <div className="mt-2 text-xs text-zinc-500 space-y-1">
                      <div className="flex justify-between">
                        <span>Job price:</span>
                        <span>£{(job.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>VAT ({vatPercentage}%):</span>
                        <span>£{((job.price || 0) * vatPercentage / 100).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  {displayChargeAmount.toFixed(2) !== calculateTotalWithVat(job.price || 0).toFixed(2) && (
                    <p className="text-xs text-amber-600 mt-1">
                      Original total: £{calculateTotalWithVat(job.price || 0).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Confirm/Cancel buttons */}
            <div className="px-6 pb-6 space-y-2">
              <button
                onClick={handleConfirmCharge}
                className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 transition-colors"
                data-testid="confirm-charge-btn"
              >
                <CheckCircle2 size={20} />
                Confirm & Charge £{displayChargeAmount.toFixed(2)}
              </button>
              
              {/* Send Invoice Instead options */}
              <div className="flex items-center gap-2 py-2">
                <div className="flex-1 h-px bg-zinc-200" />
                <span className="text-xs text-zinc-400 font-medium">or send invoice</span>
                <div className="flex-1 h-px bg-zinc-200" />
              </div>
              
              <div className="flex gap-2">
                {job.customerEmail && (
                  <button
                    onClick={() => {
                      // "Send invoice instead" — bypass auto-charge entirely.
                      // We call onComplete directly (skipping handleComplete,
                      // which would re-open this same modal in a loop) and
                      // tell the backend not to auto-charge.
                      setShowChargeConfirmModal(false);
                      onComplete(job, {
                        sendMethod: 'email',
                        hasPaymentMethod: false,
                        paymentType: null,
                        skipAutoCharge: true,
                      });
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                    data-testid="send-email-btn"
                  >
                    <Mail size={18} />
                    Email
                  </button>
                )}
                {job.customerPhone && (
                  <button
                    onClick={() => {
                      setShowChargeConfirmModal(false);
                      onComplete(job, {
                        sendMethod: 'sms',
                        hasPaymentMethod: false,
                        paymentType: null,
                        skipAutoCharge: true,
                      });
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors"
                    data-testid="send-sms-btn"
                  >
                    <MessageSquare size={18} />
                    SMS
                  </button>
                )}
                {job.customerEmail && job.customerPhone && (
                  <button
                    onClick={() => {
                      setShowChargeConfirmModal(false);
                      onComplete(job, {
                        sendMethod: 'both',
                        hasPaymentMethod: false,
                        paymentType: null,
                        skipAutoCharge: true,
                      });
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 transition-colors"
                    data-testid="send-both-btn"
                  >
                    <Send size={18} />
                    Both
                  </button>
                )}
              </div>
              
              <button
                onClick={handleCancelCharge}
                className="w-full py-3 bg-zinc-100 text-zinc-700 rounded-xl font-medium hover:bg-zinc-200 transition-colors"
                data-testid="cancel-charge-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Skip Job Modal */}
      {showSkipModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={() => setShowSkipModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            
            <div className="px-6 pb-4">
              <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <SkipForward size={32} className="text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">Skip this job?</h2>
              <p className="text-zinc-500 text-center mt-2">
                {job.customer_name || job.customerName} - {(job.customer_address || job.address || 'No address').split(',')[0]}
              </p>
            </div>
            
            {/* Skip reasons */}
            <div className="px-6 pb-6 space-y-2">
              <p className="text-sm font-medium text-zinc-700 mb-3">Select reason:</p>
              {[
                'Customer unavailable',
                'Weather conditions',
                'Access blocked',
                'Customer cancelled',
                'Running late',
                'Other'
              ].map((reason) => (
                <button
                  key={reason}
                  onClick={() => handleSkip(reason)}
                  className="w-full py-3 px-4 text-left bg-zinc-50 hover:bg-zinc-100 rounded-xl transition-colors"
                >
                  {reason}
                </button>
              ))}
            </div>
            
            <div className="px-6 pb-8">
              <button
                onClick={() => setShowSkipModal(false)}
                className="w-full py-3 text-zinc-500 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Method Modal (Done & Paid) */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={() => setShowPaymentModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            
            <div className="px-6 pb-4">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CreditCard size={32} className="text-indigo-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">How was this paid?</h2>
              <p className="text-zinc-500 text-center mt-2">
                {job.customer_name || job.customerName} - £{calculateTotalWithVat(job.price || 0).toFixed(2)}
              </p>
            </div>
            
            {/* Payment method options */}
            <div className="px-6 pb-6 space-y-2">
              {[
                { id: 'cash', name: 'Cash', color: '#ecfdf5' },
                { id: 'bank_transfer', name: 'Bank Transfer', color: '#eff6ff' },
                { id: 'card', name: 'Card', color: '#f5f3ff' },
                { id: 'cheque', name: 'Cheque', color: '#fff7ed' },
                { id: 'other', name: 'Other', color: '#f9fafb' },
              ].map(({ id, name, color }) => (
                <button
                  key={id}
                  onClick={() => {
                    setShowPaymentModal(false);
                    onDoneAndPaid && onDoneAndPaid(job, { paymentMethod: id });
                  }}
                  className="w-full flex items-center gap-3 p-4 rounded-xl transition-colors hover:opacity-80"
                  style={{ backgroundColor: color }}
                >
                  <CreditCard size={20} />
                  <span className="font-medium">{name}</span>
                </button>
              ))}
            </div>
            
            <div className="px-6 pb-8">
              <button
                onClick={() => setShowPaymentModal(false)}
                className="w-full py-3 text-zinc-500 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Undo Complete Modal */}
      {showUndoModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={() => setShowUndoModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            
            <div className="px-6 pb-4">
              <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <XCircle size={32} className="text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">Mark as Not Complete?</h2>
              <p className="text-zinc-500 text-center mt-2">
                {job.customer_name || job.customerName}
              </p>
            </div>
            
            <div className="px-6 pb-4">
              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
                <p className="text-sm text-orange-800">
                  This will revert the job back to scheduled status. If an invoice was created, it will be deleted (if unpaid) or remain on the customer's account (if paid).
                </p>
              </div>
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => { setShowUndoModal(false); onUndoComplete && onUndoComplete(job); }}
                className="w-full flex items-center justify-center gap-2 py-4 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              >
                <XCircle size={20} />
                Mark Not Complete
              </button>
            </div>
            
            <div className="px-6 pb-8">
              <button
                onClick={() => setShowUndoModal(false)}
                className="w-full py-3 text-zinc-500 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Undo Skip Modal */}
      {showUndoSkipModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center" onClick={() => setShowUndoSkipModal(false)}>
          <div className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex justify-center py-3">
              <div className="w-10 h-1 bg-zinc-300 rounded-full" />
            </div>
            
            <div className="px-6 pb-4">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <RotateCcw size={32} className="text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-zinc-900 text-center">Undo Skip?</h2>
              <p className="text-zinc-500 text-center mt-2">
                {job.customer_name || job.customerName}
              </p>
              {job.skipReason && (
                <p className="text-zinc-400 text-sm text-center mt-1">
                  Skip reason: {job.skipReason}
                </p>
              )}
            </div>
            
            <div className="px-6 pb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <p className="text-sm text-blue-800">
                  This will revert the job back to scheduled status so it can be completed or rescheduled.
                </p>
              </div>
            </div>
            
            <div className="px-6 pb-6">
              <button
                onClick={() => { setShowUndoSkipModal(false); onUndoSkip && onUndoSkip(job); }}
                className="w-full flex items-center justify-center gap-2 py-4 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors"
                data-testid="confirm-undo-skip"
              >
                <RotateCcw size={20} />
                Undo Skip
              </button>
            </div>
            
            <div className="px-6 pb-8">
              <button
                onClick={() => setShowUndoSkipModal(false)}
                className="w-full py-3 text-zinc-500 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Sortable wrapper for job cards - enables drag-and-drop reordering
// Requires holding finger down for 600ms before drag activates
const SortableJobCard = ({ job, children, isDragDisabled = false }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: job.id,
    disabled: isDragDisabled
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    // Allow normal touch scrolling - drag only activates after hold delay
    touchAction: 'pan-y',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative group"
      {...(isDragDisabled ? {} : { ...attributes, ...listeners })}
    >
      {children}
    </div>
  );
};

// Modern Actions Modal - Bottom Sheet style
const JobActionsModal = ({ selectedCount, totalValue, onClose, onAction }) => {
  const actions = [
    { id: 're-assign', label: 'Re-assign', icon: UserPlus, color: 'text-emerald-600' },
    { id: 'move-to-round', label: 'Move to round', icon: FolderInput, color: 'text-purple-600' },
    { id: 'message-customers', label: 'Message customers', icon: MessageSquare, color: 'text-zinc-600' },
    { id: 'email-reminder', label: 'Email Reminder', icon: Mail, color: 'text-amber-600' },
    { id: 'text-reminder', label: 'Text Reminder', icon: Phone, color: 'text-green-600' },
    { id: 'mark-as-done', label: 'Mark as done', icon: CheckCircle2, color: 'text-emerald-600' },
    { id: 'mark-as-skipped', label: 'Mark as skipped', icon: SkipForward, color: 'text-orange-600' },
    { id: 'mark-as-not-done', label: 'Mark as not done', icon: XCircle, color: 'text-red-600' },
    { id: 'reset-schedules', label: 'Reset Schedules', icon: RotateCcw, color: 'text-zinc-500' },
  ];

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end justify-center" onClick={onClose}>
      <div 
        className="w-full max-w-lg bg-white rounded-t-3xl overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="flex justify-center py-3">
          <div className="w-10 h-1 bg-zinc-300 rounded-full" />
        </div>
        
        {/* Header */}
        <div className="px-6 pb-4">
          <h2 className="text-xl font-bold text-zinc-900">Actions</h2>
          <p className="text-zinc-500 mt-1">
            {selectedCount} job{selectedCount > 1 ? 's' : ''} selected · £{totalValue.toFixed(2)}
          </p>
        </div>
        
        {/* Actions grid */}
        <div className="px-4 pb-8 max-h-[50vh] overflow-y-auto">
          <div className="grid grid-cols-3 gap-3">
            {actions.map(action => {
              const Icon = action.icon;
              return (
                <button
                  key={action.id}
                  onClick={() => onAction(action.id)}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-zinc-50 hover:bg-zinc-100 active:scale-95 transition-all"
                  data-testid={`action-${action.id}`}
                >
                  <div className={`w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center ${action.color}`}>
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <span className="text-xs font-medium text-zinc-700 text-center leading-tight">{action.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Notify Customers Modal — bulk SMS / email blast to every customer
 * scheduled on the selected date(s). Built for emergencies (van written
 * off, snow day, owner ill) where one message has to go out fast.
 *
 * NOT to be confused with the regular reminder flow (SMS Reminders) — that
 * one uses saved templates per-customer; this one is a one-off broadcast
 * with a single body the owner types in.
 */
const NotifyCustomersModal = ({ defaultDate, onClose, apiUrl, token }) => {
  const dateStr = defaultDate ? (defaultDate.toISOString ? defaultDate.toISOString().slice(0, 10) : String(defaultDate).slice(0, 10)) : '';
  const [startDate, setStartDate] = useState(dateStr);
  const [endDate, setEndDate] = useState(dateStr);
  const [channel, setChannel] = useState('sms');
  const [message, setMessage] = useState(
    "Hi [first_name], unfortunately we won't be able to clean your windows on [date] — our van is off the road. We'll be in touch shortly to reschedule. Sorry for the inconvenience. — [company_name]"
  );
  const [subject, setSubject] = useState('Update from [company_name]');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Load preview when date range or channel changes
  useEffect(() => {
    if (!startDate) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${apiUrl}/api/jobs/notify-customers/preview`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        start_date: startDate,
        end_date: endDate || startDate,
        channel,
        message: 'preview',
      }),
    })
      .then(r => r.json())
      .then(d => { if (!cancelled) setPreview(d); })
      .catch(() => { if (!cancelled) setPreview({ total: 0, recipients: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [startDate, endDate, channel, apiUrl, token]);

  const insertToken = (tok) => {
    setMessage(m => `${m}${tok}`);
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    if (sending) return;
    const reachableSms = preview?.reachable_sms || 0;
    const reachableEmail = preview?.reachable_email || 0;
    const sendCount = (channel === 'sms') ? reachableSms
      : (channel === 'email') ? reachableEmail
      : (preview?.total || 0);
    if (sendCount === 0) {
      alert('No reachable customers on those dates.');
      return;
    }
    const confirmText = channel === 'both'
      ? `Send to ${preview?.total || 0} customers via SMS + Email?`
      : `Send to ${sendCount} customer${sendCount === 1 ? '' : 's'} via ${channel === 'sms' ? 'SMS' : 'Email'}?`;
    if (!window.confirm(confirmText)) return;
    setSending(true);
    setResult(null);
    try {
      const r = await fetch(`${apiUrl}/api/jobs/notify-customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: startDate,
          end_date: endDate || startDate,
          channel,
          message,
          email_subject: subject,
        }),
      });
      const data = await r.json();
      setResult(data);
    } catch (e) {
      setResult({ error: String(e) });
    } finally {
      setSending(false);
    }
  };

  const charCount = message.length;
  const smsSegments = charCount > 0 ? Math.ceil(charCount / 160) : 0;
  const channelLabel = channel === 'sms' ? 'SMS' : channel === 'email' ? 'Email' : 'SMS + Email';

  // After successful send — show a summary card and close button
  if (result && !result.error) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="notify-result-modal">
        <div className="bg-white rounded-3xl max-w-md w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
          <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
            <h2 className="text-2xl font-bold mb-1">Sent</h2>
            <p className="text-emerald-50">{result.message}</p>
          </div>
          <div className="p-6 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Total customers</span>
              <span className="font-semibold text-zinc-900">{result.total}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">SMS sent</span>
              <span className="font-semibold text-blue-600">{result.sent_sms}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-zinc-500">Emails sent</span>
              <span className="font-semibold text-amber-600">{result.sent_email}</span>
            </div>
            {result.failed?.length > 0 && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm">
                <p className="font-semibold text-red-700 mb-2">{result.failed.length} failed:</p>
                <ul className="space-y-1 text-xs text-red-800 max-h-32 overflow-y-auto">
                  {result.failed.slice(0, 20).map((f, i) => (
                    <li key={i}>{f.name} — {f.channel}: {f.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={onClose}
              data-testid="notify-result-close"
              className="w-full mt-4 py-3 bg-zinc-900 text-white rounded-2xl font-semibold hover:bg-zinc-800"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose} data-testid="notify-customers-modal">
      <div className="bg-white w-full sm:max-w-lg modal-shell-h rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-indigo-600 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">Notify Customers</h2>
              <p className="text-indigo-100 text-sm mt-1">
                {loading ? 'Loading…' : preview ? (
                  channel === 'sms' ? `${preview.reachable_sms} SMS-reachable`
                  : channel === 'email' ? `${preview.reachable_email} Email-reachable`
                  : `${preview.total} customer${preview.total === 1 ? '' : 's'} · SMS + Email`
                ) : ''}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg" data-testid="notify-close-btn">
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">From</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="notify-start-date"
                className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="notify-end-date"
                className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm"
              />
            </div>
          </div>

          {/* Channel */}
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Notify using</label>
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              data-testid="notify-channel-select"
              className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm bg-white"
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
              <option value="both">SMS and Email</option>
            </select>
          </div>

          {/* Subject (email only) */}
          {channel !== 'sms' && (
            <div>
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Email subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="notify-subject"
                className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm"
              />
            </div>
          )}

          {/* Token chips */}
          <div>
            <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Insert token</label>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {['[first_name]', '[name]', '[service]', '[date]', '[address]', '[company_name]'].map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => insertToken(t)}
                  data-testid={`notify-token-${t.replace(/[\[\]]/g, '')}`}
                  className="px-2.5 py-1 text-xs font-medium rounded-full bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Message body */}
          <div>
            <div className="flex justify-between items-baseline">
              <label className="text-xs uppercase tracking-wide text-zinc-500 font-semibold">Message</label>
              <span className="text-xs text-zinc-400">
                {charCount} char{charCount === 1 ? '' : 's'}
                {channel !== 'email' && smsSegments > 1 && ` · ${smsSegments} segments`}
              </span>
            </div>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              data-testid="notify-message-body"
              className="w-full mt-1 px-3 py-2 border border-zinc-200 rounded-xl focus:border-indigo-500 outline-none text-sm font-mono"
            />
          </div>

          {/* Recipient preview */}
          {preview?.recipients?.length > 0 && (
            <details className="bg-zinc-50 rounded-xl">
              <summary className="cursor-pointer px-3 py-2 text-sm font-semibold text-zinc-700">
                Recipients ({preview.recipients.length})
              </summary>
              <div className="px-3 pb-3 max-h-48 overflow-y-auto">
                <ul className="text-xs text-zinc-600 space-y-1">
                  {preview.recipients.map(r => (
                    <li key={r.id} className="flex justify-between items-center py-1 border-b border-zinc-100 last:border-0">
                      <span className="truncate">{r.name}</span>
                      <span className="ml-2 flex gap-1 shrink-0">
                        {r.has_phone && <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-[10px]">SMS</span>}
                        {r.has_email && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px]">Email</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </details>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-200 flex gap-3">
          <button
            onClick={onClose}
            data-testid="notify-cancel-btn"
            className="flex-1 py-3 bg-zinc-100 text-zinc-700 rounded-2xl font-semibold hover:bg-zinc-200"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !preview || preview.total === 0 || !message.trim()}
            data-testid="notify-send-btn"
            className="flex-[2] py-3 bg-gradient-to-br from-indigo-600 to-blue-600 text-white rounded-2xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:from-indigo-700 hover:to-blue-700"
          >
            {sending ? 'Sending…' : `Send ${channelLabel}`}
          </button>
        </div>
      </div>
    </div>
  );
};



// Modern Stats Summary Card - Tile-based design
const JobsSummary = ({ jobs, title, icon: Icon, showFilter = false, hidePrice = false, onSelectAll, selectedCount = 0, isWorker = false }) => {
  const doneCount = jobs.filter(j => j.status === 'done' || j.status === 'completed').length;
  const pendingCount = jobs.filter(j => !j.status || j.status === 'scheduled').length;
  const skippedCount = jobs.filter(j => j.status === 'skipped').length;
  // Value excludes skipped jobs — the user isn't earning that money today.
  const totalValue = jobs
    .filter(j => j.status !== 'skipped')
    .reduce((sum, j) => sum + (j.price || 0), 0);
  // "Jobs" tile = jobs the user still has to think about TODAY: total minus
  // skipped. Done counts here too (still "your day"). Skipped is broken out
  // in its own tile so the user can see at-a-glance how many were dropped.
  const remainingCount = jobs.length - skippedCount;
  const allSelected = selectedCount === jobs.length && jobs.length > 0;

  return (
    <div className="px-3 py-2">
      {/* Stats Grid - Modern tiles */}
      <div className="grid grid-cols-4 gap-2">
        {/* Remaining Jobs Tile (excludes skipped, drops as jobs are done) */}
        <div className="bg-white rounded-xl p-3 shadow-sm border border-slate-100" data-testid="stats-jobs-tile">
          <div className="flex items-center justify-between">
            <p className="text-2xl font-bold text-slate-900" data-testid="stats-jobs-count">{remainingCount}</p>
            {!isWorker && onSelectAll && (
              <button
                onClick={onSelectAll}
                className={`px-2.5 h-7 rounded-md flex items-center justify-center text-[11px] font-bold uppercase tracking-wide transition-all shadow-sm ${
                  allSelected
                    ? 'bg-emerald-500 text-white'
                    : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 border border-emerald-200'
                }`}
                data-testid="select-all-summary-btn"
                title={allSelected ? 'Deselect all jobs' : 'Select all jobs'}
              >
                {allSelected ? '✓ All' : 'Select All'}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500 font-medium">Jobs</p>
        </div>

        {/* Done Tile */}
        <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100" data-testid="stats-done-tile">
          <p className="text-2xl font-bold text-emerald-600">{doneCount}</p>
          <p className="text-xs text-emerald-600 font-medium">Done</p>
        </div>

        {/* Skipped Tile (or empty cell when none) */}
        {skippedCount > 0 ? (
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-100" data-testid="stats-skipped-tile">
            <p className="text-2xl font-bold text-amber-600">{skippedCount}</p>
            <p className="text-xs text-amber-600 font-medium">Skipped</p>
          </div>
        ) : (
          <div />
        )}
        
        {/* Value Tile */}
        {!hidePrice ? (
          <div className="bg-slate-900 rounded-xl p-3">
            <p className="text-xl font-bold text-white">£{totalValue.toFixed(2)}</p>
            <p className="text-xs text-slate-400 font-medium">Value</p>
          </div>
        ) : (
          <div className="bg-amber-50 rounded-xl p-3 border border-amber-100">
            <p className="text-2xl font-bold text-amber-600">{skippedCount}</p>
            <p className="text-xs text-amber-600 font-medium">Skip</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Clickable Row Component for detail modal
const ClickableRow = ({ icon: Icon, children, onClick, rightContent, className = '' }) => (
  <button 
    onClick={onClick}
    className={`w-full p-4 flex items-center gap-3 hover:bg-zinc-50 active:bg-zinc-100 transition-colors text-left ${className}`}
  >
    <Icon size={20} className="text-zinc-400 flex-shrink-0" />
    <div className="flex-1 min-w-0">{children}</div>
    {rightContent}
    <ChevronRight size={20} className="text-emerald-500 flex-shrink-0" />
  </button>
);

// FAB Menu Component
const FabMenu = ({ isOpen, onToggle, onSmsReminders }) => {
  const menuItems = [
    { icon: Pencil, color: '#f97316', label: 'Add Notes', action: null },
    { icon: Plus, color: '#10b981', label: 'Add New Job', action: null },
    { icon: PoundSterling, color: '#3b82f6', label: 'Create Quote', action: null },
    { icon: Calendar, color: '#22c55e', label: 'Create Appointment', action: null },
    { icon: MessageSquare, color: '#8b5cf6', label: 'SMS Reminders', action: onSmsReminders },
  ];

  return (
    <div className="fixed bottom-20 right-6 z-40">
      {isOpen && (
        <div className="absolute bottom-16 right-0 flex flex-col gap-3 mb-2">
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                if (item.action) item.action();
                else onToggle();
              }}
              className="w-12 h-12 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-110"
              style={{ backgroundColor: item.color }}
              title={item.label}
              data-testid={`fab-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} />
            </button>
          ))}
        </div>
      )}
      
      <button
        data-testid="work-planner-fab"
        onClick={onToggle}
        className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-all ${
          isOpen ? 'bg-pink-500 rotate-45' : 'bg-pink-500'
        }`}
      >
        {isOpen ? <X size={24} /> : <MoreVertical size={24} />}
      </button>
    </div>
  );
};

// Date Picker Modal
const DatePickerModal = ({ currentDate, onSelect, onClose }) => {
  const [viewDate, setViewDate] = useState(new Date(currentDate));
  const [selectedDate, setSelectedDate] = useState(new Date(currentDate));
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  
  // Get days in month
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week for the first day (0 = Sunday, adjust for Monday start)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    // Add empty slots for days before the first day of the month
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };
  
  const days = getDaysInMonth(viewDate);
  
  const prevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };
  
  const nextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };
  
  const goToToday = () => {
    const today = new Date();
    setViewDate(today);
    setSelectedDate(today);
  };
  
  const handleSelect = () => {
    onSelect(selectedDate);
    onClose();
  };
  
  const isSameDay = (d1, d2) => {
    if (!d1 || !d2) return false;
    return d1.getDate() === d2.getDate() && 
           d1.getMonth() === d2.getMonth() && 
           d1.getFullYear() === d2.getFullYear();
  };
  
  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return isSameDay(date, today);
  };
  
  const formatSelectedDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                   day === 2 || day === 22 ? 'nd' : 
                   day === 3 || day === 23 ? 'rd' : 'th';
    return `${days[date.getDay()]} ${months[date.getMonth()]} ${day}${suffix}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="bg-zinc-800 rounded-xl w-full max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 text-center border-b border-white/10">
          <p className="text-white/60 text-sm mb-1">Select Date</p>
          <p className="text-white text-2xl font-light">{formatSelectedDate(selectedDate)}</p>
        </div>
        
        {/* Month Navigation */}
        <div className="flex items-center justify-between px-4 py-3">
          <button onClick={prevMonth} className="p-2 hover:bg-white/10 rounded text-white">
            <ChevronLeft size={20} />
          </button>
          <span className="text-white font-medium">
            {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
          </span>
          <button onClick={nextMonth} className="p-2 hover:bg-white/10 rounded text-white">
            <ChevronRight size={20} />
          </button>
        </div>
        
        {/* Day names */}
        <div className="grid grid-cols-7 px-4">
          {dayNames.map((day, i) => (
            <div key={i} className="text-center text-white/50 text-sm py-2">
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar grid */}
        <div className="grid grid-cols-7 gap-1 px-4 pb-4">
          {days.map((date, i) => (
            <button
              key={i}
              onClick={() => date && setSelectedDate(date)}
              disabled={!date}
              className={`aspect-square flex items-center justify-center rounded-full text-sm transition-colors ${
                !date ? '' :
                isSameDay(date, selectedDate) ? 'bg-pink-500 text-white' :
                isToday(date) ? 'bg-white/20 text-white' :
                'text-white/80 hover:bg-white/10'
              }`}
            >
              {date?.getDate()}
            </button>
          ))}
        </div>
        
        {/* Action buttons */}
        <div className="flex border-t border-white/10">
          <button 
            onClick={goToToday}
            className="flex-1 py-4 text-white/70 hover:bg-white/10 font-medium"
          >
            TODAY
          </button>
          <button 
            onClick={onClose}
            className="flex-1 py-4 text-white/70 hover:bg-white/10 font-medium border-l border-white/10"
          >
            CANCEL
          </button>
          <button 
            onClick={handleSelect}
            className="flex-1 py-4 text-pink-400 hover:bg-white/10 font-medium border-l border-white/10"
          >
            SELECT
          </button>
        </div>
      </div>
    </div>
  );
};

// Replan Confirmation Modal - First step
const ReplanConfirmModal = ({ jobCount, targetDay, onConfirm, onCancel }) => {
  const formatDay = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                   day === 2 || day === 22 ? 'nd' : 
                   day === 3 || day === 23 ? 'rd' : 'th';
    return `${days[date.getDay()]}, ${day}${suffix} ${months[date.getMonth()]}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">
        <div className="p-6 text-center">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Calendar size={24} className="text-emerald-600" />
          </div>
          <h3 className="text-lg font-bold text-zinc-900 mb-2">Confirm Reschedule</h3>
          <p className="text-zinc-600">
            Move {jobCount} job{jobCount > 1 ? 's' : ''} to <span className="font-semibold">{formatDay(targetDay)}</span>?
          </p>
        </div>
        <div className="flex gap-3 p-4 bg-zinc-50">
          <button 
            onClick={onCancel}
            className="flex-1 py-3 text-zinc-600 hover:bg-white font-medium rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={onConfirm}
            className="flex-1 py-3 bg-emerald-600 text-white hover:bg-emerald-700 font-medium rounded-xl transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

// Replan Schedule Modal - Second step (ask about future appointments)
const ReplanScheduleModal = ({ targetDay, onYes, onNo, onCancel }) => {
  const formatDate = (date) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const suffix = day === 1 || day === 21 || day === 31 ? 'st' : 
                   day === 2 || day === 22 ? 'nd' : 
                   day === 3 || day === 23 ? 'rd' : 'th';
    return `${days[date.getDay()]}, ${day}${suffix} ${months[date.getMonth()]}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-xl">
        <div className="p-6">
          <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <RefreshCw size={24} className="text-purple-600" />
          </div>
          <h3 className="text-lg font-bold text-zinc-900 text-center mb-2">Update Recurring?</h3>
          <p className="text-zinc-600 text-center mb-4">
            Rescheduling to <span className="font-semibold">{formatDate(targetDay)}</span>
          </p>
          
          <div className="p-4 bg-zinc-50 rounded-xl mb-4">
            <p className="text-sm text-zinc-700 text-center">
              Also update schedule for <span className="font-semibold">future recurring appointments</span> of this job?
            </p>
          </div>
        </div>
        <div className="flex gap-3 p-4 bg-zinc-50 border-t border-zinc-100">
          <button 
            onClick={onCancel}
            className="flex-1 py-3 text-zinc-600 hover:bg-white font-medium rounded-xl transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={onNo}
            className="flex-1 py-3 bg-zinc-200 text-zinc-700 hover:bg-zinc-300 font-medium rounded-xl transition-colors"
          >
            No
          </button>
          <button 
            onClick={onYes}
            className="flex-1 py-3 bg-emerald-600 text-white hover:bg-emerald-700 font-medium rounded-xl transition-colors"
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  );
};

// SMS Reminders Modal
const SmsRemindersModal = ({ jobs, onClose }) => {
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  
  // Get unique customers from jobs - using customerRef as the unique identifier
  const customers = useMemo(() => {
    const uniqueCustomers = new Map();
    jobs.forEach(job => {
      const customerId = job.customerRef || job.customerId || job.id;
      if (!uniqueCustomers.has(customerId)) {
        uniqueCustomers.set(customerId, {
          id: customerId,
          name: job.customerName,
          address: job.address,
          service: job.services || 'Window Clean',
          phone: job.customerPhone,
          reminded: false, // Default to not reminded
        });
      }
    });
    return Array.from(uniqueCustomers.values());
  }, [jobs]);
  
  const notReminded = customers.filter(c => !c.reminded);
  const reminded = customers.filter(c => c.reminded);
  
  const toggleCustomer = (customerId) => {
    setSelectedCustomers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  };
  
  const handleNext = () => {
    if (selectedCustomers.size > 0) {
      setShowPreview(true);
    }
  };
  
  // Generate the reminder message for a customer
  const generateMessage = (customer) => {
    const firstName = customer.name ? customer.name.split(' ')[0] : 'there';
    const service = customer.service || 'window clean';
    return `Hi ${firstName},\n\nJust a reminder that we'll be round tomorrow to do your ${service}.\n\nPlease leave any side gates open if you won't be home.\n\nThanks,\nStreakFree`;
  };
  
  const handleSend = async () => {
    setSending(true);
    const selectedCustomersList = customers.filter(c => selectedCustomers.has(c.id));
    
    let successCount = 0;
    let failCount = 0;
    const errors = [];
    
    // Get auth token
    const token = localStorage.getItem('auth_token');
    if (!token) {
      toast.error('Please log in to send SMS');
      setSending(false);
      return;
    }
    
    // Send SMS to each selected customer
    for (const customer of selectedCustomersList) {
      if (!customer.phone) {
        failCount++;
        errors.push(`${customer.name}: No phone number`);
        continue;
      }
      
      try {
        const message = generateMessage(customer);
        const response = await fetch(`${API_URL}/api/sms/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            recipient_phone: customer.phone,
            message: message,
            customer_id: customer.id
          })
        });
        
        if (response.ok) {
          successCount++;
        } else {
          const errorData = await response.json().catch(() => ({}));
          failCount++;
          errors.push(`${customer.name}: ${errorData.detail || 'Failed to send'}`);
        }
      } catch (error) {
        failCount++;
        errors.push(`${customer.name}: Network error`);
      }
    }
    
    setSending(false);
    
    // Show results
    if (successCount > 0 && failCount === 0) {
      toast.success(`Sent ${successCount} SMS reminder${successCount > 1 ? 's' : ''} successfully!`);
      onClose();
    } else if (successCount > 0 && failCount > 0) {
      toast.warning(`Sent ${successCount} SMS. ${failCount} failed.`);
      setSendResults({ success: successCount, failed: failCount, errors });
    } else {
      toast.error(`Failed to send SMS reminders`);
      setSendResults({ success: 0, failed: failCount, errors });
    }
  };
  
  if (showPreview) {
    const selectedCustomersList = customers.filter(c => selectedCustomers.has(c.id));
    return (
      <div className="fixed inset-0 bg-black/50 z-[130] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg w-full max-w-md modal-shell-h overflow-hidden flex flex-col">
          <div className="p-4 bg-zinc-800 text-white flex items-center justify-between">
            <button onClick={() => !sending && setShowPreview(false)} className="p-1" disabled={sending}>
              <ChevronLeft size={24} />
            </button>
            <h2 className="text-lg font-semibold">SMS Sample Message</h2>
            <button 
              onClick={handleSend} 
              className="text-pink-300 font-medium disabled:opacity-50 flex items-center gap-1"
              disabled={sending}
            >
              {sending ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                'SEND'
              )}
            </button>
          </div>
          
          {sendResults ? (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className={`p-4 rounded-lg ${sendResults.success > 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                <p className="font-medium">{sendResults.success} sent, {sendResults.failed} failed</p>
              </div>
              {sendResults.errors.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-red-600">Errors:</p>
                  {sendResults.errors.map((err, idx) => (
                    <p key={idx} className="text-sm text-red-500">{err}</p>
                  ))}
                </div>
              )}
              <button 
                onClick={onClose}
                className="w-full py-2 bg-zinc-800 text-white rounded-lg"
              >
                Close
              </button>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <p className="text-sm text-gray-500">
                Preview of the SMS that will be sent to {selectedCustomers.size} customer(s):
              </p>
              
              {selectedCustomersList.slice(0, 3).map(customer => (
                <div key={customer.id} className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700">
                    <strong>Hi {customer.name ? customer.name.split(' ')[0] : 'there'},</strong>
                  </p>
                  <p className="text-gray-600 mt-2">
                    Just a reminder that we&apos;ll be round tomorrow to do your {customer.service}.
                  </p>
                  <p className="text-gray-600 mt-2">
                    Please leave any side gates open if you won&apos;t be home.
                  </p>
                  <p className="text-gray-500 mt-4 text-sm">
                    Thanks,<br />
                    StreakFree
                  </p>
                  {!customer.phone && (
                    <p className="text-red-500 text-xs mt-2">⚠️ No phone number</p>
                  )}
                </div>
              ))}
              
              {selectedCustomersList.length > 3 && (
                <p className="text-sm text-gray-500 text-center">
                  +{selectedCustomersList.length - 3} more messages
                </p>
              )}
            
              <div className="bg-amber-50 rounded-lg p-3">
                <p className="text-sm text-amber-700">
                  ~120 characters per message (1 SMS credit each)
                </p>
                <p className="text-sm text-amber-600 font-medium">
                  Total: {selectedCustomers.size} credits
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 z-[130] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md modal-shell-h overflow-hidden flex flex-col">
        <div className="p-4 bg-zinc-800 text-white flex items-center justify-between">
          <button onClick={onClose} className="p-1">
            <X size={24} />
          </button>
          <h2 className="text-lg font-semibold">
            SMS Reminders ({selectedCustomers.size} of {notReminded.length} Done)
          </h2>
          <button 
            onClick={handleNext}
            disabled={selectedCustomers.size === 0}
            className={`font-medium ${selectedCustomers.size > 0 ? 'text-pink-300' : 'text-white/50'}`}
          >
            NEXT
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {/* Not Reminded Section */}
          <div className="p-3 bg-gray-100 border-b border-gray-200">
            <p className="text-sm font-medium text-gray-600">Not reminded ({notReminded.length})</p>
          </div>
          {notReminded.map(customer => (
            <div 
              key={customer.id}
              onClick={() => toggleCustomer(customer.id)}
              className="flex items-center gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
            >
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                selectedCustomers.has(customer.id) 
                  ? 'bg-pink-500 border-pink-500' 
                  : 'border-gray-300'
              }`}>
                {selectedCustomers.has(customer.id) && (
                  <CheckCircle2 size={16} className="text-white" />
                )}
              </div>
              <Clock size={18} className="text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{customer.name}</p>
                <p className="text-sm text-gray-500 truncate">{customer.address}</p>
                <p className="text-xs text-gray-400">{customer.service}</p>
              </div>
            </div>
          ))}
          
          {/* Already Reminded Section */}
          {reminded.length > 0 && (
            <>
              <div className="p-3 bg-green-50 border-b border-green-100">
                <p className="text-sm font-medium text-green-600">Reminder Sent ({reminded.length})</p>
              </div>
              {reminded.map(customer => (
                <div 
                  key={customer.id}
                  className="flex items-center gap-3 p-4 border-b border-gray-100 bg-gray-50 opacity-60"
                >
                  <div className="w-6 h-6 rounded bg-green-500 flex items-center justify-center">
                    <CheckCircle2 size={16} className="text-white" />
                  </div>
                  <Clock size={18} className="text-green-500" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-700 truncate">{customer.name}</p>
                    <p className="text-sm text-gray-500 truncate">{customer.address}</p>
                    <p className="text-xs text-gray-400">{customer.service}</p>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
        
        {/* Select All Button */}
        <div className="p-4 border-t border-gray-200">
          <button 
            onClick={() => {
              if (selectedCustomers.size === notReminded.length) {
                setSelectedCustomers(new Set());
              } else {
                setSelectedCustomers(new Set(notReminded.map(c => c.id)));
              }
            }}
            className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-lg font-medium transition-colors"
          >
            {selectedCustomers.size === notReminded.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Reminder Menu Modal - Shows options for SMS, Email, or Both
const ReminderMenuModal = ({ onClose, onSelect }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div 
        className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-sm overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-4 border-b border-zinc-200">
          <h2 className="text-lg font-semibold text-center">Send Reminders</h2>
          <p className="text-sm text-zinc-500 text-center mt-1">Choose how to notify customers</p>
        </div>
        
        <div className="p-2">
          <button
            onClick={() => onSelect('whatsapp_email')}
            className="w-full flex items-center gap-4 p-4 hover:bg-zinc-50 rounded-xl transition-colors"
            data-testid="reminder-option-whatsapp-email"
          >
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Bell size={24} className="text-emerald-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-zinc-900">Send by WhatsApp &amp; Email</p>
              <p className="text-sm text-zinc-500">WhatsApp first, plus email</p>
            </div>
            <ChevronRight size={20} className="text-zinc-400" />
          </button>
          
          <button
            onClick={() => onSelect('sms')}
            className="w-full flex items-center gap-4 p-4 hover:bg-zinc-50 rounded-xl transition-colors"
            data-testid="reminder-option-sms"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <MessageSquare size={24} className="text-blue-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-zinc-900">Send by SMS</p>
              <p className="text-sm text-zinc-500">Text message only</p>
            </div>
            <ChevronRight size={20} className="text-zinc-400" />
          </button>
          
          <button
            onClick={() => onSelect('sms_email')}
            className="w-full flex items-center gap-4 p-4 hover:bg-zinc-50 rounded-xl transition-colors"
            data-testid="reminder-option-sms-email"
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Mail size={24} className="text-purple-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-zinc-900">Send by SMS &amp; Email</p>
              <p className="text-sm text-zinc-500">Both — no WhatsApp</p>
            </div>
            <ChevronRight size={20} className="text-zinc-400" />
          </button>
          <button
            onClick={() => onSelect('email')}
            className="w-full flex items-center gap-4 p-4 hover:bg-zinc-50 rounded-xl transition-colors"
            data-testid="reminder-option-email"
          >
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Mail size={24} className="text-amber-600" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-medium text-zinc-900">Send by Email</p>
              <p className="text-sm text-zinc-500">Email only</p>
            </div>
            <ChevronRight size={20} className="text-zinc-400" />
          </button>
        </div>
        
        <div className="p-4 border-t border-zinc-200">
          <button
            onClick={onClose}
            className="w-full py-3 text-zinc-600 font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Reminder Selection Modal - Shows jobs with checkboxes
const ReminderSelectionModal = ({ jobs, reminderType, onClose, onSend, isSending }) => {
  const [selectedJobs, setSelectedJobs] = useState(new Set(jobs.map(j => j.id)));
  // Two-step modal: 1) pick customers  2) preview every message  3) send.
  // After May 11 2026 the user lost trust in batch sends after a stale-address
  // bug shipped wrong addresses to 11 real customers — preview step is now
  // mandatory before anything actually goes out.
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState('');
  
  const toggleJob = (jobId) => {
    setSelectedJobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };
  
  const toggleAll = () => {
    if (selectedJobs.size === jobs.length) {
      setSelectedJobs(new Set());
    } else {
      setSelectedJobs(new Set(jobs.map(j => j.id)));
    }
  };

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setPreviewError('');
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/jobs/preview-reminders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          job_ids: Array.from(selectedJobs),
          reminder_type: reminderType,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Preview failed (${res.status})`);
      }
      const data = await res.json();
      setPreviewData(data);
    } catch (e) {
      setPreviewError(e.message || 'Could not generate preview');
    } finally {
      setLoadingPreview(false);
    }
  };
  
  const getTypeInfo = () => {
    switch(reminderType) {
      case 'sms':
        return { label: 'SMS', icon: MessageSquare, bgClass: 'bg-blue-500 hover:bg-blue-600', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' };
      case 'email':
        return { label: 'Email', icon: Mail, bgClass: 'bg-purple-500 hover:bg-purple-600', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' };
      default:
        return { label: 'SMS & Email', icon: Bell, bgClass: 'bg-emerald-500 hover:bg-emerald-600', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' };
    }
  };
  
  const typeInfo = getTypeInfo();
  const TypeIcon = typeInfo.icon;

  // ---- PREVIEW SCREEN ----
  if (previewData) {
    const deliverable = (previewData.previews || []).filter(p => !p.skipped);
    const skipped = (previewData.previews || []).filter(p => p.skipped);
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex flex-col" onClick={onClose}>
        <div
          className="bg-white flex-1 flex flex-col mt-12 rounded-t-2xl overflow-hidden"
          onClick={e => e.stopPropagation()}
          data-testid="reminder-preview-screen"
        >
          {/* Header */}
          <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
            <button
              onClick={() => setPreviewData(null)}
              className="p-2 -ml-2 hover:bg-zinc-100 rounded-lg"
              data-testid="back-to-selection"
            >
              <ChevronLeft size={24} />
            </button>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">Preview {typeInfo.label}</h2>
              <p className="text-sm text-zinc-500">
                {deliverable.length} ready &middot; review each message before sending
              </p>
            </div>
            <div className={`w-10 h-10 ${typeInfo.iconBg} rounded-xl flex items-center justify-center`}>
              <TypeIcon size={20} className={typeInfo.iconColor} />
            </div>
          </div>

          {/* Preview Cards */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {deliverable.map((p) => (
              <div
                key={p.job_id}
                className="border border-zinc-200 rounded-2xl bg-white overflow-hidden"
                data-testid={`preview-card-${p.job_id}`}
              >
                {/* Customer header */}
                <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200">
                  <p className="font-semibold text-zinc-900">{p.customer_name}</p>
                  <p className="text-xs text-zinc-500 break-words">{p.customer_address || '—'}</p>
                </div>
                {/* Channel previews */}
                <div className="p-4 space-y-3">
                  {p.channels.whatsapp && (
                    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-xs font-bold px-1.5 py-0.5 bg-[#25d366] text-white rounded">WhatsApp</span>
                        <span className="text-xs text-zinc-500">{p.channels.whatsapp.recipient}</span>
                      </div>
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{p.channels.whatsapp.preview}</p>
                    </div>
                  )}
                  {p.channels.sms && (
                    <div className={`rounded-xl border p-3 ${p.channels.sms.is_fallback ? 'border-amber-200 bg-amber-50' : 'border-blue-200 bg-blue-50'}`}>
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-xs font-bold px-1.5 py-0.5 bg-blue-600 text-white rounded">SMS</span>
                        <span className="text-xs text-zinc-500">{p.channels.sms.recipient}</span>
                        {p.channels.sms.is_fallback && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-200 text-amber-800">
                            Only sent if WhatsApp fails
                          </span>
                        )}
                        <span className="ml-auto text-[10px] text-zinc-400 tabular-nums">{p.channels.sms.char_count}/320 chars &middot; {p.channels.sms.segments} segment{p.channels.sms.segments !== 1 ? 's' : ''}</span>
                      </div>
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{p.channels.sms.body}</p>
                    </div>
                  )}
                  {p.channels.email && (
                    <div className="rounded-xl border border-purple-200 bg-purple-50 p-3">
                      <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                        <span className="text-xs font-bold px-1.5 py-0.5 bg-purple-600 text-white rounded">Email</span>
                        <span className="text-xs text-zinc-500">{p.channels.email.recipient}</span>
                        {p.channels.email.cc && p.channels.email.cc.length > 0 && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-purple-200 text-purple-800">
                            CC: {p.channels.email.cc.join(', ')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-zinc-900 mb-1">{p.channels.email.subject}</p>
                      <p className="text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{p.channels.email.body}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {skipped.length > 0 && (
              <div className="border border-amber-200 rounded-2xl bg-amber-50 p-4" data-testid="preview-skipped">
                <p className="font-semibold text-amber-800 mb-2">Skipped ({skipped.length})</p>
                <ul className="text-sm text-amber-900 space-y-1">
                  {skipped.map((p) => (
                    <li key={p.job_id}>
                      &bull; <span className="font-medium">{p.customer_name || 'Unknown'}</span>: {p.reason}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Send Button */}
          <div className="p-4 border-t border-zinc-200 bg-white">
            <button
              onClick={() => onSend(Array.from(selectedJobs))}
              disabled={isSending || deliverable.length === 0}
              className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                !isSending && deliverable.length > 0
                  ? `${typeInfo.bgClass} text-white shadow-lg`
                  : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
              }`}
              data-testid="confirm-send-button"
            >
              {isSending ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={20} />
                  Send to {deliverable.length} Customer{deliverable.length !== 1 ? 's' : ''} now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex flex-col" onClick={onClose}>
      <div 
        className="bg-white flex-1 flex flex-col mt-12 rounded-t-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-zinc-200 flex items-center gap-3">
          <button onClick={onClose} className="p-2 -ml-2 hover:bg-zinc-100 rounded-lg">
            <ChevronLeft size={24} />
          </button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Select Customers</h2>
            <p className="text-sm text-zinc-500">Step 1 of 2 &middot; {typeInfo.label} reminders</p>
          </div>
          <div className={`w-10 h-10 ${typeInfo.iconBg} rounded-xl flex items-center justify-center`}>
            <TypeIcon size={20} className={typeInfo.iconColor} />
          </div>
        </div>
        
        {/* Select All Button */}
        <div className="p-4 border-b border-zinc-200">
          <button
            onClick={toggleAll}
            className={`w-full py-3 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
              selectedJobs.size === jobs.length 
                ? 'bg-emerald-500 text-white' 
                : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
            }`}
          >
            {selectedJobs.size === jobs.length ? (
              <>
                <CheckSquare size={20} />
                All Selected ({jobs.length})
              </>
            ) : (
              <>
                <Square size={20} />
                Select All ({jobs.length})
              </>
            )}
          </button>
        </div>
        
        {/* Jobs List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {jobs.map(job => (
            <button
              key={job.id}
              onClick={() => toggleJob(job.id)}
              className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                selectedJobs.has(job.id)
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-zinc-200 bg-white hover:border-zinc-300'
              }`}
            >
              <div className={`w-6 h-6 rounded-md flex items-center justify-center ${
                selectedJobs.has(job.id) ? 'bg-emerald-500 text-white' : 'bg-zinc-200'
              }`}>
                {selectedJobs.has(job.id) && <Check size={16} />}
              </div>
              <div className="flex-1 text-left">
                <p className="font-medium text-zinc-900">{job.customer_name || job.customerName}</p>
                <p className="text-sm text-zinc-500 truncate">
                  {job.job_address || job.address_override || job.customer_address || job.address || 'No address'}
                </p>
              </div>
              {job.reminder_sent && (
                <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                  Already sent
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Preview Button (replaces direct Send) */}
        <div className="p-4 border-t border-zinc-200 bg-white">
          {previewError && (
            <p className="text-sm text-red-600 mb-2" data-testid="preview-error">{previewError}</p>
          )}
          <button
            onClick={fetchPreview}
            disabled={selectedJobs.size === 0 || loadingPreview}
            className={`w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
              selectedJobs.size > 0 && !loadingPreview
                ? `${typeInfo.bgClass} text-white shadow-lg`
                : 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
            }`}
          >
            {loadingPreview ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                Generating preview...
              </>
            ) : (
              <>
                <Bell size={20} />
                Preview {selectedJobs.size} message{selectedJobs.size !== 1 ? 's' : ''}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const WorkPlanner = ({ initialDate, onDateNavigated, user, onMenuClick }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get user role - default to Owner
  const userRole = user?.role || 'Owner';
  const isWorker = userRole === 'Worker';
  const userName = user?.name || '';
  
  // Initialize week start from initialDate prop, URL param, or current week
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const dateParam = initialDate || searchParams.get('date');
    let targetDate;
    
    if (dateParam) {
      targetDate = parseLocalDate(dateParam);
    } else {
      targetDate = new Date();
    }
    
    // Get Monday of that week
    const day = targetDate.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(targetDate);
    monday.setDate(targetDate.getDate() + diff);
    return monday;
  });
  
  // Initialize selected day from initialDate prop, URL param, or today
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const dateParam = initialDate || searchParams.get('date');
    if (dateParam) {
      const targetDate = parseLocalDate(dateParam);
      const day = targetDate.getDay();
      return day === 0 ? 6 : day - 1; // Convert to Mon=0 index
    }
    const today = new Date();
    const day = today.getDay();
    return day === 0 ? 6 : day - 1;
  });
  
  // Swipe animation state for week navigation
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Handle initialDate prop changes (from App.js navigation)
  useEffect(() => {
    if (initialDate) {
      const targetDate = parseLocalDate(initialDate);
      
      // Update week start to contain this date
      const day = targetDate.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(targetDate);
      monday.setDate(targetDate.getDate() + diff);
      setCurrentWeekStart(monday);
      
      // Update selected day
      const dayIndex = day === 0 ? 6 : day - 1;
      setSelectedDayIndex(dayIndex);
      
      // Notify parent that we've navigated
      if (onDateNavigated) {
        onDateNavigated();
      }
    }
  }, [initialDate, onDateNavigated]);
  
  // Handle URL date parameter changes (legacy support)
  useEffect(() => {
    const dateParam = searchParams.get('date');
    if (dateParam) {
      const targetDate = parseLocalDate(dateParam);
      
      // Update week start to contain this date
      const day = targetDate.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const monday = new Date(targetDate);
      monday.setDate(targetDate.getDate() + diff);
      setCurrentWeekStart(monday);
      
      // Update selected day
      const dayIndex = day === 0 ? 6 : day - 1;
      setSelectedDayIndex(dayIndex);
      
      // Clear the URL param after navigation
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  // Active round filter for the day view (null = show all rounds). Toggled by tapping a round badge.
  const [roundFilter, setRoundFilter] = useState(null);
  // Reset round filter when changing day or assignee — otherwise stale filters confuse users
  useEffect(() => { setRoundFilter(null); }, [selectedDayIndex, selectedAssignee]);
  const [viewMode, setViewMode] = useState('chart');
  const [fabOpen, setFabOpen] = useState(false);
  const [showNewJobModal, setShowNewJobModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedCustomerFromJob, setSelectedCustomerFromJob] = useState(null);
  const [showSmsReminders, setShowSmsReminders] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [showActions, setShowActions] = useState(false);
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Schedule shift menu — squeegee-style kebab on the planner header that
  // lets the owner slide every pending job forward or back by N days.
  // Used for time off, weather delays, single-day extensions etc.
  const [showShiftMenu, setShowShiftMenu] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftDays, setShiftDays] = useState(7);
  const [shiftBusy, setShiftBusy] = useState(false);
  // Round Optimiser — geographic outlier detection with move suggestions
  const [showOptimizerModal, setShowOptimizerModal] = useState(false);
  const [optimizerCount, setOptimizerCount] = useState(0);
  // Easy Mode — big buttons, no swipe gestures, no drag-reorder. Per-device
  // (localStorage) so a worker's phone stays idiot-proof regardless of login.
  const [easyMode, setEasyMode] = useState(() => localStorage.getItem('sf_easy_mode') === '1');
  const toggleEasyMode = () => {
    setEasyMode(prev => {
      const next = !prev;
      localStorage.setItem('sf_easy_mode', next ? '1' : '0');
      showNotification(next ? 'Easy Mode ON — big buttons, no swiping' : 'Easy Mode off — swipe gestures back on', 'success');
      return next;
    });
  };
  // Live Mode — OFF means completing jobs is silent record-keeping only:
  // no invoices, no card charges, no customer emails/SMS (pre-launch use).
  const [liveMode, setLiveMode] = useState(true);
  useEffect(() => {
    if (isWorker) return;
    const token = localStorage.getItem('streakfree_token');
    fetch(`${API_URL}/api/settings`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) setLiveMode(d.live_mode !== false); })
      .catch(() => {});
  }, [isWorker]);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [goLiveChecklist, setGoLiveChecklist] = useState(null);
  const applyLiveMode = async (next) => {
    setLiveMode(next);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ live_mode: next })
      });
      if (!res.ok) throw new Error('save failed');
      setUserSettings(prev => ({ ...prev, live_mode: next }));
      showNotification(
        next
          ? 'LIVE — completing jobs now invoices, charges and notifies customers as normal'
          : 'OFF — jobs marked Done are recorded silently: no invoices, no charges, no customer messages',
        'success'
      );
    } catch (e) {
      setLiveMode(!next);
      showNotification('Could not save Live Mode — try again', 'error');
    }
  };
  const toggleLiveMode = async () => {
    if (liveMode) {
      applyLiveMode(false);
      return;
    }
    // Going LIVE — show readiness checklist first
    setGoLiveChecklist(null);
    setShowGoLiveModal(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/settings/go-live-checklist`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setGoLiveChecklist(await res.json());
      else setGoLiveChecklist({});
    } catch (e) {
      setGoLiveChecklist({});
    }
  };
  useEffect(() => {
    if (isWorker) return;
    const token = localStorage.getItem('streakfree_token');
    fetch(`${API_URL}/api/optimizer/suggestions?count_only=true`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!d) return;
        const count = d.count || 0;
        setOptimizerCount(count);
        // Pop a once-a-day toast so the owner notices without being nagged
        const todayKey = toLocalYMD(new Date());
        if (count > 0 && localStorage.getItem('optimizerToastDate') !== todayKey) {
          localStorage.setItem('optimizerToastDate', todayKey);
          toast(`${count} round improvement${count > 1 ? 's' : ''} spotted`, {
            description: 'Jobs that would fit closer to your route on another day',
            action: { label: 'Review', onClick: () => setShowOptimizerModal(true) },
            duration: 10000,
          });
        }
      })
      .catch(() => {});
  }, [isWorker]);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // Bulk action modals
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [showReassignWorkerModal, setShowReassignWorkerModal] = useState(false);
  const [showReassignRoundModal, setShowReassignRoundModal] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignWorker, setBulkAssignWorker] = useState(null);
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [bulkMessageType, setBulkMessageType] = useState('sms'); // sms, email, both
  const [bulkMessage, setBulkMessage] = useState('');
  
  // SMS Compose Modal state
  const [showSMSComposeModal, setShowSMSComposeModal] = useState(false);
  const [smsRecipients, setSmsRecipients] = useState([]);
  
  // Navigation Modal state
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [navigationTarget, setNavigationTarget] = useState({ address: '', lat: null, lng: null });
  const [savedNavApp, setSavedNavApp] = useState('');
  const [savedNavMode, setSavedNavMode] = useState('address');
  
  // Day Notes state
  const [showDayNoteModal, setShowDayNoteModal] = useState(false);
  const [dayNotes, setDayNotes] = useState({});  // { 'YYYY-MM-DD': [notes] }
  
  // FAB visibility state - hide when scrolled to bottom
  const [fabVisible, setFabVisible] = useState(true);
  const scrollContainerRef = useRef(null);
  const lastScrollTop = useRef(0);
  
  // API data state
  const [weekJobs, setWeekJobs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Replan state
  const [replanTargetDay, setReplanTargetDay] = useState(null);
  const [showReplanConfirm, setShowReplanConfirm] = useState(false);
  const [showReplanSchedule, setShowReplanSchedule] = useState(false);
  const [jobsToReplan, setJobsToReplan] = useState([]); // Jobs being replanned
  
  // Track replanned jobs - maps job ID to new date string
  const [replannedJobs, setReplannedJobs] = useState({});
  
  // Track job status changes (completed/skipped) - local optimistic updates
  const [jobStatuses, setJobStatuses] = useState({});
  
  // Track invoices created from completed jobs
  const [invoices, setInvoices] = useState([]);
  
  // Track owings (unpaid invoices) - will be added to when no payment method
  const [owings, setOwings] = useState([]);
  
  // Weather forecast state
  const [weatherForecast, setWeatherForecast] = useState([]);
  const [weatherLoading, setWeatherLoading] = useState(false);
  
  // User settings for VAT etc
  const [userSettings, setUserSettings] = useState({});
  // Work Planner display style — synced account setting (classic | chart | compact)
  const plannerDisplay = userSettings?.work_planner_display || 'classic';
  const plannerBarColor = userSettings?.work_planner_bar_color || '#10b981';
  
  // Bank holidays and worker leave state
  const [bankHolidays, setBankHolidays] = useState([]);
  const [workerLeave, setWorkerLeave] = useState({});
  
  // Toast/notification state with undo support
  const [notification, setNotification] = useState(null);
  
  // Store last action for undo functionality
  const [lastAction, setLastAction] = useState(null);
  // Pin-correction prompt: shown after job completion when worker GPS is
  // 100m–1000m from saved customer pin. Always opt-in via modal.
  const [pinCorrectionSuggestion, setPinCorrectionSuggestion] = useState(null);

  // Explicit "Confirm pin location" button on each job card. Captures GPS
  // (best-effort) then opens the pin modal pre-jumped to the map editor.
  // Skips the confirmation step because the user has already explicitly
  // tapped the button asking to fix the pin.
  const handleConfirmPinLocation = useCallback(async (job) => {
    const captureGps = () => new Promise((resolve) => {
      if (!('geolocation' in navigator)) return resolve(null);
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; resolve(null); } }, 4000);
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => { if (!done) { done = true; clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); } },
          () => { if (!done) { done = true; clearTimeout(timer); resolve(null); } },
          { enableHighAccuracy: true, timeout: 4000, maximumAge: 15000 }
        );
      } catch (e) { clearTimeout(timer); resolve(null); }
    });
    const savedLat = job.latitude || job.customer_lat || job.lat;
    const savedLng = job.longitude || job.customer_lng || job.lng;
    const gps = await captureGps();
    setPinCorrectionSuggestion({
      customer_id: job.customer_id,
      customer_name: job.customer_name || job.customerName,
      customer_address: job.customer_address || job.address,
      saved_lat: savedLat,
      saved_lng: savedLng,
      worker_lat: gps ? gps.lat : (savedLat || 50.8225),
      worker_lng: gps ? gps.lng : (savedLng || -0.1372),
      distance_m: null,
      _skip_confirm: true,
    });
  }, []);
  
  // Reminder menu state
  const [showReminderMenu, setShowReminderMenu] = useState(false);
  const [showReminderSelection, setShowReminderSelection] = useState(false);
  const [selectedReminderType, setSelectedReminderType] = useState(null);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderConfirmation, setReminderConfirmation] = useState(null); // { sent: number, type: string }
  
  // Custom job order state - allows drag-and-drop reordering
  const [customJobOrder, setCustomJobOrder] = useState({}); // { [dateKey]: [jobId1, jobId2, ...] }
  
  // Route direction state - 'forward' = 1→Last, 'reverse' = Last→1
  const [routeDirection, setRouteDirection] = useState('forward');
  
  // Work planner start date state - for first-time setup
  const [showStartDateModal, setShowStartDateModal] = useState(false);
  const [workPlannerStartDate, setWorkPlannerStartDate] = useState(null);
  const [startDateInput, setStartDateInput] = useState('');
  const [settingStartDate, setSettingStartDate] = useState(false);
  
  // DnD sensors for drag and drop - optimized for mobile touch
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 15, // Desktop mouse: 15px movement before drag starts
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 600, // 600ms hold before drag starts - prevents accidental drags while scrolling
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  // Worker permissions state
  const [workerPermissions, setWorkerPermissions] = useState({
    can_see_prices: true,
    can_see_customer_balance: true,
    can_send_reminders: true,
    can_create_jobs: true,
    can_mark_jobs_done: true,
    can_skip_jobs: true,
    can_see_customer_phone: true,
    can_see_customer_address: true,
    can_take_payments: false
  });
  
  // Fetch worker permissions on mount
  useEffect(() => {
    const loadWorkerPermissions = async () => {
      try {
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/worker-permissions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setWorkerPermissions(data);
        }
      } catch (err) {
        console.error('Error loading worker permissions:', err);
      }
    };
    if (isWorker) {
      loadWorkerPermissions();
    }
  }, [isWorker]);
  
  // Load navigation preferences on mount
  useEffect(() => {
    const loadNavPrefs = async () => {
      try {
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.navigation_app) setSavedNavApp(data.navigation_app);
          if (data.navigation_mode) setSavedNavMode(data.navigation_mode);
        }
      } catch (err) {
        console.error('Error loading navigation preferences:', err);
      }
    };
    loadNavPrefs();
  }, []);
  
  // FAB scroll visibility handler - hide when at bottom (can't scroll anymore), show when scrolling up
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 50; // Within 50px of bottom
      const isScrollingUp = scrollTop < lastScrollTop.current;
      
      if (isAtBottom) {
        // Hide FAB when can't scroll anymore to avoid blocking content
        setFabVisible(false);
      } else if (isScrollingUp) {
        // Show FAB when scrolling back up
        setFabVisible(true);
      }
      
      lastScrollTop.current = scrollTop;
    };
    
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Load day notes for the current week
  const loadDayNotes = useCallback(async (startDate, endDate) => {
    if (!startDate || !endDate) return;
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/day-notes?start_date=${startDate}&end_date=${endDate}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const notes = await res.json();
        // Group notes by date
        const notesByDate = {};
        notes.forEach(note => {
          if (!notesByDate[note.date]) {
            notesByDate[note.date] = [];
          }
          notesByDate[note.date].push(note);
        });
        setDayNotes(notesByDate);
      }
    } catch (err) {
      console.error('Error loading day notes:', err);
    }
  }, []);
  
  // Save navigation preferences
  const saveNavigationPreference = async (app, mode) => {
    try {
      const token = api.getToken();
      await fetch(`${API_URL}/api/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ navigation_app: app, navigation_mode: mode })
      });
      setSavedNavApp(app);
      setSavedNavMode(mode);
    } catch (err) {
      console.error('Error saving navigation preference:', err);
    }
  };
  
  // Open navigation modal
  const openNavigation = (address, lat = null, lng = null) => {
    setNavigationTarget({ address, lat, lng });
    setShowNavigationModal(true);
  };
  
  // Fetch work planner start date setting on mount
  useEffect(() => {
    const loadStartDateSetting = async () => {
      try {
        const token = api.getToken();
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Store full settings including VAT
          setUserSettings(data);
          
          if (data.work_planner_start_date) {
            setWorkPlannerStartDate(data.work_planner_start_date);
          } else {
            // No start date set - show the popup for owners only
            // Defer while the onboarding wizard is showing (new accounts)
            if (!isWorker && data.onboarding_completed !== false) {
              setShowStartDateModal(true);
              // Default to tomorrow
              const tomorrow = new Date();
              tomorrow.setDate(tomorrow.getDate() + 1);
              setStartDateInput(toLocalYMD(tomorrow));
            }
          }
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    loadStartDateSetting();
    // When the onboarding wizard completes, show the start-date prompt if still unset
    const onOnboardingDone = async () => {
      if (isWorker) return;
      try {
        const res = await fetch(`${API_URL}/api/settings`, {
          headers: { 'Authorization': `Bearer ${api.getToken()}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (!data.work_planner_start_date) {
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            setStartDateInput(toLocalYMD(tomorrow));
            setShowStartDateModal(true);
          }
        }
      } catch (e) { /* ignore */ }
    };
    window.addEventListener('sf-onboarding-complete', onOnboardingDone);
    return () => window.removeEventListener('sf-onboarding-complete', onOnboardingDone);
  }, [isWorker]);
  
  // Handle setting the work planner start date
  const handleSetStartDate = async () => {
    if (!startDateInput) return;
    
    setSettingStartDate(true);
    try {
      const token = api.getToken();
      const res = await fetch(`${API_URL}/api/settings/work-planner-start-date`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ start_date: startDateInput })
      });
      
      if (res.ok) {
        const data = await res.json();
        setWorkPlannerStartDate(startDateInput);
        setShowStartDateModal(false);
        showNotification(`Schedule start date set! ${data.jobs_auto_completed} past jobs marked as complete.`, 'success');
        // Refresh jobs to show updated statuses
        fetchWeekJobs();
      } else {
        const error = await res.json();
        showNotification(error.detail || 'Failed to set start date', 'error');
      }
    } catch (err) {
      console.error('Error setting start date:', err);
      showNotification('Failed to set start date', 'error');
    }
    setSettingStartDate(false);
  };
  
  // Week dates - MUST be defined before any code that uses it
  const weekDates = useMemo(() => getWeekDates(currentWeekStart), [currentWeekStart]);
  
  // Load day notes when week changes; poll every 60s ONLY when viewing
  // today's week (no point polling historic or future weeks).
  useEffect(() => {
    if (weekDates && weekDates.length >= 7) {
      const startDate = toLocalYMD(weekDates[0]);
      const endDate = toLocalYMD(weekDates[6]);
      loadDayNotes(startDate, endDate);

      const today = toLocalYMD(new Date());
      const isCurrentWeek = today >= startDate && today <= endDate;
      if (!isCurrentWeek) return;  // No polling on future/past weeks

      const notesInterval = setInterval(() => {
        loadDayNotes(startDate, endDate);
      }, 60000);

      return () => clearInterval(notesInterval);
    }
  }, [weekDates, loadDayNotes]);
  
  // Staff list state - loaded from API
  const [staffList, setStaffList] = useState([]);
  const [staffLinkedAssignees, setStaffLinkedAssignees] = useState({}); // Map of staff name -> linked assignees
  const [staffDisplayNames, setStaffDisplayNames] = useState({}); // Map of full name -> display name

  // Bump this counter whenever the round-colour cache refreshes so memoised
  // children re-evaluate with the new colours.
  const [, setRoundColorTick] = useState(0);
  useEffect(() => {
    loadRoundColors().then(() => setRoundColorTick((t) => t + 1));
    const unsub = subscribeToRoundColors(() => setRoundColorTick((t) => t + 1));
    return unsub;
  }, []);
  
  // Fetch team members (staff) from API
  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('streakfree_token');
        const res = await fetch(`${API_URL}/api/team/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          // Extract worker/planner names from team users (exclude Owner)
          const workers = (data.users || [])
            .filter(u => u.role === 'Worker' || u.role === 'Planner')
            .map(u => u.name)
            .filter(Boolean);
          setStaffList(workers);
          
          // Build linked assignees map for filtering
          const linkedMap = {};
          const displayNameMap = {}; // Map full name to display name (linked assignee)
          (data.users || []).forEach(u => {
            if (u.name && u.linked_assignees && u.linked_assignees.length > 0) {
              const key = u.name.toLowerCase().replace(/\s+/g, '_');
              linkedMap[key] = u.linked_assignees.map(a => a.toLowerCase());
              // Use first linked assignee as display name
              displayNameMap[u.name] = u.linked_assignees[0];
            }
          });
          setStaffLinkedAssignees(linkedMap);
          setStaffDisplayNames(displayNameMap);
        }
      } catch (err) {
        console.error('Error fetching staff:', err);
      }
    };
    fetchStaff();
  }, []);
  
  // Extract unique rounds from jobs for JobDetailPanel
  const roundsList = useMemo(() => {
    const rounds = new Set();
    weekJobs.forEach(job => {
      const round = job.customer_zone || job.zone || job.round;
      if (round) rounds.add(round);
    });
    return Array.from(rounds).sort();
  }, [weekJobs]);
  
  // Staff colors for JobDetailPanel - dynamically generate colors
  const getStaffColor = (name) => {
    const defaultColors = ['#10b981', '#84cc16', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'];
    const index = staffList.indexOf(name);
    if (index >= 0) return defaultColors[index % defaultColors.length];
    if (name === 'all') return '#3b82f6';
    return '#8b5cf6';
  };
  
  // Bulk-assign every unassigned job on the selected day to a worker.
  const handleBulkAssign = async (assignee, applyToAll) => {
    setBulkAssigning(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/jobs/bulk-assign`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          job_ids: dayJobs.map(j => j.id),
          assignee,
          apply_to_all: applyToAll,
        }),
      });
      if (!res.ok) throw new Error('Bulk assign failed');
      const data = await res.json();
      fetchWeekJobs();
      showNotification(
        applyToAll
          ? `${data.updated} job${data.updated !== 1 ? 's' : ''} + all future visits assigned to ${assignee}`
          : `${data.updated} job${data.updated !== 1 ? 's' : ''} assigned to ${assignee}`,
        'success'
      );
      setShowBulkAssignModal(false);
      setBulkAssignWorker(null);
    } catch (err) {
      console.error('Bulk assign error:', err);
      showNotification('Failed to assign jobs', 'error');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Handle job update (for edits from JobDetailPanel)
  const handleUpdateJob = async (jobId, updates, mode = 'this') => {
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // If updating price with "all" mode, also update customer's default price
      if (updates.update_customer_price && updates.price !== undefined) {
        const job = weekJobs.find(j => j.id === jobId);
        if (job && job.customer_id) {
          await fetch(`${API_URL}/api/customers/${job.customer_id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ price: updates.price })
          });
          // Tell Customers list / other dependent pages so the customer's
          // default price reflects the new value without a manual refresh.
          customerChanged(Actions.UPDATE, { id: job.customer_id, price: updates.price });
        }
        delete updates.update_customer_price;
      }
      
      // Round (zone) is a customer-level attribute, not a job-level one. When
      // the user edits "Round" from the job detail panel we get _customer_zone
      // on the updates payload — push it to the customer record so the
      // `customer_zone` enrichment used by the live job listing picks up the
      // new value on next refetch.
      if (updates._customer_zone !== undefined) {
        const job = weekJobs.find(j => j.id === jobId);
        if (job && job.customer_id) {
          try {
            const r = await fetch(`${API_URL}/api/customers/${job.customer_id}`, {
              method: 'PATCH',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ zone: updates._customer_zone })
            });
            if (!r.ok) {
              const err = await r.text();
              console.warn('Customer zone PATCH failed:', err);
              alert('Could not save the round change. Check connection and try again.');
              return;
            }
            customerChanged(Actions.UPDATE, { id: job.customer_id, zone: updates._customer_zone });
          } catch (e) {
            console.error('Round-save error:', e);
            alert('Could not save the round change. Check connection and try again.');
            return;
          }
        }
        delete updates._customer_zone;
      }
      
      const res = await fetch(`${API_URL}/api/jobs/${jobId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      
      if (res.ok) {
        const updatedJob = await res.json();
        // Refresh jobs list
        fetchWeekJobs();
        // Update selected job if it's the one being edited
        if (selectedJob && selectedJob.id === jobId) {
          setSelectedJob(prev => ({ ...prev, ...updates, ...updatedJob }));
        }
        showNotification('Job updated successfully', 'success');
      } else {
        showNotification('Failed to update job', 'error');
      }
    } catch (err) {
      console.error('Error updating job:', err);
      showNotification('Error updating job', 'error');
    }
  };
  
  // Handle undo complete for JobDetailPanel
  const handleUndoCompleteJob = async (job) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}/undo-complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchWeekJobs();
        showNotification('Job completion undone', 'success');
        setSelectedJob(null);
      }
    } catch (err) {
      console.error('Error undoing completion:', err);
      showNotification('Error undoing completion', 'error');
    }
  };
  
  // Handle navigate for JobDetailPanel
  const handleNavigate = (job) => {
    const address = job.job_address || job.address_override || job.customer_address || job.address;
    // Check multiple possible field names for coordinates
    const lat = job.latitude || job.lat || job.customer_lat;
    const lng = job.longitude || job.lng || job.customer_lng;
    if (address) {
      openNavigation(address, lat, lng);
    }
  };
  
  // Handle view invoice for JobDetailPanel
  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };
  
  // Fetch weather forecast when week changes
  useEffect(() => {
    const fetchWeather = async () => {
      if (!weekDates || weekDates.length === 0) return;
      
      setWeatherLoading(true);
      try {
        const token = localStorage.getItem('streakfree_token');
        const startDate = toLocalYMD(weekDates[0]);
        
        // Default to Burgess Hill area coordinates
        const response = await fetch(
          `${API_URL}/api/weather/forecast/week?start_date=${startDate}&latitude=50.95&longitude=-0.13`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          setWeatherForecast(data.forecasts || []);
        }
      } catch (err) {
        console.error('Weather fetch error:', err);
        // Don't show error to user - weather is non-critical
      } finally {
        setWeatherLoading(false);
      }
    };
    
    fetchWeather();
  }, [weekDates]);
  
  // Fetch bank holidays and worker leave when week changes
  useEffect(() => {
    const fetchHolidaysAndLeave = async () => {
      if (!weekDates || weekDates.length === 0) return;
      
      const token = localStorage.getItem('streakfree_token');
      const startDate = toLocalYMD(weekDates[0]);
      
      try {
        // Fetch bank holidays
        const holidaysRes = await fetch(
          `${API_URL}/api/holidays/bank-holidays/week?start_date=${startDate}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (holidaysRes.ok) {
          const data = await holidaysRes.json();
          setBankHolidays(data.holidays || []);
        }
        
        // Fetch worker leave
        const leaveRes = await fetch(
          `${API_URL}/api/holidays/leave/week?start_date=${startDate}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        if (leaveRes.ok) {
          const data = await leaveRes.json();
          setWorkerLeave(data.leave_by_date || {});
        }
      } catch (err) {
        console.error('Holidays/leave fetch error:', err);
      }
    };
    
    fetchHolidaysAndLeave();
  }, [weekDates]);
  
  // Check if viewing tomorrow (needs weekDates)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const selectedDate = weekDates[selectedDayIndex];
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = selectedDate && selectedDate.toDateString() === tomorrow.toDateString();
  
  // Fetch jobs from API when week changes
  const fetchWeekJobs = useCallback(async (silent = true) => {
    // Silent by default — most call sites (polls, post-action refreshes,
    // cross-page sync) just want to confirm in the background and shouldn't
    // flash a loading spinner. Pass silent=false explicitly for the initial
    // mount and "Try Again" retry, where a spinner is appropriate.
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);
    
    // CRITICAL: Do NOT short-circuit on navigator.onLine.
    //
    // On iOS PWAs (and to a lesser extent Android Chrome PWAs), navigator.onLine
    // is famously unreliable — it returns `false` when the phone has been off
    // for a few minutes, when you switch between 4G ↔ Wi-Fi, or randomly on
    // wake. When that happens the previous code path went straight to the
    // offline cache, which is either empty (first launch on a new device) or
    // filtered to a different week than the user is currently viewing, so
    // the page showed "0 jobs" and the only fix was logout/login.
    //
    // New behaviour: ALWAYS attempt the live API call. Only fall back to the
    // cache if the fetch actually throws. This keeps true-offline working
    // (where fetch throws a network error) while eliminating the false-offline
    // trap that was hiding live jobs from the user.
    
    try {
      const startDate = toLocalYMD(weekDates[0]);
      const endDate = toLocalYMD(weekDates[6]);
      
      const jobs = await api.getJobs({
        start_date: startDate,
        end_date: endDate,
        limit: 500,
        // Server applies nearest-neighbour route optimisation using the user's
        // configured starting postcode. `route_direction` controls nearest-first
        // (asc) vs furthest-first (desc).
        route_optimized: true,
        route_direction: routeDirection === 'reverse' ? 'desc' : 'asc',
      });
      const transformedJobs = jobs.map(job => transformApiJob(job, weekDates));
      setWeekJobs(transformedJobs);
      
      // Cache jobs for offline access
      cacheJobs(jobs);
      console.log(`[WorkPlanner] Loaded ${transformedJobs.length} jobs for ${startDate} → ${endDate}`);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      
      // Real network failure: try to load from cache as a fallback so the user
      // still sees SOMETHING. We surface an error banner so they know what
      // they're looking at is potentially stale.
      const cachedJobs = getCachedJobs();
      if (cachedJobs.length > 0) {
        console.log('[WorkPlanner] Falling back to cached jobs after fetch failure');
        const startDate = toLocalYMD(weekDates[0]);
        const endDate = toLocalYMD(weekDates[6]);
        const weekFilteredJobs = cachedJobs.filter(job => {
          const jobDate = job.scheduled_date || job.date;
          return jobDate >= startDate && jobDate <= endDate;
        });
        const transformedJobs = weekFilteredJobs.map(job => transformApiJob(job, weekDates));
        setWeekJobs(transformedJobs);
        // Only show an error banner if cache returned zero too — otherwise the
        // user sees their list (slightly stale) and we don't panic them.
        if (transformedJobs.length === 0) {
          setError(err.message || 'Could not load jobs');
        }
      } else {
        setError(err.message || 'Could not load jobs');
      }
    } finally {
      setIsLoading(false);
    }
  }, [weekDates, routeDirection]);
  
  // Fetch jobs when week changes
  useEffect(() => {
    // Loud first load (or week navigation) — show the spinner.
    fetchWeekJobs(false);
  }, [fetchWeekJobs]);

  // Subscribe to centralized data sync - auto refresh when jobs change
  // SILENT — another page made the change, the user shouldn't see a flicker.
  useEffect(() => {
    const unsubscribe = subscribeToRefresh('workplanner', (detail) => {
      console.log('[WorkPlanner] Refresh triggered by:', detail.source);
      fetchWeekJobs(true);
    });
    return unsubscribe;
  }, [fetchWeekJobs]);
  
  // Poll for job updates every 60s ONLY when viewing today's week
  // (to see workers' progress in real-time). No polling on past/future weeks.
  // SILENT — keep existing list visible; swap in new data when it arrives.
  useEffect(() => {
    if (!weekDates || weekDates.length < 7) return;
    const startDate = toLocalYMD(weekDates[0]);
    const endDate = toLocalYMD(weekDates[6]);
    const today = toLocalYMD(new Date());
    if (today < startDate || today > endDate) return;  // No polling on non-current weeks

    const jobsInterval = setInterval(() => {
      fetchWeekJobs(true);
    }, 60000);
    return () => clearInterval(jobsInterval);
  }, [fetchWeekJobs, weekDates]);
  
  // Re-fetch jobs whenever the app comes back to the foreground (PWA wake,
  // tab focus, screen unlock, etc). This is the other half of the iOS PWA
  // false-offline fix — `navigator.onLine` might lie OR the connection might
  // have actually been down briefly while the screen was locked, so the
  // freshest data wins. Silent so the existing list stays visible until the
  // refetch resolves.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        console.log('[WorkPlanner] App became visible — refreshing jobs');
        fetchWeekJobs(true);
      }
    };
    const onFocus = () => {
      console.log('[WorkPlanner] Window focused — refreshing jobs');
      fetchWeekJobs(true);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, [fetchWeekJobs]);
  
  // Apply replanned jobs and status changes
  const adjustedWeekJobs = useMemo(() => {
    return weekJobs.map(job => {
      let adjustedJob = { ...job };
      
      // Apply replanned date
      const newDateStr = replannedJobs[job.id];
      if (newDateStr) {
        const newDayIndex = weekDates.findIndex(d => 
          toLocalYMD(d) === newDateStr
        );
        if (newDayIndex !== -1) {
          adjustedJob = { ...adjustedJob, dayIndex: newDayIndex, date: newDateStr };
        }
      }
      
      // Apply status changes from swipe actions
      const statusChange = jobStatuses[job.id];
      if (statusChange) {
        adjustedJob = { 
          ...adjustedJob, 
          status: statusChange.status,
          skipReason: statusChange.skipReason,
          invoiceId: statusChange.invoiceId,
          invoice_paid: statusChange.invoice_paid || statusChange.invoicePaid,
          completedAt: statusChange.completedAt,
          skippedAt: statusChange.skippedAt
        };
      }
      
      return adjustedJob;
    });
  }, [weekJobs, replannedJobs, weekDates, jobStatuses]);
  
  // Helper function to check if a job is overdue
  const isJobOverdue = useCallback((job) => {
    // No start date means overdue feature is not active
    if (!workPlannerStartDate) return false;
    
    const jobDate = job.scheduled_date || job.date;
    if (!jobDate) return false;
    
    const today = toLocalYMD(new Date());
    const status = job.status?.toLowerCase();
    
    // Job is overdue if:
    // 1. Job date is in the past (before today)
    // 2. Job date is on or after the start date (not auto-completed)
    // 3. Job status is not done/completed/skipped
    return (
      jobDate < today &&
      jobDate >= workPlannerStartDate &&
      status !== 'done' && 
      status !== 'completed' && 
      status !== 'skipped'
    );
  }, [workPlannerStartDate]);
  
  // Filter jobs for worker role - they only see their own jobs
  const workerFilteredJobs = useMemo(() => {
    if (!isWorker) return adjustedWeekJobs;
    
    // Workers only see jobs assigned to them
    return adjustedWeekJobs.filter(job => {
      if (!job.assignee) return false;
      const assigneeNames = job.assignee.toLowerCase().split(',').map(s => s.trim());
      const workerFirstName = userName.split(' ')[0].toLowerCase();
      return assigneeNames.some(name => name.includes(workerFirstName) || workerFirstName.includes(name));
    });
  }, [adjustedWeekJobs, isWorker, userName]);
  
  const dayJobs = useMemo(() => {
    const jobsForDay = workerFilteredJobs.filter(job => job.dayIndex === selectedDayIndex);
    
    // Workers don't get assignee filter - they only see their own jobs
    if (isWorker) return jobsForDay;
    
    // Filter by assignee inline
    if (selectedAssignee === 'all') return jobsForDay;
    if (selectedAssignee === 'unassigned') {
      return jobsForDay.filter(j => !j.assignee || j.assignee.trim() === '');
    }
    
    // Use linked assignees for filtering (dynamic, not hardcoded)
    const linkedNames = staffLinkedAssignees[selectedAssignee] || [];
    if (linkedNames.length > 0) {
      return jobsForDay.filter(j => {
        if (!j.assignee) return false;
        const jobAssignees = j.assignee.toLowerCase().split(',').map(s => s.trim());
        // Check if any linked name matches any job assignee
        return linkedNames.some(linked => 
          jobAssignees.some(jobA => jobA === linked || jobA.includes(linked) || linked.includes(jobA))
        );
      });
    }
    
    // Fallback: match by first part of filter name
    const filterFirstName = selectedAssignee.split('_')[0].toLowerCase();
    return jobsForDay.filter(j => {
      if (!j.assignee) return false;
      const primaryAssignee = j.assignee.split(',')[0].trim().toLowerCase();
      return primaryAssignee === filterFirstName || primaryAssignee.includes(filterFirstName);
    });
  }, [workerFilteredJobs, selectedDayIndex, selectedAssignee, isWorker, staffLinkedAssignees]);
  
  // All jobs for the day (unfiltered) - for total count display (also filtered for workers)
  const allDayJobs = useMemo(() => {
    return workerFilteredJobs.filter(job => job.dayIndex === selectedDayIndex);
  }, [workerFilteredJobs, selectedDayIndex]);
  
  // Get date key for custom order storage
  const currentDateKey = useMemo(() => {
    const date = weekDates[selectedDayIndex];
    return date ? toLocalYMD(date) : '';
  }, [weekDates, selectedDayIndex]);
  
  // Sort jobs: Scheduled first, then Completed, then Skipped
  // Respects custom drag-and-drop order if set
  // Applies route direction (forward/reverse) when no custom order
  const numberedDayJobs = useMemo(() => {
    // Apply round filter (if user tapped a round badge)
    const filteredDayJobs = roundFilter
      ? dayJobs.filter(j => (j.zone || j.round) === roundFilter)
      : dayJobs;

    // Check if we have a custom order for this day
    const customOrder = customJobOrder[currentDateKey];
    
    let sortedJobs;
    if (customOrder && customOrder.length > 0) {
      // Use custom order - put jobs in the order specified, any new jobs at the end
      const orderMap = new Map(customOrder.map((id, idx) => [id, idx]));
      sortedJobs = [...filteredDayJobs].sort((a, b) => {
        const aIdx = orderMap.has(a.id) ? orderMap.get(a.id) : 9999;
        const bIdx = orderMap.has(b.id) ? orderMap.get(b.id) : 9999;
        return aIdx - bIdx;
      });
    } else {
      // Default: trust the order returned by the API. The server has already
      // applied nearest-neighbour route optimisation using the user's starting
      // postcode and the chosen route_direction (nearest-first vs furthest-first).
      // We just keep that order, surfacing scheduled jobs first, then completed,
      // then skipped — without re-shuffling the spatial sort.
      const scheduled = filteredDayJobs.filter(j => !j.status || j.status === 'scheduled' || j.status === 'pending');
      const completed = filteredDayJobs.filter(j => j.status === 'done' || j.status === 'completed');
      const skipped = filteredDayJobs.filter(j => j.status === 'skipped');
      sortedJobs = [...scheduled, ...completed, ...skipped];
    }
    
    // Add job numbers based on sorted position
    return sortedJobs.map((job, index) => ({
      ...job,
      jobNumber: index + 1
    }));
  }, [dayJobs, customJobOrder, currentDateKey, roundFilter]);
  
  const weekStats = useMemo(() => {
    const total = workerFilteredJobs.reduce((sum, job) => sum + (job.price || 0), 0);
    return {
      jobCount: workerFilteredJobs.length,
      totalValue: total
    };
  }, [workerFilteredJobs]);
  
  // Stats for ALL jobs on the day (unfiltered) - for the summary bar
  const allDayStats = useMemo(() => {
    const completed = allDayJobs.filter(j => j.status === 'done');
    const notDone = allDayJobs.filter(j => j.status !== 'done');
    
    const calcTime = (jobs) => {
      const totalMinutes = jobs.reduce((sum, job) => {
        const duration = job.duration || '00:30';
        const parts = duration.split(':');
        return sum + (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1]) || 30);
      }, 0);
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      return `${hours}h ${mins}m`;
    };
    
    return {
      // "Jobs" headline = jobs to action today (incl. completed). Skipped is
      // excluded because that work isn't being done — Skipped has its own tile.
      total: allDayJobs.filter(j => j.status !== 'skipped').length,
      totalValue: allDayJobs
        .filter(j => j.status !== 'skipped')
        .reduce((sum, job) => sum + (job.price || 0), 0),
      totalTime: calcTime(allDayJobs.filter(j => j.status !== 'skipped')),
      completed,
      notDone
    };
  }, [allDayJobs]);
  
  // Save job order to backend - updates sort_order on jobs
  const saveJobOrderToBackend = useCallback(async (date, jobIds) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/jobs/order`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ date, job_ids: jobIds })
      });
      if (res.ok) {
        // Jobs now have updated sort_order - will apply to recurring appointments
        console.log('Job order saved - will apply to recurring appointments');
      }
    } catch (err) {
      console.error('Failed to save job order:', err);
    }
  }, []);
  
  // Pin a single job to a specific position. Server keeps the other day's
  // jobs route-optimised around it. Persists to customer for recurrence.
  const pinJobPosition = useCallback(async (jobId, position) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/jobs/${jobId}/pin-position`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ position })
      });
      // Silently re-fetch so the server's optimised+pinned merge is reflected
      fetchWeekJobs(true);
    } catch (err) {
      console.error('Failed to pin job position:', err);
    }
  }, [fetchWeekJobs]);
  
  // Handle drag end for job reordering
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id && over) {
      const oldIndex = numberedDayJobs.findIndex(j => j.id === active.id);
      const newIndex = numberedDayJobs.findIndex(j => j.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        // Optimistic local reorder
        const newOrder = arrayMove(numberedDayJobs.map(j => j.id), oldIndex, newIndex);
        setCustomJobOrder(prev => ({
          ...prev,
          [currentDateKey]: newOrder
        }));
        
        // Pin ONLY the dragged job — others stay route-optimised
        pinJobPosition(active.id, newIndex);
        
        toast.success('Pinned to position', {
          description: 'Other jobs stay route-optimised around it'
        });
      }
    }
  }, [numberedDayJobs, currentDateKey, pinJobPosition]);
  
  // Clear custom order for current day
  const clearCustomOrder = useCallback(async () => {
    setCustomJobOrder(prev => {
      const newOrder = { ...prev };
      delete newOrder[currentDateKey];
      return newOrder;
    });
    
    // Delete from backend
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/jobs/order/${currentDateKey}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Failed to delete job order:', err);
    }
    
    toast.success('Order reset to default');
  }, [currentDateKey]);
  
  // Check if current day has custom order
  const hasCustomOrder = customJobOrder[currentDateKey]?.length > 0;
  
  
  // Stats for filtered jobs (current assignee view)
  const dayStats = useMemo(() => {
    const completed = numberedDayJobs.filter(j => j.status === 'done');
    const notDone = numberedDayJobs.filter(j => j.status !== 'done');
    
    const calcTime = (jobs) => {
      const totalMinutes = jobs.reduce((sum, job) => {
        const duration = job.duration || '00:30';
        const parts = duration.split(':');
        return sum + (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1]) || 30);
      }, 0);
      const hours = Math.floor(totalMinutes / 60);
      const mins = totalMinutes % 60;
      return `${hours}h ${mins}m`;
    };
    
    return {
      // "Jobs" headline = work-to-do (incl. completed). Skipped excluded — it
      // has its own tile, and counting it would inflate the "Jobs" number
      // (per user 22 Jun 2026: "8 jobs - 1 skip = 7, not 8").
      total: numberedDayJobs.filter(j => j.status !== 'skipped').length,
      totalValue: numberedDayJobs
        .filter(j => j.status !== 'skipped')
        .reduce((sum, job) => sum + (job.price || 0), 0),
      totalTime: calcTime(numberedDayJobs.filter(j => j.status !== 'skipped')),
      completed,
      notDone
    };
  }, [numberedDayJobs]);
  
  const jobsPerDay = useMemo(() => {
    return weekDates.map((_, index) => workerFilteredJobs.filter(j => j.dayIndex === index).length);
  }, [weekDates, workerFilteredJobs]);
  
  // Calculate detailed stats per day (total, completed, skipped) for progress bars
  const dayProgressStats = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return weekDates.map((date, index) => {
      const dayJobs = workerFilteredJobs.filter(j => j.dayIndex === index);
      const total = dayJobs.length;
      
      // Check if this day is in the past
      const dayDate = new Date(date);
      dayDate.setHours(0, 0, 0, 0);
      const isPast = dayDate < today;
      
      // For past days, count actual completed/skipped from database OR local state
      const completed = dayJobs.filter(j => {
        const localStatus = jobStatuses[j.id];
        const dbStatus = j.status;
        return localStatus === 'completed' || localStatus === 'done' || 
               dbStatus === 'completed' || dbStatus === 'done';
      }).length;
      
      const skipped = dayJobs.filter(j => {
        const localStatus = jobStatuses[j.id];
        const dbStatus = j.status;
        return localStatus === 'skipped' || dbStatus === 'skipped';
      }).length;
      
      const pending = total - completed - skipped;
      
      // Calculate overdue jobs: past date, after start date, and not completed/skipped
      const todayStr = toLocalYMD(new Date());
      const dateStr = toLocalYMD(weekDates[index]);
      const overdue = dayJobs.filter(j => {
        const localStatus = jobStatuses[j.id];
        const dbStatus = j.status;
        const status = localStatus?.status || dbStatus;
        const isCompletedOrSkipped = status === 'done' || status === 'completed' || status === 'skipped';
        // Overdue if: past date + has start date set + date >= start date + not completed
        return isPast && workPlannerStartDate && dateStr >= workPlannerStartDate && !isCompletedOrSkipped;
      }).length;
      
      // For PAST days before the start date: treat pending as completed (historical)
      // For PAST days on/after start date: show overdue count
      const isBeforeStartDate = workPlannerStartDate && dateStr < workPlannerStartDate;
      const effectiveCompleted = isPast && isBeforeStartDate ? (completed + pending) : completed;
      const effectivePending = isPast ? 0 : pending;
      
      return {
        total,
        completed: effectiveCompleted,
        skipped,
        pending: effectivePending,
        overdue,
        isPast,
        isBeforeStartDate,
        // Calculate percentages for the progress bar
        completedPercent: total > 0 ? (effectiveCompleted / total) * 100 : 0,
        skippedPercent: total > 0 ? (skipped / total) * 100 : 0,
        overduePercent: total > 0 ? (overdue / total) * 100 : 0
      };
    });
  }, [weekDates, workerFilteredJobs, jobStatuses, workPlannerStartDate]);
  
  const prevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };
  
  const nextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };
  
  const handleDateSelect = (date) => {
    // Set the week to contain the selected date
    const newStart = new Date(date);
    // Find the Monday of that week
    const day = newStart.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Adjust for Sunday
    newStart.setDate(newStart.getDate() + diff);
    setCurrentWeekStart(newStart);
    
    // Set the selected day index based on the selected date
    const selectedDay = date.getDay();
    const dayIndex = selectedDay === 0 ? 6 : selectedDay - 1; // Convert to Monday=0 index
    setSelectedDayIndex(dayIndex);
  };
  
  // Handle replan confirmation
  const handleReplanConfirm = () => {
    // Store the jobs being replanned with their original dates
    const jobIds = Array.from(selectedJobs);
    setJobsToReplan(jobIds);
    setShowReplanConfirm(false);
    setShowReplanSchedule(true);
  };
  
  // Get the original date of the currently selected day (for recurring job exceptions)
  const getOriginalDateForReplan = () => {
    const date = weekDates[selectedDayIndex];
    return date ? toLocalYMD(date) : null;
  };
  
  // Handle replan with or without schedule update
  const handleReplanComplete = async (updateSchedule) => {
    // Move the selected jobs to the new date
    const newDateStr = toLocalYMD(replanTargetDay);
    const originalDateStr = getOriginalDateForReplan();
    
    try {
      const token = localStorage.getItem('streakfree_token');
      
      // Build the URL with query params - include original_date for recurring exceptions
      let url = `${API_URL}/api/jobs/bulk-reschedule?new_date=${newDateStr}&update_recurring=${updateSchedule}`;
      if (!updateSchedule && originalDateStr) {
        url += `&original_date=${originalDateStr}`;
      }
      
      // Call the bulk reschedule API
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobsToReplan)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to reschedule jobs');
      }
      
      const result = await response.json();
      
      // Clear the replannedJobs entries for moved jobs - we'll get fresh data from the API
      setReplannedJobs(prev => {
        const newState = { ...prev };
        jobsToReplan.forEach(jobId => {
          delete newState[jobId];
        });
        return newState;
      });
      
      // Show success notification
      showNotification(`Moved ${result.updated_count} job${result.updated_count !== 1 ? 's' : ''} to ${newDateStr}${updateSchedule ? ' (schedule updated)' : ''}`, 'success');
      
      // Check if target date is in the current week view
      const targetDate = new Date(replanTargetDay);
      const currentWeekStart = new Date(weekDates[0]);
      const currentWeekEnd = new Date(weekDates[6]);
      
      const isTargetInCurrentWeek = targetDate >= currentWeekStart && targetDate <= currentWeekEnd;
      
      // Find the target day index within current week (will be -1 if in different week)
      const targetDayIndex = weekDates.findIndex(d => 
        d.getDate() === replanTargetDay.getDate() && 
        d.getMonth() === replanTargetDay.getMonth()
      );
      
      // Clear selection and close modals
      setSelectedJobs(new Set());
      setJobsToReplan([]);
      setShowReplanSchedule(false);
      setReplanTargetDay(null);
      
      if (isTargetInCurrentWeek && targetDayIndex !== -1) {
        // Target is in current week - switch to that day and refresh
        setSelectedDayIndex(targetDayIndex);
        await fetchWeekJobs();
      } else {
        // Target is in a different week - navigate to that week
        // Calculate the Monday of the target week
        const targetWeekStart = new Date(targetDate);
        const dayOfWeek = targetWeekStart.getDay();
        const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday = 1
        targetWeekStart.setDate(targetWeekStart.getDate() - daysToSubtract);
        targetWeekStart.setHours(0, 0, 0, 0);
        
        // Update the current week start - this will trigger fetchWeekJobs via useEffect
        setCurrentWeekStart(targetWeekStart);
        
        // Find which day of the week the target is (0=Mon, 1=Tue, etc)
        const targetDayOfWeek = targetDate.getDay();
        const targetIndex = targetDayOfWeek === 0 ? 6 : targetDayOfWeek - 1;
        setSelectedDayIndex(targetIndex);
      }
      
    } catch (err) {
      console.error('Error rescheduling jobs:', err);
      showNotification(err.message || 'Failed to reschedule jobs', 'error');
    }
  };
  
  // Cancel replan
  const handleReplanCancel = () => {
    setShowReplanConfirm(false);
    setShowReplanSchedule(false);
    setReplanTargetDay(null);
    setJobsToReplan([]);
  };
  
  // Shift the entire schedule forward / back by N days. Squeegee-style:
  // every pending job from today onward slides by `shiftDays`. Completed
  // jobs are left alone so the history stays accurate.
  const handleShiftSchedule = async () => {
    if (!shiftDays || shiftDays === 0) {
      showNotification('Choose a number of days to shift', 'error');
      return;
    }
    setShiftBusy(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/jobs/shift-schedule`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ days: shiftDays }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to shift schedule');
      }
      const data = await res.json();
      setShowShiftModal(false);
      setShowShiftMenu(false);
      showNotification(data.message || `Shifted ${data.updated_count} jobs`, 'success');
      // Force a reload of the planner — the simplest reliable way to pick
      // up the new dates across every cached job state.
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      showNotification(err.message || 'Failed to shift schedule', 'error');
    } finally {
      setShiftBusy(false);
    }
  };
  
  // Send reminders for tomorrow's jobs (all or selected)
  const handleSendReminders = async (type, selectedJobIds = null) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      // Use selected job IDs if provided, otherwise all jobs for the day
      const jobIds = selectedJobIds || numberedDayJobs.map(j => j.id);
      
      const response = await fetch(`${API_URL}/api/jobs/send-reminders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_ids: jobIds,
          reminder_type: type, // 'sms', 'email', or 'both'
          scheduled_date: toLocalYMD(selectedDate)
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        // Clear selection after sending reminders
        if (selectedJobIds) {
          setSelectedJobs(new Set());
        }
        return result; // Return result for optimistic update
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send reminders');
      }
    } catch (err) {
      console.error('Error sending reminders:', err);
      throw err;
    }
  };
  
  // Bulk message selected customers
  const handleBulkMessage = async () => {
    if (!bulkMessage.trim()) {
      showNotification('Please enter a message', 'error');
      return;
    }
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const jobIds = Array.from(selectedJobs);
      
      const response = await fetch(`${API_URL}/api/jobs/bulk-message`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_ids: jobIds,
          message: bulkMessage,
          message_type: bulkMessageType
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        showNotification(`Message sent to ${result.sent_count} customers`, 'success');
        setShowBulkMessageModal(false);
        setBulkMessage('');
        setSelectedJobs(new Set());
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to send messages', 'error');
      }
    } catch (err) {
      console.error('Error sending bulk message:', err);
      showNotification('Failed to send messages', 'error');
    }
  };
  
  // Reassign worker for selected jobs
  const handleReassignWorker = async (newWorker) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const jobIds = Array.from(selectedJobs);
      
      const response = await fetch(`${API_URL}/api/jobs/bulk-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_ids: jobIds,
          assignee: newWorker
        })
      });
      
      if (response.ok) {
        showNotification(`${jobIds.length} jobs reassigned to ${newWorker}`, 'success');
        setShowReassignWorkerModal(false);
        setSelectedJobs(new Set());
        fetchWeekJobs(); // Refresh
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to reassign', 'error');
      }
    } catch (err) {
      console.error('Error reassigning worker:', err);
      showNotification('Failed to reassign', 'error');
    }
  };
  
  // Reassign round for selected jobs
  const handleReassignRound = async (newRound) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const jobIds = Array.from(selectedJobs);
      
      const response = await fetch(`${API_URL}/api/jobs/bulk-update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          job_ids: jobIds,
          round: newRound,
          zone: newRound
        })
      });
      
      if (response.ok) {
        showNotification(`${jobIds.length} jobs moved to ${newRound}`, 'success');
        setShowReassignRoundModal(false);
        setSelectedJobs(new Set());
        fetchWeekJobs(); // Refresh
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to reassign', 'error');
      }
    } catch (err) {
      console.error('Error reassigning round:', err);
      showNotification('Failed to reassign', 'error');
    }
  };
  
  // Bulk mark selected jobs as done — loops the real complete endpoint so
  // recurrence rolls forward and Live Mode is respected. bulk-update was a
  // raw status write that silently dropped customers off future planning.
  const handleBulkMarkDone = async () => {
    const jobIds = Array.from(selectedJobs);
    if (jobIds.length === 0) return;

    const token = localStorage.getItem('streakfree_token');
    const dateStr = selectedDate ? toLocalYMD(selectedDate) : null;
    let done = 0, failed = 0;

    for (const id of jobIds) {
      try {
        const res = await fetch(`${API_URL}/api/jobs/${id}/complete`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ send_notification: false, occurrence_date: dateStr })
        });
        if (res.ok) {
          done++;
          setJobStatuses(prev => ({
            ...prev,
            [id]: { status: 'done', completedAt: new Date().toISOString() }
          }));
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
      }
    }

    showNotification(
      failed
        ? `${done} marked done, ${failed} failed — try again`
        : `${done} job${done !== 1 ? 's' : ''} marked as done`,
      failed ? 'error' : 'success'
    );
    setSelectedJobs(new Set());
    setTimeout(() => fetchWeekJobs(), 500);
  };
  
  // Bulk mark selected jobs as skipped — loops the real skip endpoint so the
  // schedule rolls forward (bulk-update was a raw status write).
  const handleBulkSkip = async () => {
    const jobIds = Array.from(selectedJobs);
    if (jobIds.length === 0) return;

    const token = localStorage.getItem('streakfree_token');
    const dateStr = selectedDate ? toLocalYMD(selectedDate) : null;
    let done = 0, failed = 0;

    for (const id of jobIds) {
      try {
        const params = new URLSearchParams({ reason: 'skipped' });
        if (dateStr) params.set('occurrence_date', dateStr);
        const res = await fetch(`${API_URL}/api/jobs/${id}/skip?${params.toString()}`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          done++;
          setJobStatuses(prev => ({
            ...prev,
            [id]: { status: 'skipped', skippedAt: new Date().toISOString() }
          }));
        } else {
          failed++;
        }
      } catch (err) {
        failed++;
      }
    }

    showNotification(
      failed
        ? `${done} skipped, ${failed} failed — try again`
        : `${done} job${done !== 1 ? 's' : ''} skipped`,
      failed ? 'error' : 'success'
    );
    setSelectedJobs(new Set());
    setTimeout(() => fetchWeekJobs(), 500);
  };
  
  // Show notification helper with optional undo
  const showNotification = (message, type = 'success', canUndo = false) => {
    setNotification({ message, type, canUndo });
    // Auto-dismiss timing tuned to severity:
    //  - error: 8s — payment failures need to be SEEN, not glanced past as
    //    the worker swipes onto the next job. (Amber's Debbie Cox case.)
    //  - canUndo: 5s — long enough to tap Undo deliberately
    //  - everything else: 3s
    let timeout = 3000;
    if (type === 'error') timeout = 8000;
    else if (canUndo) timeout = 5000;
    setTimeout(() => setNotification(null), timeout);
  };
  
  // Undo last action
  const handleUndo = () => {
    if (!lastAction) return;
    
    const { type, jobId, previousStatus, invoiceId, owingId } = lastAction;
    
    if (type === 'complete') {
      // Remove the job status change
      setJobStatuses(prev => {
        const newStatuses = { ...prev };
        if (previousStatus) {
          newStatuses[jobId] = previousStatus;
        } else {
          delete newStatuses[jobId];
        }
        return newStatuses;
      });
      
      // Remove the invoice
      if (invoiceId) {
        setInvoices(prev => prev.filter(inv => inv.id !== invoiceId));
      }
      
      // Remove from owings if added
      if (owingId) {
        setOwings(prev => prev.filter(owe => owe.id !== owingId));
      }
      
      showNotification('Action undone - job restored', 'info', false);
    } else if (type === 'skip') {
      // Remove the skip status
      setJobStatuses(prev => {
        const newStatuses = { ...prev };
        if (previousStatus) {
          newStatuses[jobId] = previousStatus;
        } else {
          delete newStatuses[jobId];
        }
        return newStatuses;
      });
      
      showNotification('Action undone - job restored', 'info', false);
    }
    
    setLastAction(null);
    setNotification(null);
  };
  
  // Handle job completion (swipe right)
  const handleCompleteJob = async (job, options) => {
    const { sendMethod = 'none', hasPaymentMethod, paymentType, chargeAmount = null, skipAutoCharge = false, frontOnly = false } = options;
    const now = new Date().toISOString();
    
    // Convert sendMethod to sendNotification for backwards compatibility with API
    // 'sms', 'email', 'both' all need notification; 'none' doesn't
    const sendNotification = sendMethod !== 'none';
    
    // Use chargeAmount if provided (user-confirmed), otherwise use job price
    const finalPrice = chargeAmount !== null ? chargeAmount : (job.price || 0);
    
    // Resolve payment_route to send to backend. Only forward when the customer
    // actually has both methods available — otherwise the backend's default
    // priority (Stripe → GC) handles it correctly. This avoids overriding when
    // it's pointless.
    let paymentRoute = null;
    if (job.customer_has_stripe && job.customer_has_gocardless && paymentType) {
      const norm = String(paymentType).toLowerCase();
      if (norm === 'stripe' || norm === 'card') paymentRoute = 'stripe';
      else if (norm === 'gocardless') paymentRoute = 'gocardless';
    }
    
    // Store previous state for undo
    const previousStatus = jobStatuses[job.id] || null;
    
    // Optimistic update - update UI immediately. NOTE: we DON'T assume the
    // charge will succeed when hasPaymentMethod=true. Previously this set
    // `invoice_paid: hasPaymentMethod && !skipAutoCharge` unconditionally,
    // which left Amber's UI showing "paid" even when the actual Stripe
    // charge had failed (Debbie Cox bug, May 2026). The correct paid
    // status is sourced from the API response below — until then we show
    // a neutral "pending" pill so we never claim payment took without
    // confirmation from the backend.
    setJobStatuses(prev => ({
      ...prev,
      [job.id]: {
        status: 'done',
        invoiceId: 'pending',
        invoice_paid: false,
        completedAt: now,
        notificationSent: sendNotification,
        sendMethod: sendMethod
      }
    }));
    
    try {
      // Capture worker GPS (best-effort, 3s timeout) for the server-side
      // pin auto-correction feature. Failure is silent — job still completes.
      let workerLat = null, workerLng = null;
      try {
        const gps = await new Promise((resolve) => {
          if (!('geolocation' in navigator)) return resolve(null);
          let done = false;
          const timer = setTimeout(() => { if (!done) { done = true; resolve(null); } }, 3000);
          try {
            navigator.geolocation.getCurrentPosition(
              (pos) => { if (!done) { done = true; clearTimeout(timer); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); } },
              () => { if (!done) { done = true; clearTimeout(timer); resolve(null); } },
              { enableHighAccuracy: true, timeout: 3000, maximumAge: 30000 }
            );
          } catch (e) { clearTimeout(timer); resolve(null); }
        });
        if (gps) { workerLat = gps.lat; workerLng = gps.lng; }
      } catch (e) { /* swallow */ }

      // Call API to complete job with sendMethod, optional chargeAmount,
      // optional payment_route (when customer has both Stripe + GC), and
      // skip_auto_charge (when user clicked "send invoice instead").
      const result = await api.completeJob(job.id, sendNotification, sendMethod, chargeAmount, paymentRoute, skipAutoCharge, workerLat, workerLng, job.scheduled_date || null, frontOnly);
      
      // If the backend detected the worker is in the "near-but-not-on" GPS
      // sweet spot (50-1000m from the saved pin) AND the pin isn't manually
      // locked, surface the pin-correction prompt. Never auto-applies —
      // always requires explicit user action.
      if (result?.pin_correction_suggestion) {
        setPinCorrectionSuggestion(result.pin_correction_suggestion);
      }
      
      // Update with actual invoice ID and the ACTUAL paid status returned
      // by the backend. The backend tells us whether the auto-charge
      // succeeded (`invoice_paid: true`) or fell through to the unpaid /
      // Owings path (`invoice_paid: false` + `auto_charge_error: "..."`).
      // We must use this — never the optimistic guess from before the
      // API call — or the UI will lie about whether the customer paid.
      setJobStatuses(prev => ({
        ...prev,
        [job.id]: {
          ...prev[job.id],
          invoiceId: result.invoice_id,
          invoice_paid: result.invoice_paid === true || result.invoice_status === 'paid',
        }
      }));
      
      setLastAction({
        type: 'complete',
        jobId: job.id,
        previousStatus,
        invoiceId: result.invoice_id,
        job
      });
      
      // Show notification based on sendMethod
      let methodText = '';
      if (sendMethod === 'sms') methodText = ' - WhatsApp/SMS sent';
      else if (sendMethod === 'email') methodText = ' - Email sent';
      else if (sendMethod === 'both') methodText = ' - Email & WhatsApp/SMS sent';
      
      if (result.invoice_status === 'paid') {
        showNotification(`Job completed! £${finalPrice.toFixed(2)} charged via auto-pay${methodText}`, 'success', true);
      } else if (result.auto_charge_error) {
        // Charge failed (declined card, expired key, etc). The backend has
        // already flipped the invoice to unpaid, added the customer to
        // Owings, sent them the invoice via Email + SMS/WhatsApp, raised a
        // bell notification, and emailed the owner. We surface a CLEAR
        // red toast so the field worker (e.g. Amber) knows the customer
        // wasn't actually charged — without this they'd glance at the
        // completed card and assume payment took. The 'error' severity
        // gives it the red background + XCircle icon (vs the amber
        // "warning" we use for routine no-payment-method jobs).
        const customerLabel = job.customerName || job.customer_name || 'Customer';
        showNotification(
          `${customerLabel}'s payment was declined — invoice sent + customer flagged on Owings`,
          'error',
          true,
        );
      } else {
        showNotification(`Job completed! £${finalPrice.toFixed(2)} added to Owings${methodText}`, 'warning', true);
      }
      
    } catch (err) {
      console.error('Failed to complete job:', err);
      // Revert optimistic update
      if (previousStatus) {
        setJobStatuses(prev => ({ ...prev, [job.id]: previousStatus }));
      } else {
        setJobStatuses(prev => {
          const newState = { ...prev };
          delete newState[job.id];
          return newState;
        });
      }
      showNotification(`Failed to complete job: ${err.message}`, 'error', false);
    }
  };
  
  // Handle job skip (swipe left)
  const handleSkipJob = async (job, reason) => {
    const now = new Date().toISOString();
    
    // Store previous state for undo
    const previousStatus = jobStatuses[job.id] || null;
    
    // Optimistic update
    setJobStatuses(prev => ({
      ...prev,
      [job.id]: {
        status: 'skipped',
        skipReason: reason,
        skippedAt: now
      }
    }));
    
    try {
      // Call API to skip job — returns {next_job_id} when roll-forward fires.
      // Pass the job's scheduled_date (the day it appears on in the planner)
      // so the skipped row stays on the user's current day-view rather than
      // jumping to its DB anchor date.
      const skipResult = await api.skipJob(job.id, reason, job.scheduled_date || null);
      
      setLastAction({
        type: 'skip',
        jobId: job.id,
        previousStatus,
        job,
        reason
      });
      
      // Build a confirmation toast that EXPLICITLY shows the next-due date so
      // the user can see at a glance that the skip rolled forward correctly.
      // (Previously this was just "Job skipped: {reason}" — easy to miss; some
      // users tapped Skip several times thinking the action hadn't saved,
      // pushing the schedule out by months. Showing "next visit Thu 30 Jul"
      // removes the ambiguity.)
      let nextDueLabel = '';
      const nextJobId = skipResult && (skipResult.next_job_id || skipResult.nextJobId);
      if (nextJobId) {
        try {
          const tok = localStorage.getItem('streakfree_token');
          const r = await fetch(`${API_URL}/api/jobs/${nextJobId}`, {
            headers: { 'Authorization': `Bearer ${tok}` }
          });
          if (r.ok) {
            const nj = await r.json();
            if (nj.scheduled_date) {
              const d = new Date(nj.scheduled_date + 'T00:00:00');
              nextDueLabel = d.toLocaleDateString('en-GB', {
                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
              });
            }
          }
        } catch (e) { /* non-fatal — fall back to simple message */ }
      }
      const msg = nextDueLabel
        ? `${job.customer_name || 'Job'} skipped — next visit ${nextDueLabel}`
        : `Job skipped: ${reason}`;
      showNotification(msg, 'success', true);
      
    } catch (err) {
      console.error('Failed to skip job:', err);
      // Revert optimistic update
      if (previousStatus) {
        setJobStatuses(prev => ({ ...prev, [job.id]: previousStatus }));
      } else {
        setJobStatuses(prev => {
          const newState = { ...prev };
          delete newState[job.id];
          return newState;
        });
      }
      showNotification(`Failed to skip job: ${err.message}`, 'error', false);
    }
  };
  
  // Handle undo complete (revert job back to scheduled)
  const handleUndoComplete = async (job) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}/undo-complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Remove the job status from local state to show as scheduled again
        setJobStatuses(prev => {
          const newState = { ...prev };
          delete newState[job.id];
          return newState;
        });
        
        // Refresh jobs to get updated data
        fetchWeekJobs();
        
        showNotification('Job reverted to scheduled', 'info', false);
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to undo completion', 'error', false);
      }
    } catch (err) {
      console.error('Failed to undo complete:', err);
      showNotification('Failed to undo completion', 'error', false);
    }
  };
  
  // Handle undo skip (revert skipped job back to scheduled)
  const handleUndoSkip = async (job) => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/jobs/${job.id}/unskip`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        // Remove the job status from local state to show as scheduled again
        setJobStatuses(prev => {
          const newState = { ...prev };
          delete newState[job.id];
          return newState;
        });
        
        // Refresh jobs to get updated data
        fetchWeekJobs();
        
        showNotification('Job unskipped - back to scheduled', 'info', false);
      } else {
        const error = await response.json();
        showNotification(error.detail || 'Failed to undo skip', 'error', false);
      }
    } catch (err) {
      console.error('Failed to undo skip:', err);
      showNotification('Failed to undo skip', 'error', false);
    }
  };

  return (
    <div data-testid="work-planner-page" className="h-full flex flex-col bg-zinc-100">
      {/* Toast Notification with Undo */}
      {notification && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 animate-fade-in max-w-[92vw] ${
          notification.type === 'success' ? 'bg-emerald-500 text-white' :
          notification.type === 'warning' ? 'bg-amber-500 text-white' :
          notification.type === 'error' ? 'bg-red-600 text-white ring-4 ring-red-300/40 animate-pulse-once' :
          'bg-zinc-700 text-white'
        }`}>
          {notification.type === 'success' && <CheckCircle2 size={20} />}
          {notification.type === 'warning' && <PoundSterling size={20} />}
          {notification.type === 'error' && <XCircle size={22} strokeWidth={2.5} />}
          {notification.type === 'info' && <SkipForward size={20} />}
          <span className="font-medium">{notification.message}</span>
          
          {/* Undo button */}
          {notification.canUndo && lastAction && (
            <>
              <div className="w-px h-5 bg-white/30 mx-1" />
              <button
                onClick={handleUndo}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors font-semibold text-sm"
                data-testid="undo-btn"
              >
                <RotateCcw size={14} />
                Undo
              </button>
            </>
          )}
        </div>
      )}
      
      {/* Compact Mobile Header - Entire section is swipeable */}
      <div 
        className="header bg-zinc-900 text-white select-none pt-2" 
        style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}
        onTouchStart={(e) => {
          const touch = e.touches[0];
          e.currentTarget.dataset.touchStartX = touch.clientX.toString();
          e.currentTarget.dataset.touchStartY = touch.clientY.toString();
          e.currentTarget.dataset.touchStartTime = Date.now().toString();
        }}
        onTouchMove={(e) => {
          // Calculate movement to determine if this is a horizontal swipe
          const touchStartX = parseFloat(e.currentTarget.dataset.touchStartX || '0');
          const touchStartY = parseFloat(e.currentTarget.dataset.touchStartY || '0');
          const touchCurrentX = e.touches[0].clientX;
          const touchCurrentY = e.touches[0].clientY;
          const diffX = Math.abs(touchStartX - touchCurrentX);
          const diffY = Math.abs(touchStartY - touchCurrentY);
          
          // If horizontal movement is dominant, prevent vertical scroll
          if (diffX > 10 && diffX > diffY * 1.5) {
            e.currentTarget.dataset.isHorizontalSwipe = 'true';
          }
        }}
        onTouchEnd={(e) => {
          const touchStartX = parseFloat(e.currentTarget.dataset.touchStartX || '0');
          const touchStartY = parseFloat(e.currentTarget.dataset.touchStartY || '0');
          const touchEndX = e.changedTouches[0].clientX;
          const touchEndY = e.changedTouches[0].clientY;
          const diffX = touchStartX - touchEndX;
          const diffY = Math.abs(touchStartY - touchEndY);
          const touchDuration = Date.now() - parseFloat(e.currentTarget.dataset.touchStartTime || '0');
          
          // Trigger on shorter swipe (30px) if it's a quick gesture, otherwise 50px
          const threshold = touchDuration < 300 ? 30 : 50;
          
          // Only trigger if horizontal swipe is more significant than vertical
          if (Math.abs(diffX) > threshold && Math.abs(diffX) > diffY) {
            if (diffX > 0) nextWeek();
            else prevWeek();
          }
          
          // Reset flags
          delete e.currentTarget.dataset.isHorizontalSwipe;
        }}
      >
        {/* Combined Top Bar with Week Nav */}
        <div className="flex items-center justify-between px-1.5 py-2">
          <button 
            onClick={onMenuClick}
            className="p-1.5 flex-shrink-0 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Menu size={20} strokeWidth={1.5} />
          </button>
          
          {/* Week Navigation - Inline */}
          <div 
            onClick={() => setShowDatePicker(true)}
            className="flex-1 min-w-0 overflow-hidden flex items-center justify-center gap-0.5 cursor-pointer"
            data-testid="week-date-picker"
          >
            <button 
              onClick={(e) => { e.stopPropagation(); prevWeek(); }} 
              className="p-0.5 hover:bg-white/10 rounded"
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-center px-0.5">
              <p className="text-[13px] font-semibold whitespace-nowrap">
                {weekDates[0].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - {weekDates[6].toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              </p>
            </div>
            <button 
              onClick={(e) => { e.stopPropagation(); nextWeek(); }} 
              className="p-0.5 hover:bg-white/10 rounded"
            >
              <ChevronRight size={16} />
            </button>
            
            {/* Week jobs + revenue — one compact stacked pill */}
            {!isWorker ? (
              <div className="ml-1 px-2 py-0.5 bg-emerald-500/20 rounded-lg flex flex-col items-center leading-tight whitespace-nowrap">
                <span className="text-white/80 text-[10px] font-medium">{weekStats.jobCount} jobs</span>
                <span className="text-emerald-300 font-bold text-xs">£{Math.round(weekStats.totalValue)}</span>
              </div>
            ) : (
              <div className="ml-1 px-2 py-1 bg-zinc-700/50 rounded-lg whitespace-nowrap">
                <span className="text-white font-semibold text-xs">{weekStats.jobCount} jobs</span>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {!isWorker && (
              <button
                onClick={toggleLiveMode}
                data-testid="live-mode-toggle"
                aria-label={liveMode ? 'Live Mode ON' : 'Live Mode OFF'}
                title={liveMode ? 'App is LIVE — Done sends invoices & notifications as normal. Tap to switch off.' : 'OFF — Done just records the job. No invoices, charges or customer messages. Tap to go live.'}
                className={`p-1.5 rounded-lg transition-colors ${liveMode ? 'text-emerald-400 hover:bg-white/10' : 'bg-amber-400 text-black'}`}
              >
                <Power size={18} strokeWidth={2} />
              </button>
            )}
            <LocationShareToggle />
            <button
              onClick={toggleEasyMode}
              className={`p-1.5 rounded-lg transition-colors ${easyMode ? 'bg-emerald-500 text-white' : 'hover:bg-white/10'}`}
              aria-label="Easy Mode — big buttons, no swiping"
              title="Easy Mode — big buttons, no swiping"
              data-testid="easy-mode-toggle"
            >
              <Hand size={18} strokeWidth={1.5} />
            </button>
            <button className="p-1.5 hover:bg-white/10 rounded-lg transition-colors">
              <Search size={18} strokeWidth={1.5} />
            </button>
            {!isWorker && (
              <div className="relative" data-shift-menu-anchor>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowShiftMenu(v => !v); }}
                  className="relative p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Schedule options"
                  data-testid="planner-shift-menu-btn"
                >
                  <MoreVertical size={18} strokeWidth={1.5} />
                  {optimizerCount > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-amber-400 rounded-full" data-testid="optimizer-alert-dot" />
                  )}
                </button>
                {showShiftMenu && (
                  <>
                    {/* Click-away overlay */}
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setShowShiftMenu(false)}
                    />
                    <div
                      className="absolute right-0 top-full mt-1 w-56 bg-white rounded-xl shadow-xl border border-zinc-200 overflow-hidden z-40"
                      data-testid="planner-shift-menu"
                    >
                      <button
                        onClick={() => {
                          setShiftDays(7);
                          setShowShiftModal(true);
                          setShowShiftMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-zinc-800 hover:bg-amber-50 hover:text-amber-700 transition-colors flex items-center gap-2"
                        data-testid="planner-shift-entire-btn"
                      >
                        <Calendar size={16} />
                        Shift entire schedule…
                      </button>
                      <button
                        onClick={() => {
                          setShowNotifyModal(true);
                          setShowShiftMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-zinc-800 hover:bg-indigo-50 hover:text-indigo-700 transition-colors flex items-center gap-2 border-t border-zinc-100"
                        data-testid="planner-notify-customers-btn"
                      >
                        <MessageSquare size={16} />
                        Notify customers (SMS/Email)…
                      </button>
                      <button
                        onClick={() => {
                          setShowOptimizerModal(true);
                          setShowShiftMenu(false);
                        }}
                        className="w-full px-4 py-3 text-left text-sm font-medium text-zinc-800 hover:bg-violet-50 hover:text-violet-700 transition-colors flex items-center gap-2 border-t border-zinc-100"
                        data-testid="planner-round-optimizer-btn"
                      >
                        <Sparkles size={16} />
                        Round Optimiser…
                        {optimizerCount > 0 && (
                          <span className="ml-auto text-[11px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">
                            {optimizerCount}
                          </span>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        
        {/* Compact Day Selector - Swipeable with Mouse & Touch */}
        <div 
          className="px-2 pb-3 pt-1 overflow-hidden select-none"
          data-week-container="true"
          onMouseDown={(e) => {
            if (isAnimating) return;
            const target = e.currentTarget;
            if (target?.dataset) {
              target.dataset.isDragging = 'true';
              target.dataset.startX = e.clientX.toString();
              target.dataset.wasSwiped = 'false';
            }
          }}
          onMouseMove={(e) => {
            const target = e.currentTarget;
            if (target?.dataset?.isDragging !== 'true' || isAnimating) return;
            const startX = parseFloat(target?.dataset?.startX || '0');
            const diffX = e.clientX - startX;
            
            if (Math.abs(diffX) > 10) {
              if (target?.dataset) target.dataset.wasSwiped = 'true';
              setSwipeOffset(diffX * 0.7);
            }
          }}
          onMouseUp={(e) => {
            const target = e.currentTarget;
            if (!target || target.dataset?.isDragging !== 'true') return;
            target.dataset.isDragging = 'false';
            
            const startX = parseFloat(target.dataset.startX || '0');
            const diffX = e.clientX - startX;
            
            if (Math.abs(diffX) > 60) {
              const direction = diffX > 0 ? 'right' : 'left';
              setIsAnimating(true);
              setSwipeOffset(direction === 'right' ? 400 : -400);
              
              setTimeout(() => {
                if (direction === 'left') nextWeek();
                else prevWeek();
                setSwipeOffset(0);
                setIsAnimating(false);
                if (target.dataset) target.dataset.wasSwiped = 'false';
              }, 200);
            } else {
              setSwipeOffset(0);
              setTimeout(() => {
                if (target.dataset) target.dataset.wasSwiped = 'false';
              }, 50);
            }
          }}
          onMouseLeave={(e) => {
            const target = e.currentTarget;
            if (target?.dataset?.isDragging === 'true') {
              target.dataset.isDragging = 'false';
              setSwipeOffset(0);
              target.dataset.wasSwiped = 'false';
            }
          }}
          onTouchStart={(e) => {
            if (isAnimating) return;
            const target = e.currentTarget;
            if (target?.dataset) {
              target.dataset.startX = e.touches[0].clientX.toString();
              target.dataset.startY = e.touches[0].clientY.toString();
              target.dataset.wasSwiped = 'false';
            }
          }}
          onTouchMove={(e) => {
            if (isAnimating) return;
            const target = e.currentTarget;
            const startX = parseFloat(target?.dataset?.startX || '0');
            const startY = parseFloat(target?.dataset?.startY || '0');
            const diffX = e.touches[0].clientX - startX;
            const diffY = Math.abs(e.touches[0].clientY - startY);
            
            if (Math.abs(diffX) > 10 && Math.abs(diffX) > diffY) {
              if (target?.dataset) target.dataset.wasSwiped = 'true';
              setSwipeOffset(diffX * 0.7);
            }
          }}
          onTouchEnd={(e) => {
            const target = e.currentTarget;
            const startX = parseFloat(target?.dataset?.startX || '0');
            const diffX = e.changedTouches[0].clientX - startX;
            
            if (Math.abs(diffX) > 60 && target?.dataset?.wasSwiped === 'true') {
              const direction = diffX > 0 ? 'right' : 'left';
              setIsAnimating(true);
              setSwipeOffset(direction === 'right' ? 400 : -400);
              
              setTimeout(() => {
                if (direction === 'left') nextWeek();
                else prevWeek();
                setSwipeOffset(0);
                setIsAnimating(false);
                if (target?.dataset) target.dataset.wasSwiped = 'false';
              }, 200);
            } else {
              setSwipeOffset(0);
              if (target?.dataset) {
                setTimeout(() => {
                  if (target?.dataset) target.dataset.wasSwiped = 'false';
                }, 50);
              }
            }
          }}
        >
          {/* Weather Row - Above Day Tiles */}
          {weatherForecast.length > 0 && (
            <div 
              className="flex gap-1.5 mb-1.5"
              style={{
                transform: `translateX(${swipeOffset}px)`,
                transition: isAnimating ? 'transform 0.2s ease-out' : 'none'
              }}
            >
              {weekDates.map((date, index) => {
                const dateStr = toLocalYMD(date);
                const weather = weatherForecast.find(w => w.date === dateStr);
                
                if (!weather) return <div key={index} className="flex-1 min-w-[44px] h-8" />;
                
                return (
                  <div 
                    key={index}
                    className={`flex-1 min-w-[44px] h-8 flex items-center justify-center gap-1 rounded-lg text-sm ${
                      weather.is_rainy ? 'bg-blue-500/20 text-blue-300' : 'bg-amber-500/10 text-amber-300'
                    }`}
                    title={weather.description}
                  >
                    <span className="text-lg">{weather.emoji}</span>
                    {weather.temp !== null && (
                      <span className="font-medium text-xs">{weather.temp}°</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Day Selector Tiles - Swipeable */}
          <div 
            className="flex gap-1.5"
            style={{
              transform: `translateX(${swipeOffset}px)`,
              transition: isAnimating ? 'transform 0.2s ease-out' : 'none'
            }}
          >
            {plannerDisplay !== 'classic' ? (
              <ChartWeekStrip
                weekDates={weekDates}
                jobsPerDay={jobsPerDay}
                dayProgressStats={dayProgressStats}
                selectedDayIndex={selectedDayIndex}
                bankHolidays={bankHolidays}
                dayNotes={dayNotes}
                barColor={plannerBarColor}
                onDayTap={(index, date) => {
                  if (!isWorker && selectedJobs.size > 0 && index !== selectedDayIndex) {
                    setReplanTargetDay(date);
                    setShowReplanConfirm(true);
                  } else {
                    setSelectedDayIndex(index);
                  }
                }}
              />
            ) : weekDates.map((date, index) => {
              const isSelected = index === selectedDayIndex;
              const dayJobCount = jobsPerDay[index] || 0;
              const isReplanTarget = selectedJobs.size > 0 && index !== selectedDayIndex;
              const stats = dayProgressStats[index];
              
              // Determine if day is fully complete (all jobs done or skipped)
              const isFullyComplete = stats.total > 0 && stats.pending === 0 && stats.overdue === 0;
              const hasOverdue = stats.overdue > 0;
              
              // Calculate the fill heights - red at bottom (overdue), purple above (skipped), green above (completed)
              const overdueHeight = stats.overduePercent || 0;
              const skippedHeight = stats.skippedPercent;
              const completedHeight = stats.completedPercent;
              
              // Check for bank holiday
              const dateStr = toLocalYMD(date);
              const bankHoliday = bankHolidays.find(h => h.date === dateStr);
              const isBankHoliday = !!bankHoliday;
              
              // Check for worker leave on this day
              const dayLeave = workerLeave[dateStr] || [];
              const hasLeave = dayLeave.length > 0;
              
              // Check for notes on this day
              const notesForDay = dayNotes[dateStr] || [];
              const hasNotes = notesForDay.length > 0;
              
              // Handle tap vs swipe - only fire click if it wasn't a swipe
              const handleDayTap = (e) => {
                const container = e.currentTarget.closest('[data-week-container]');
                const wasSwiped = container?.dataset?.wasSwiped === 'true';
                if (wasSwiped) return;
                
                if (!isWorker && selectedJobs.size > 0 && index !== selectedDayIndex) {
                  setReplanTargetDay(date);
                  setShowReplanConfirm(true);
                } else {
                  setSelectedDayIndex(index);
                }
              };
              
              return (
                <div
                  key={index}
                  onClick={handleDayTap}
                  className={`flex-1 min-w-[44px] rounded-xl transition-all duration-200 overflow-hidden relative cursor-pointer select-none ${
                    isBankHoliday
                      ? 'ring-2 ring-red-400 ring-offset-1 ring-offset-zinc-900 bg-red-900/30'
                      : isSelected 
                      ? 'ring-2 ring-offset-1 ring-offset-zinc-900 bg-zinc-800/50' 
                      : isReplanTarget && !isWorker
                      ? 'bg-purple-500/30 border border-purple-400'
                      : 'bg-zinc-800/50 hover:bg-zinc-700/50'
                  }`}
                  style={{ 
                    height: '80px',
                    ...(isSelected && !isBankHoliday ? { '--tw-ring-color': 'var(--theme-accent)' } : {})
                  }}
                  title={isBankHoliday ? bankHoliday.title : hasLeave ? `${dayLeave.map(l => l.worker_name).join(', ')} on leave` : ''}
                >
                  {/* Bank Holiday indicator */}
                  {isBankHoliday && (
                    <div className="absolute top-0 left-0 right-0 bg-red-500 text-white text-[8px] font-bold text-center py-0.5 z-20">
                      HOLIDAY
                    </div>
                  )}
                  
                  {/* Worker Leave indicator */}
                  {hasLeave && !isBankHoliday && (
                    <div className="absolute top-0 left-0 right-0 bg-orange-500 text-white text-[8px] font-bold text-center py-0.5 z-20 truncate px-1">
                      {dayLeave.length === 1 ? dayLeave[0].worker_name.split(' ')[0] : `${dayLeave.length} off`}
                    </div>
                  )}
                  
                  {/* Day Notes indicator */}
                  {hasNotes && (
                    <div className="absolute top-1 right-1 z-30">
                      <div className="w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center shadow-sm" title={`${notesForDay.length} note${notesForDay.length > 1 ? 's' : ''}`}>
                        <StickyNote size={10} className="text-yellow-900" />
                      </div>
                    </div>
                  )}
                  
                  {/* Progress bar - fills from bottom */}
                  <div className="absolute inset-0 flex flex-col justify-end pointer-events-none">
                    {/* Green (completed) - on top. Colour follows the user's bar-colour setting. */}
                    {completedHeight > 0 && (
                      <div 
                        className="w-full transition-all duration-500 ease-out"
                        style={{ height: `${completedHeight}%`, backgroundColor: plannerBarColor }}
                      />
                    )}
                    {/* Yellow (skipped) - middle */}
                    {skippedHeight > 0 && (
                      <div 
                        className="w-full bg-yellow-400 transition-all duration-500 ease-out"
                        style={{ height: `${skippedHeight}%` }}
                      />
                    )}
                    {/* Red (overdue) - at the very bottom */}
                    {overdueHeight > 0 && (
                      <div 
                        className="w-full bg-red-500 transition-all duration-500 ease-out"
                        style={{ height: `${overdueHeight}%` }}
                      />
                    )}
                  </div>
                  
                  {/* Content overlay */}
                  <div className={`relative z-10 h-full flex flex-col items-center justify-center ${isBankHoliday || hasLeave ? 'pt-4' : ''} py-2`}>
                    <div className={`text-[10px] font-semibold tracking-wide ${
                      isBankHoliday ? 'text-red-300' :
                      isSelected ? 'text-emerald-300' : 'text-white/50'
                    }`}>
                      {['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'][index]}
                    </div>
                    <div className={`text-xl font-bold leading-tight ${
                      isBankHoliday ? 'text-red-200' :
                      isSelected ? 'text-white' : 'text-white/90'
                    }`}>
                      {date.getDate()}
                    </div>
                    <div className={`text-[11px] font-semibold ${
                      hasOverdue ? 'text-red-400' :
                      isFullyComplete ? 'text-emerald-300' : 
                      isSelected ? 'text-white/80' : 'text-white/50'
                    }`}>
                      {dayJobCount > 0 ? (
                        hasOverdue ? `${stats.overdue} overdue` :
                        isFullyComplete ? '✓ Done' : `${stats.completed}/${stats.total - stats.skipped}`
                      ) : '—'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        
        {/* Replan mode banner - HIDDEN FOR WORKERS */}
        {selectedJobs.size > 0 && !isWorker && (
          <div className="bg-purple-600 text-white px-3 py-2 text-center text-sm">
            <p className="font-medium">Tap a day to move {selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''}</p>
          </div>
        )}
      </div>
      
      {/* Combined Filter & Stats Bar - OR Bulk Action Bar when jobs selected */}
      <div className="bg-white border-b border-zinc-200 shadow-sm">
        {/* Bulk Action Bar - shows when jobs are selected */}
        {selectedJobs.size > 0 && !isWorker ? (
          <div className="bg-zinc-900 px-3 py-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white font-semibold">{selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''} selected</span>
              <div className="flex items-center gap-2">
                <span className="text-emerald-400 font-semibold">
                  £{numberedDayJobs.filter(j => selectedJobs.has(j.id)).reduce((sum, j) => sum + (j.price || 0), 0).toFixed(2)}
                </span>
                <button 
                  onClick={() => setSelectedJobs(new Set())}
                  className="text-white/70 hover:text-white text-sm px-2 py-1 bg-zinc-700 rounded"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="flex gap-2 overflow-x-auto no-scrollbar">
              {/* Check if any selected jobs are completed - show Undo Complete */}
              {numberedDayJobs.filter(j => selectedJobs.has(j.id)).some(j => j.status === 'done' || j.status === 'completed') && (
                <button 
                  onClick={async () => {
                    const completedJobs = numberedDayJobs.filter(j => 
                      selectedJobs.has(j.id) && (j.status === 'done' || j.status === 'completed')
                    );
                    for (const job of completedJobs) {
                      try {
                        await api.updateJob(job.id, { status: 'scheduled' });
                      } catch (err) {
                        console.error('Failed to undo complete:', err);
                      }
                    }
                    toast.success(`Undo complete for ${completedJobs.length} jobs`);
                    setSelectedJobs(new Set());
                    fetchWeekJobs();
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-xl text-sm font-medium whitespace-nowrap"
                >
                  <RotateCcw size={16} />
                  Undo Complete
                </button>
              )}
              {/* Mark Done - only for pending jobs */}
              {numberedDayJobs.filter(j => selectedJobs.has(j.id)).some(j => !j.status || j.status === 'scheduled') && (
                <button 
                  onClick={handleBulkMarkDone}
                  className="flex items-center gap-2 px-3 py-2 bg-emerald-500 text-white rounded-xl text-sm font-medium whitespace-nowrap"
                >
                  <Check size={16} />
                  Done
                </button>
              )}
              {/* Skip - only for pending jobs */}
              {numberedDayJobs.filter(j => selectedJobs.has(j.id)).some(j => !j.status || j.status === 'scheduled') && (
                <button 
                  onClick={handleBulkSkip}
                  className="flex items-center gap-2 px-3 py-2 bg-purple-500 text-white rounded-xl text-sm font-medium whitespace-nowrap"
                >
                  <SkipForward size={16} />
                  Skip
                </button>
              )}
              <button 
                onClick={() => {
                  // Use `numberedDayJobs` — the day's visible jobs in this
                  // scope. (`transformedJobs` only exists inside the async
                  // fetcher and was a silent ReferenceError here.)
                  const selectedJobsList = numberedDayJobs.filter(j => selectedJobs.has(j.id));
                  const recipients = selectedJobsList.map(j => ({
                    id: j.customerRef,
                    customer_id: j.customerRef,
                    name: j.customerName || j.customer_name,
                    phone: j.customerPhone
                  })).filter(r => r.phone);
                  
                  if (recipients.length === 0) {
                    toast.error('No phone numbers available');
                    return;
                  }
                  setSmsRecipients(recipients);
                  setShowSMSComposeModal(true);
                }}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium whitespace-nowrap"
              >
                <MessageSquare size={16} />
                SMS
              </button>
              <button 
                onClick={() => setShowReassignWorkerModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-700 text-white rounded-xl text-sm font-medium whitespace-nowrap"
              >
                <Users size={16} />
                Reassign
              </button>
              <button 
                onClick={() => setShowReassignRoundModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-zinc-700 text-white rounded-xl text-sm font-medium whitespace-nowrap"
              >
                <MapPin size={16} />
                Round
              </button>
            </div>
          </div>
        ) : (
          /* Normal Filter Bar */
          <div className="px-2 py-1.5 flex items-center justify-between gap-2">
            {/* Assignee Filter - Compact inline - HIDDEN FOR WORKERS */}
            {!isWorker && (
              <div className="flex gap-1 overflow-x-auto no-scrollbar flex-shrink-0">
                {[
                  { id: 'all', name: 'All', color: '#3b82f6' },
                  ...staffList.map((name, idx) => ({
                    id: name.toLowerCase().replace(/\s+/g, '_'),
                    name: name, // Always show worker's actual name
                    color: ['#10b981', '#84cc16', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'][idx % 6]
                  })),
                  { id: 'unassigned', name: 'Unassigned', color: '#94a3b8' },
                ].map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setSelectedAssignee(a.id)}
                    data-testid={`assignee-filter-${a.id}`}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all text-xs font-medium whitespace-nowrap ${
                      selectedAssignee === a.id 
                        ? 'bg-zinc-900 text-white' 
                        : 'bg-zinc-100 text-zinc-600'
                    }`}
                  >
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: a.color }} />
                    {a.name}
                  </button>
                ))}
                {selectedAssignee === 'unassigned' && dayJobs.length > 0 && (
                  <button
                    onClick={() => { setBulkAssignWorker(null); setShowBulkAssignModal(true); }}
                    data-testid="bulk-assign-btn"
                    className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-xs font-semibold whitespace-nowrap hover:bg-emerald-700"
                  >
                    <UserPlus size={12} />
                    Assign all {dayJobs.length}
                  </button>
                )}
              </div>
            )}
            
            {/* Stats & Remind button */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {(!isWorker || workerPermissions.can_send_reminders) && numberedDayJobs.length > 0 && (
                <button
                  onClick={() => setShowReminderMenu(true)}
                  className="flex items-center justify-center w-10 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-xl transition-colors relative"
                  data-testid="reminder-menu-btn"
                >
                  <span className="absolute inset-0 rounded-xl bg-blue-400 animate-ping opacity-30"></span>
                  <Bell size={20} className="relative z-10" />
                </button>
              )}
              
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-green-100 rounded-full" title="Auto-updates every 30s">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                  <span className="text-[10px] font-medium text-green-700">LIVE</span>
                </div>
                <span className="text-xs font-semibold text-zinc-700">{allDayStats.total} jobs</span>
                {(!isWorker || workerPermissions.can_see_prices) && (
                  <span className="text-xs font-semibold text-emerald-600">£{allDayStats.totalValue.toFixed(2)}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Show Map - Google Map of jobs for the selected day */}
      <DayMapPanel
        jobs={numberedDayJobs}
        isWorker={isWorker}
        getStaffColor={getStaffColor}
        dayLabel={weekDates[selectedDayIndex]?.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
        dataTestId="work-planner-day-map"
      />

      {/* Jobs List */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto pt-4 pb-24">
        {/* Go Live checklist modal */}
        {showGoLiveModal && (
          <div className="fixed inset-0 z-[300] bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center" data-testid="go-live-modal">
            <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 max-h-[85vh] overflow-y-auto">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Power size={20} className="text-emerald-600" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-zinc-900">Ready to go live?</h3>
                  <p className="text-xs text-zinc-500">Once live, completing jobs invoices, charges and messages customers.</p>
                </div>
              </div>
              {!goLiveChecklist ? (
                <p className="text-sm text-zinc-500 py-6 text-center">Checking your setup…</p>
              ) : (
                <div className="mt-4 space-y-2" data-testid="go-live-checklist">
                  {[
                    {
                      ok: (goLiveChecklist.customers_count || 0) > 0,
                      label: `Customers added (${goLiveChecklist.customers_count || 0})`,
                      tip: 'Import your round or add customers first'
                    },
                    {
                      ok: (goLiveChecklist.upcoming_jobs_count || 0) > 0,
                      label: `Jobs planned (${goLiveChecklist.upcoming_jobs_count || 0} upcoming)`,
                      tip: 'Add recurring jobs so your planner fills itself'
                    },
                    {
                      ok: !!(goLiveChecklist.stripe_connected || goLiveChecklist.gocardless_connected),
                      label: goLiveChecklist.stripe_connected && goLiveChecklist.gocardless_connected
                        ? 'Payments connected (Stripe + GoCardless)'
                        : goLiveChecklist.stripe_connected ? 'Payments connected (Stripe)'
                        : goLiveChecklist.gocardless_connected ? 'Payments connected (GoCardless)'
                        : 'Payments not connected',
                      tip: 'Connect Stripe or GoCardless in Connected Apps so invoices include a pay link'
                    },
                    {
                      ok: !!goLiveChecklist.business_name_set,
                      label: 'Business name set for invoices & emails',
                      tip: 'Add it in Settings so customer emails look professional'
                    },
                    {
                      ok: !!goLiveChecklist.email_domain_verified,
                      label: goLiveChecklist.email_domain_verified ? 'Email domain verified — emails send from your address' : 'Email domain not verified',
                      tip: 'Optional — verify your domain in Settings → Messaging so emails come from your address (Gmail users are covered automatically)'
                    },
                    {
                      ok: !!goLiveChecklist.logo_uploaded,
                      label: 'Logo uploaded for branded emails',
                      tip: 'Optional — add your logo in Settings for branded invoices'
                    },
                  ].map((item, i) => (
                    <div key={i} className={`flex items-start gap-2.5 p-2.5 rounded-xl border ${item.ok ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${item.ok ? 'bg-emerald-500' : 'bg-amber-400'}`}>
                        {item.ok ? <Check size={12} className="text-white" strokeWidth={3} /> : <AlertTriangle size={11} className="text-black" />}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-semibold ${item.ok ? 'text-emerald-900' : 'text-amber-900'}`}>{item.label}</p>
                        {!item.ok && <p className="text-xs text-amber-700 mt-0.5">{item.tip}</p>}
                      </div>
                    </div>
                  ))}
                  <p className="text-[11px] text-zinc-400 pt-1">Nothing done while you were in practice mode will be sent or charged — going live only affects jobs you complete from now on.</p>
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setShowGoLiveModal(false)}
                  className="flex-1 py-3 rounded-xl border border-zinc-200 text-sm font-bold text-zinc-600"
                  data-testid="go-live-cancel"
                >
                  Not yet
                </button>
                <button
                  onClick={() => { setShowGoLiveModal(false); applyLiveMode(true); }}
                  className="flex-1 py-3 rounded-xl bg-emerald-500 text-white text-sm font-bold"
                  data-testid="go-live-confirm"
                >
                  Go LIVE
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Live Mode OFF banner — completions are silent record-keeping */}
        {!isWorker && userSettings?.live_mode === false && !userSettings?.handover_mode && (
          <div
            className="mx-4 mb-4 bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 flex items-start gap-3"
            data-testid="live-mode-off-banner"
          >
            <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} className="text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">Live Mode is OFF</p>
              <p className="text-xs text-amber-800 mt-0.5 leading-snug">
                Marking jobs Done just records them — no invoices, no card charges, no customer
                messages. Tap the amber power button above to go live again.
              </p>
            </div>
          </div>
        )}
        {/* Handover Mode Banner — visible whenever Settings → handover_mode is ON */}
        {userSettings?.handover_mode && (
          <div
            className="mx-4 mb-4 bg-amber-50 border-2 border-amber-300 rounded-xl px-4 py-3 flex items-start gap-3"
            data-testid="handover-mode-banner"
          >
            <div className="w-9 h-9 rounded-full bg-amber-200 flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} className="text-amber-800" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">
                Handover Mode is ON
              </p>
              <p className="text-xs text-amber-800 mt-0.5 leading-snug">
                Marking jobs Done will silently roll them forward — no invoices, no charges,
                no SMS / Email, no Owings updates.{' '}
                <a
                  href="/settings"
                  className="underline font-medium hover:text-amber-900"
                  data-testid="handover-mode-banner-settings-link"
                >
                  Turn off in Settings
                </a>
              </p>
            </div>
          </div>
        )}

        {/* Day Notes Banner */}
        {(() => {
          const currentDateStr = toLocalYMD(weekDates[selectedDayIndex]);
          const notesForToday = dayNotes[currentDateStr] || [];
          if (notesForToday.length === 0) return null;
          
          return (
            <div 
              className="mx-4 mb-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl overflow-hidden cursor-pointer hover:bg-yellow-100 transition-colors"
              onClick={() => setShowDayNoteModal(true)}
              data-testid="day-notes-banner"
            >
              <div className="bg-yellow-400 px-4 py-2 flex items-center gap-2">
                <StickyNote size={18} className="text-yellow-900" />
                <span className="font-bold text-yellow-900">Day Notes</span>
                <span className="text-yellow-800 text-sm">({notesForToday.length})</span>
                <span className="ml-auto text-yellow-800 text-xs">Tap to edit</span>
              </div>
              <div className="p-4 space-y-2">
                {notesForToday.map((note, idx) => (
                  <div key={note.id || idx} className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-yellow-500 mt-2 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-zinc-800 font-medium">{note.note}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        {note.created_by_name || 'Team'} • {new Date(note.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-8">
            <Loader2 size={40} className="text-emerald-500 animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Loading jobs...</h3>
            <p className="text-zinc-500">Fetching schedule from server</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-8">
            <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
              <XCircle size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">Failed to load jobs</h3>
            <p className="text-zinc-500 mb-4">{error}</p>
            <button 
              onClick={() => fetchWeekJobs(false)}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : numberedDayJobs.length > 0 ? (
          <>
            {plannerDisplay !== 'classic' ? (
              <SlimProgressStrip
                jobs={numberedDayJobs}
                hidePrice={isWorker}
                isWorker={isWorker}
                selectedCount={selectedJobs.size}
                overdueCount={numberedDayJobs.filter(j => (!j.status || j.status === 'scheduled') && isJobOverdue(j)).length}
                barColor={plannerBarColor}
                onSelectAll={() => {
                  if (selectedJobs.size === numberedDayJobs.length && numberedDayJobs.length > 0) {
                    setSelectedJobs(new Set());
                  } else {
                    setSelectedJobs(new Set(numberedDayJobs.map(j => j.id)));
                  }
                }}
              />
            ) : (
            <JobsSummary 
              jobs={numberedDayJobs}
              title="Today's Jobs"
              icon={CheckCircle2}
              hidePrice={isWorker}
              isWorker={isWorker}
              selectedCount={selectedJobs.size}
              onSelectAll={() => {
                if (selectedJobs.size === numberedDayJobs.length && numberedDayJobs.length > 0) {
                  setSelectedJobs(new Set());
                } else {
                  setSelectedJobs(new Set(numberedDayJobs.map(j => j.id)));
                }
              }}
            />
            )}
            
            {/* Round Badges - show which rounds are scheduled for this day. Tap to filter. */}
            {(() => {
              // Compute counts from unfiltered day jobs so all rounds remain visible
              // even when a filter is active.
              //
              // The literal sentinel string "Unassigned" means "no round set" — we
              // keep it as a filter chip so the owner can quickly find jobs that
              // need a round assigned, but we relabel it "No round" so it doesn't
              // get visually confused with "no worker assigned" (a separate concept).
              const SENTINEL = 'unassigned';
              const allDayRounds = [...new Set(dayJobs.map(j => j.zone || j.round).filter(Boolean))];
              if (allDayRounds.length === 0) return null;

              const labelFor = (round) =>
                String(round).trim().toLowerCase() === SENTINEL ? 'No round' : round;

              const roundCounts = allDayRounds.map(round => ({
                name: round,
                label: labelFor(round),
                count: dayJobs.filter(j => (j.zone || j.round) === round).length,
                isSentinel: String(round).trim().toLowerCase() === SENTINEL,
              })).sort((a, b) => {
                // Push the sentinel "No round" chip to the end of the row so it
                // doesn't crowd the real rounds.
                if (a.isSentinel !== b.isSentinel) return a.isSentinel ? 1 : -1;
                return a.label.localeCompare(b.label);
              });
              
              return (
                <div className="mx-3 mb-3" data-testid="round-filter-bar">
                  <div className="flex flex-wrap gap-1.5">
                    {roundCounts.map(({ name, label, count, isSentinel }) => {
                      const color = isSentinel ? '#94a3b8' : getZoneColor(name);
                      const isActive = roundFilter === name;
                      return (
                        <button
                          key={name}
                          type="button"
                          onClick={() => setRoundFilter(prev => prev === name ? null : name)}
                          data-testid={`round-filter-${name}`}
                          aria-pressed={isActive}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border transition-all active:scale-95"
                          style={isActive ? {
                            backgroundColor: color,
                            color: '#fff',
                            borderColor: color,
                            boxShadow: `0 1px 4px ${color}66`
                          } : {
                            backgroundColor: `${color}1a`,
                            color: color,
                            borderColor: `${color}40`
                          }}
                        >
                          <span className="truncate max-w-[8rem]">{label}</span>
                          <span
                            className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full leading-none"
                            style={isActive
                              ? { backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' }
                              : { backgroundColor: `${color}33`, color }}
                          >
                            {count}
                          </span>
                        </button>
                      );
                    })}
                    {roundFilter && (
                      <button
                        type="button"
                        onClick={() => setRoundFilter(null)}
                        data-testid="clear-round-filter"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border border-zinc-300 bg-white text-zinc-600 hover:bg-zinc-50 active:scale-95 transition-all"
                      >
                        <X size={12} />
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}
            
            {/* Route controls - direction toggle and custom order indicator */}
            <div className="mx-4 mb-2 flex items-center justify-between">
              {/* Route direction toggle. Tapping this clears any saved
                  drag-to-reorder for the current day, so the API's new
                  optimisation actually shows up — otherwise customJobOrder
                  silently overrides the toggle and the user just sees a
                  label change with no reorder. */}
              <button
                onClick={() => {
                  if (hasCustomOrder) {
                    // Clear silently — toast would be misleading mid-toggle
                    setCustomJobOrder(prev => {
                      const next = { ...prev };
                      delete next[currentDateKey];
                      return next;
                    });
                    // Best-effort backend cleanup
                    try {
                      const token = localStorage.getItem('streakfree_token');
                      fetch(`${API_URL}/api/jobs/order/${currentDateKey}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${token}` },
                      }).catch(() => {});
                    } catch (_) { /* ignore */ }
                  }
                  setRouteDirection(prev => prev === 'forward' ? 'reverse' : 'forward');
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-zinc-600 transition-colors text-sm font-medium"
                data-testid="route-direction-toggle"
              >
                {routeDirection === 'forward' ? (
                  <ArrowDown size={14} className="text-emerald-500" />
                ) : (
                  <ArrowUp size={14} className="text-blue-500" />
                )}
                <span>{routeDirection === 'forward' ? 'Nearest first' : 'Furthest first'}</span>
              </button>
              
              {/* Custom order indicator and reset */}
              {hasCustomOrder && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-blue-600 font-medium flex items-center gap-1">
                    <GripVertical size={12} />
                    Custom order
                  </span>
                  <button
                    onClick={clearCustomOrder}
                    className="text-xs text-blue-500 hover:text-blue-700 underline"
                    data-testid="reset-order-btn"
                  >
                    Reset
                  </button>
                </div>
              )}
            </div>
            
            {/* Drag hint - only show when no custom order */}
            {!hasCustomOrder && numberedDayJobs.length > 1 && (
              <div className="mx-4 mb-2 flex items-center gap-2 text-xs text-zinc-400">
                <GripVertical size={14} />
                <span>Drag jobs to reorder your route</span>
              </div>
            )}
            
            {/* Sortable Job List */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={numberedDayJobs.map(j => j.id)}
                strategy={verticalListSortingStrategy}
              >
                {numberedDayJobs.map((job, idx) => (
                  <SortableJobCard key={job.id} job={job} isDragDisabled={easyMode}>
                    {plannerDisplay === 'compact' ? (
                    <CompactJobRow
                      job={job}
                      index={idx}
                      currentStatus={jobStatuses[job.id]}
                      onClick={() => setSelectedJob(job)}
                      onComplete={handleCompleteJob}
                      canSeePrice={!isWorker || workerPermissions.can_see_prices}
                      getZoneColor={getZoneColor}
                      isOverdue={isJobOverdue(job)}
                    />
                    ) : (
                    <SwipeableJobCard 
                      job={job}
                      index={idx}
                      easyMode={easyMode}
                      currentStatus={jobStatuses[job.id]}
                      onClick={() => setSelectedJob(job)}
                      isSelected={selectedJobs.has(job.id)}
                      replannedDate={replannedJobs[job.id]}
                      onToggleSelect={() => {
                        setSelectedJobs(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(job.id)) {
                            newSet.delete(job.id);
                          } else {
                            newSet.add(job.id);
                          }
                          return newSet;
                        });
                      }}
                      onComplete={handleCompleteJob}
                      onDoneAndPaid={async (job, options = {}) => {
                        try {
                          const token = localStorage.getItem('streakfree_token');
                          const response = await fetch(`${API_URL}/api/jobs/${job.id}/complete`, {
                            method: 'POST',
                            headers: { 
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({ 
                              mark_paid: true,
                              payment_method: options.paymentMethod || 'cash',
                              send_notification: false,
                              occurrence_date: job.scheduled_date || null,
                            })
                          });
                          if (response.ok) {
                            setJobStatuses(prev => ({
                              ...prev,
                              [job.id]: {
                                status: 'done',
                                invoice_paid: true
                              }
                            }));
                          }
                        } catch (err) {
                          console.error('Failed to complete job:', err);
                        }
                      }}
                      onSkip={handleSkipJob}
                      onUndoComplete={handleUndoComplete}
                      onUndoSkip={handleUndoSkip}
                      isWorker={isWorker}
                      workerPermissions={workerPermissions}
                      isOverdue={isJobOverdue(job)}
                      userSettings={userSettings}
                      onNavigate={openNavigation}
                      onConfirmPinLocation={handleConfirmPinLocation}
                    />
                    )}
                  </SortableJobCard>
                ))}
              </SortableContext>
            </DndContext>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-64 text-center px-8">
            <div className="w-16 h-16 rounded-2xl bg-zinc-200 flex items-center justify-center mb-4">
              <Calendar size={28} className="text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No jobs scheduled</h3>
            <p className="text-zinc-500">This day is clear. Tap + to add a job.</p>
          </div>
        )}
      </div>
      
      {/* Selection Action Bar - appears when jobs are selected */}
      
      {/* Modern FAB with Menu - Fixed at bottom LEFT of main content area - Hidden for workers */}
      {/* Fades out when user scrolls to bottom to avoid blocking content, fades back in when scrolling up */}
      {!isWorker && (
        <div 
          className={`fixed bottom-6 left-6 lg:left-[280px] z-40 transition-all duration-300 ${
            fabVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
        >
          {/* FAB Menu Items */}
          {fabOpen && (
            <div className="absolute bottom-16 left-0 flex flex-col gap-3 mb-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
              <button 
                onClick={() => {
                  setFabOpen(false);
                  // Use the same global event pattern the Header "+ New" menu
                  // uses — CustomersEnhanced listens for this and opens the New
                  // Customer form (and navigates to that page first).
                  sessionStorage.setItem('openCreateForm', 'true');
                  window.dispatchEvent(new CustomEvent('streakfree:open-create-form', { detail: { action: 'customers' } }));
                }}
                className="flex items-center gap-3 bg-white rounded-full shadow-lg pl-4 pr-5 py-2.5 hover:shadow-xl transition-all"
                data-testid="fab-add-customer"
              >
                <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center">
                  <UserPlus size={20} className="text-white" />
                </div>
                <span className="font-medium text-zinc-700 whitespace-nowrap">Add Customer</span>
              </button>
              
              <button 
                onClick={() => {
                  setFabOpen(false);
                  setShowNewJobModal(true);
                }}
                className="flex items-center gap-3 bg-white rounded-full shadow-lg pl-4 pr-5 py-2.5 hover:shadow-xl transition-all"
                data-testid="fab-add-job"
              >
                <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                  <Briefcase size={20} className="text-white" />
                </div>
                <span className="font-medium text-zinc-700 whitespace-nowrap">Add Job</span>
              </button>
              
              <button 
                onClick={() => {
                  setFabOpen(false);
                  // Settings → Services tab: dispatch an event the App-level
                  // navigator listens for, OR fall back to direct nav.
                  window.dispatchEvent(new CustomEvent('streakfree:navigate', { detail: { page: 'services' } }));
                }}
                className="flex items-center gap-3 bg-white rounded-full shadow-lg pl-4 pr-5 py-2.5 hover:shadow-xl transition-all"
                data-testid="fab-add-service"
              >
                <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center">
                  <FileText size={20} className="text-white" />
                </div>
                <span className="font-medium text-zinc-700 whitespace-nowrap">Manage Services</span>
              </button>
              
              <button 
                onClick={() => {
                  setFabOpen(false);
                  setShowDayNoteModal(true);
                }}
                className="flex items-center gap-3 bg-white rounded-full shadow-lg pl-4 pr-5 py-2.5 hover:shadow-xl transition-all"
                data-testid="fab-add-note"
              >
                <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                  <StickyNote size={20} className="text-white" />
                </div>
                <span className="font-medium text-zinc-700 whitespace-nowrap">Add Note to Day</span>
              </button>
            </div>
          )}
          
          {/* FAB Button */}
          <button 
            onClick={() => setFabOpen(!fabOpen)}
            className={`w-14 h-14 rounded-2xl shadow-lg flex items-center justify-center transition-all duration-200 ${
              fabOpen ? 'bg-zinc-800 rotate-45' : 'hover:opacity-90'
            }`}
            style={!fabOpen ? { backgroundColor: 'var(--theme-accent)', boxShadow: '0 10px 15px -3px var(--theme-accent)33' } : {}}
            data-testid="work-planner-fab"
          >
            <Plus size={28} strokeWidth={2} className="text-white" />
          </button>
        </div>
      )}
      
      {/* Job Detail Panel - Slides in from right */}
      <JobDetailPanel
        job={selectedJob}
        isOpen={!!selectedJob && !selectedCustomerFromJob && !showInvoiceModal}
        onClose={() => setSelectedJob(null)}
        onComplete={async (job, options = {}) => {
          // Unified handler: routes through the same handleCompleteJob the
          // WorkPlanner inline list uses, so JobDetailPanel and the list cards
          // behave identically for charge confirmation, charge_amount, and
          // notification routing.
          try {
            await handleCompleteJob(job, {
              sendMethod: options.sendMethod ?? 'none',
              hasPaymentMethod: options.hasPaymentMethod ?? false,
              paymentType: options.paymentType ?? null,
              chargeAmount: options.chargeAmount ?? null,
              skipAutoCharge: options.skipAutoCharge ?? false,
            });
            fetchWeekJobs();
            setSelectedJob(null);
          } catch (err) {
            console.error('Failed to complete job:', err);
          }
        }}
        onDoneAndPaid={async (job, options = {}) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            const response = await fetch(`${API_URL}/api/jobs/${job.id}/complete`, {
              method: 'POST',
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ 
                mark_paid: true,
                payment_method: options.paymentMethod || 'cash',
                send_notification: false,  // Don't send invoice notification for paid jobs
                occurrence_date: job.scheduled_date || null,
              })
            });
            if (response.ok) {
              fetchWeekJobs();
              setSelectedJob(null);
              const methodDisplay = options.paymentMethod ? options.paymentMethod.replace('_', ' ') : 'cash';
              showNotification(`Job completed and marked as paid (${methodDisplay})`, 'success');
            }
          } catch (err) {
            console.error('Error completing job:', err);
          }
        }}
        onReschedule={(job) => {
          // Open date picker for reschedule
          showNotification('Reschedule feature - use the calendar to pick a new date', 'info');
        }}
        onSkip={async (job, reason) => {
          try {
            const token = localStorage.getItem('streakfree_token');
            const params = new URLSearchParams({ reason: reason || 'Skipped' });
            if (job.scheduled_date) params.set('occurrence_date', job.scheduled_date);
            const response = await fetch(`${API_URL}/api/jobs/${job.id}/skip?${params.toString()}`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              fetchWeekJobs();
              setSelectedJob(null);
              showNotification('Job skipped', 'success');
            }
          } catch (err) {
            console.error('Error skipping job:', err);
          }
        }}
        onNavigate={handleNavigate}
        onViewCustomer={(customerId) => {
          if (customerId) {
            setSelectedJob(null);
            navigate(`/customers/${customerId}`);
          }
        }}
        onSendMessage={(job) => {
          showNotification('Message feature coming soon', 'info');
        }}
        onUndoComplete={handleUndoCompleteJob}
        onViewInvoice={handleViewInvoice}
        onUpdateJob={handleUpdateJob}
        getStaffColor={getStaffColor}
        staffList={staffList}
        roundsList={roundsList}
        isWorker={isWorker}
        workerPermissions={workerPermissions}
        userSettings={userSettings}
      />
      
      {/* Invoice Summary Modal */}
      {showInvoiceModal && selectedInvoice && (
        <InvoiceSummaryModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}
          onSendInvoice={async (invoice, method) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              const response = await fetch(`${API_URL}/api/invoices/${invoice.id}/send`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ method })
              });
              
              if (response.ok) {
                showNotification(`Invoice sent via ${method}`, 'success');
                // Refresh the invoice data
                setSelectedInvoice(prev => ({
                  ...prev,
                  [`${method}_sent`]: true,
                  [`${method}_sent_at`]: new Date().toISOString()
                }));
              } else {
                const err = await response.json();
                showNotification(err.detail || `Failed to send ${method}`, 'error');
              }
            } catch (err) {
              console.error(`Error sending invoice via ${method}:`, err);
              showNotification(`Failed to send invoice via ${method}`, 'error');
            }
          }}
          onCancel={async (invoice) => {
            try {
              const token = localStorage.getItem('streakfree_token');
              await fetch(`${API_URL}/api/invoices/${invoice.id}/cancel`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              fetchWeekJobs();
              setShowInvoiceModal(false);
              setSelectedInvoice(null);
              showNotification('Invoice cancelled', 'success');
            } catch (err) {
              console.error('Error cancelling invoice:', err);
            }
          }}
        />
      )}
      
      {/* Customer Detail Modal - Opens from Job Card */}
      {selectedCustomerFromJob && (
        <CustomerDetailModal 
          customer={selectedCustomerFromJob}
          onClose={() => setSelectedCustomerFromJob(null)} 
        />
      )}
      
      {/* SMS Reminders Modal */}
      {showSmsReminders && (
        <SmsRemindersModal 
          jobs={numberedDayJobs} 
          onClose={() => setShowSmsReminders(false)} 
        />
      )}
      
      {/* Actions Modal */}
      {showActions && (
        <JobActionsModal 
          selectedCount={selectedJobs.size}
          totalValue={numberedDayJobs.filter(j => selectedJobs.has(j.id)).reduce((sum, j) => sum + (j.price || 0), 0)}
          onClose={() => setShowActions(false)}
          onAction={(actionId) => {
            if (actionId === 'text-reminder' || actionId === 'email-reminder') {
              setShowActions(false);
              setShowSmsReminders(true);
            } else if (actionId === 'message-customers') {
              setShowActions(false);
              setShowNotifyModal(true);
            } else {
              alert(`Action: ${actionId} for ${selectedJobs.size} jobs`);
              setShowActions(false);
              setSelectedJobs(new Set());
              setSelectionMode(false);
            }
          }}
        />
      )}

      {/* Notify Customers Modal — bulk SMS/email broadcast (e.g. van written off) */}
      {showOptimizerModal && (
        <RoundOptimizerModal
          onClose={() => setShowOptimizerModal(false)}
          onApplied={() => setOptimizerCount((c) => Math.max(0, c - 1))}
        />
      )}
      
      {showNotifyModal && (
        <NotifyCustomersModal
          defaultDate={weekDates[selectedDayIndex]}
          apiUrl={API_URL}
          token={localStorage.getItem('streakfree_token')}
          onClose={() => setShowNotifyModal(false)}
        />
      )}
      
      {/* Date Picker Modal */}
      {showDatePicker && (
        <DatePickerModal 
          currentDate={weekDates[selectedDayIndex]}
          onSelect={handleDateSelect}
          onClose={() => setShowDatePicker(false)}
        />
      )}
      
      {/* Schedule Shift Modal — slide every pending job forward/back */}
      {showShiftModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="shift-schedule-modal">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            <div className="px-6 py-5 bg-gradient-to-br from-amber-500 to-orange-600 text-white">
              <h2 className="text-xl font-bold mb-1">Shift entire schedule</h2>
              <p className="text-amber-100 text-sm">
                Move every pending job from today onward forward or backward.
              </p>
            </div>
            <div className="p-6 space-y-5">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
                <strong>Affects:</strong> only jobs scheduled today or later that are still pending.
                Completed, cancelled and skipped jobs are untouched, so your history stays accurate.
                <br /><strong>Customers are NOT notified.</strong> No emails or texts will be sent — you can re-send reminders manually after the shift.
              </div>
              
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-zinc-700">
                  Shift by:
                </label>
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => setShiftDays(d => d - 1)}
                    className="w-10 h-10 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xl font-bold flex items-center justify-center"
                    data-testid="shift-decrement-btn"
                  >
                    −
                  </button>
                  <div className="w-32 text-center">
                    <input
                      type="number"
                      value={shiftDays}
                      onChange={(e) => setShiftDays(parseInt(e.target.value, 10) || 0)}
                      className="w-full text-4xl font-bold text-center bg-transparent border-b-2 border-zinc-300 focus:border-amber-500 outline-none"
                      data-testid="shift-days-input"
                    />
                    <p className="text-xs text-zinc-500 mt-1">days {shiftDays >= 0 ? 'forward' : 'backward'}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShiftDays(d => d + 1)}
                    className="w-10 h-10 rounded-full bg-zinc-100 hover:bg-zinc-200 text-zinc-700 text-xl font-bold flex items-center justify-center"
                    data-testid="shift-increment-btn"
                  >
                    +
                  </button>
                </div>
                
                {/* Quick presets — most common shifts are ±1 day and ±1 week. */}
                <div className="flex flex-wrap gap-2 justify-center pt-1">
                  {[-7, -1, 1, 7].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setShiftDays(n)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        shiftDays === n
                          ? 'bg-amber-500 text-white'
                          : 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700'
                      }`}
                      data-testid={`shift-preset-${n}`}
                    >
                      {n > 0 ? `+${n}` : n}d
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowShiftModal(false)}
                disabled={shiftBusy}
                className="flex-1 py-3 border border-zinc-200 rounded-xl font-medium text-zinc-600 hover:bg-zinc-50"
                data-testid="shift-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleShiftSchedule}
                disabled={shiftBusy || shiftDays === 0}
                className="flex-1 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-xl font-medium"
                data-testid="shift-confirm-btn"
              >
                {shiftBusy ? 'Shifting…' : `Shift ${Math.abs(shiftDays)} day${Math.abs(shiftDays) !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Work Planner Start Date Setup Modal - One-time popup for new users */}
      {showStartDateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl">
            <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
              <h2 className="text-xl font-bold mb-2">Welcome to Work Planner!</h2>
              <p className="text-emerald-100 text-sm">Let's set up your schedule tracking</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">
                  When would you like to start tracking your schedule?
                </label>
                <p className="text-xs text-zinc-500 mb-3">
                  Jobs before this date will be marked as completed automatically. 
                  Jobs after this date that aren't completed will show as overdue.
                </p>
                <input
                  type="date"
                  value={startDateInput}
                  onChange={(e) => setStartDateInput(e.target.value)}
                  className="w-full px-4 py-3 border border-zinc-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  data-testid="start-date-input"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSetStartDate}
                  disabled={!startDateInput || settingStartDate}
                  className="flex-1 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-zinc-300 text-white font-medium rounded-xl transition-colors"
                  data-testid="confirm-start-date-btn"
                >
                  {settingStartDate ? 'Setting up...' : 'Start Tracking'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Replan Confirm Modal - First step */}
      {showReplanConfirm && replanTargetDay && (
        <ReplanConfirmModal 
          jobCount={selectedJobs.size}
          targetDay={replanTargetDay}
          onConfirm={handleReplanConfirm}
          onCancel={handleReplanCancel}
        />
      )}
      
      {/* Replan Schedule Modal - Second step */}
      {showReplanSchedule && replanTargetDay && (
        <ReplanScheduleModal 
          targetDay={replanTargetDay}
          onYes={() => handleReplanComplete(true)}
          onNo={() => handleReplanComplete(false)}
          onCancel={handleReplanCancel}
        />
      )}
      
      {/* Bulk Message Modal */}
      {showBulkMessageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
            <div className="p-4 border-b border-zinc-100">
              <h2 className="text-lg font-semibold text-zinc-900">Message {selectedJobs.size} Customer{selectedJobs.size > 1 ? 's' : ''}</h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Send via</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setBulkMessageType('sms')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${bulkMessageType === 'sms' ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-700'}`}
                  >
                    SMS
                  </button>
                  <button
                    onClick={() => setBulkMessageType('email')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${bulkMessageType === 'email' ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-700'}`}
                  >
                    Email
                  </button>
                  <button
                    onClick={() => setBulkMessageType('both')}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium ${bulkMessageType === 'both' ? 'bg-emerald-500 text-white' : 'bg-zinc-100 text-zinc-700'}`}
                  >
                    Both
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-2">Message</label>
                <textarea
                  value={bulkMessage}
                  onChange={(e) => setBulkMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-zinc-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none resize-none"
                  placeholder="Enter your message..."
                />
              </div>
            </div>
            <div className="p-4 bg-zinc-50 border-t border-zinc-100 flex gap-3">
              <button
                onClick={() => { setShowBulkMessageModal(false); setBulkMessage(''); }}
                className="flex-1 py-2.5 text-zinc-600 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkMessage}
                className="flex-1 py-2.5 bg-emerald-500 text-white font-medium rounded-xl"
              >
                Send
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reassign Worker Modal */}
      {showReassignWorkerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden">
            <div className="p-4 border-b border-zinc-100">
              <h2 className="text-lg font-semibold text-zinc-900">Reassign Worker</h2>
              <p className="text-sm text-zinc-500">{selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''} selected</p>
            </div>
            <div className="p-2">
              {[...staffList, 'Unassigned'].map(worker => (
                <button
                  key={worker}
                  onClick={() => handleReassignWorker(worker)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-zinc-50 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Users size={18} className="text-emerald-600" />
                  </div>
                  <span className="font-medium text-zinc-900">{worker}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-zinc-100">
              <button
                onClick={() => setShowReassignWorkerModal(false)}
                className="w-full py-2.5 text-zinc-600 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Reassign Round Modal */}
      {showReassignRoundModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden">
            <div className="p-4 border-b border-zinc-100">
              <h2 className="text-lg font-semibold text-zinc-900">Reassign Round</h2>
              <p className="text-sm text-zinc-500">{selectedJobs.size} job{selectedJobs.size > 1 ? 's' : ''} selected</p>
            </div>
            <div className="p-2 max-h-64 overflow-y-auto">
              {['Monday Round', 'Tuesday Round', 'Wednesday Round', 'Thursday Round', 'Friday Round', 'Hassocks', 'BH Zone 1', 'BH Zone 2'].map(round => (
                <button
                  key={round}
                  onClick={() => handleReassignRound(round)}
                  className="w-full flex items-center gap-3 p-3 hover:bg-zinc-50 rounded-xl transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                    <MapPin size={18} className="text-amber-600" />
                  </div>
                  <span className="font-medium text-zinc-900">{round}</span>
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-zinc-100">
              <button
                onClick={() => setShowReassignRoundModal(false)}
                className="w-full py-2.5 text-zinc-600 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Assign Unassigned Modal */}
      {showBulkAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" data-testid="bulk-assign-modal">
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden">
            <div className="p-4 border-b border-zinc-100">
              <h2 className="text-lg font-semibold text-zinc-900">
                {bulkAssignWorker ? `Assign to ${bulkAssignWorker}` : 'Assign Unassigned Jobs'}
              </h2>
              <p className="text-sm text-zinc-500">
                {bulkAssignWorker
                  ? `${dayJobs.length} job${dayJobs.length !== 1 ? 's' : ''} on this day`
                  : `${dayJobs.length} unassigned job${dayJobs.length !== 1 ? 's' : ''} on this day — pick a worker`}
              </p>
            </div>
            {!bulkAssignWorker ? (
              <div className="p-2 max-h-64 overflow-y-auto">
                {staffList.map((name, idx) => (
                  <button
                    key={name}
                    onClick={() => setBulkAssignWorker(name)}
                    data-testid={`bulk-assign-worker-${name.toLowerCase().replace(/\s+/g, '_')}`}
                    className="w-full flex items-center gap-3 p-3 hover:bg-zinc-50 rounded-xl transition-colors"
                  >
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: ['#10b981', '#84cc16', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4'][idx % 6] }}
                    >
                      {name.charAt(0)}
                    </div>
                    <span className="font-medium text-zinc-900">{name}</span>
                  </button>
                ))}
                {staffList.length === 0 && (
                  <p className="p-4 text-sm text-zinc-500 text-center">No workers found — add them in Users & Teams first.</p>
                )}
              </div>
            ) : (
              <div className="p-4 space-y-2">
                <button
                  onClick={() => handleBulkAssign(bulkAssignWorker, true)}
                  disabled={bulkAssigning}
                  data-testid="bulk-assign-all-future"
                  className="w-full py-3 px-4 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-50 text-left"
                >
                  These + all future visits
                  <span className="block text-xs font-normal text-emerald-100 mt-0.5">Every future occurrence keeps {bulkAssignWorker}</span>
                </button>
                <button
                  onClick={() => handleBulkAssign(bulkAssignWorker, false)}
                  disabled={bulkAssigning}
                  data-testid="bulk-assign-just-these"
                  className="w-full py-3 px-4 rounded-xl bg-zinc-100 text-zinc-800 font-semibold hover:bg-zinc-200 disabled:opacity-50 text-left"
                >
                  Just these visits
                  <span className="block text-xs font-normal text-zinc-500 mt-0.5">Only the {dayJobs.length} job{dayJobs.length !== 1 ? 's' : ''} on this day</span>
                </button>
              </div>
            )}
            <div className="p-4 border-t border-zinc-100">
              <button
                onClick={() => {
                  if (bulkAssignWorker) { setBulkAssignWorker(null); }
                  else { setShowBulkAssignModal(false); }
                }}
                disabled={bulkAssigning}
                data-testid="bulk-assign-cancel"
                className="w-full py-2.5 text-zinc-600 font-medium"
              >
                {bulkAssignWorker ? 'Back' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Job Modal */}
      {showNewJobModal && (
        <NewJobModal
          onClose={() => setShowNewJobModal(false)}
          onJobCreated={() => {
            setShowNewJobModal(false);
            fetchWeekJobs();
          }}
        />
      )}
      
      {/* Reminder Menu Modal */}
      {showReminderMenu && (
        <ReminderMenuModal
          onClose={() => setShowReminderMenu(false)}
          onSelect={(type) => {
            setSelectedReminderType(type);
            setShowReminderMenu(false);
            setShowReminderSelection(true);
          }}
        />
      )}
      
      {/* Reminder Selection Modal */}
      {showReminderSelection && selectedReminderType && (
        <ReminderSelectionModal
          jobs={numberedDayJobs}
          reminderType={selectedReminderType}
          isSending={sendingReminders}
          onClose={() => {
            setShowReminderSelection(false);
            setSelectedReminderType(null);
          }}
          onSend={async (selectedJobIds) => {
            setSendingReminders(true);
            try {
              const result = await handleSendReminders(selectedReminderType, selectedJobIds);
              
              // Optimistically update jobs to show reminder_sent immediately
              setWeekJobs(prevJobs => prevJobs.map(job => 
                selectedJobIds.includes(job.id) 
                  ? { ...job, reminder_sent: true, reminder_type: selectedReminderType }
                  : job
              ));
              
              setShowReminderSelection(false);
              setSelectedReminderType(null);
              
              // Show confirmation modal with results
              const sentCount = result?.sent_count || 0;
              const failedCount = result?.failed_count || 0;
              const smsFailed = result?.sms_failed || 0;
              const smsConfigured = result?.sms_configured;
              const smsAttempted = result?.sms_attempted || 0;
              const emailSent = result?.email_sent || 0;
              
              // Calculate total successful sends (email + sms)
              const totalSuccessful = sentCount;  // sent_count from backend is the total successful
              
              setReminderConfirmation({
                sent: totalSuccessful,
                failed: failedCount,
                type: selectedReminderType,
                smsFailed: smsFailed,
                smsConfigured: smsConfigured
              });
              
              // Add notification to bell if SMS failed due to not being configured
              if (smsFailed > 0 && !smsConfigured && (selectedReminderType === 'sms' || selectedReminderType === 'both')) {
                addNotification({
                  title: 'SMS Credits Required',
                  message: `${smsFailed} SMS reminder${smsFailed !== 1 ? 's' : ''} could not be sent. Please set up an SMS provider in Settings to send text reminders.`,
                  type: NotificationType.WARNING
                });
              }
              
            } catch (error) {
              console.error('Failed to send reminders:', error);
              // Show error confirmation
              setReminderConfirmation({
                sent: 0,
                failed: selectedJobIds.length,
                type: selectedReminderType,
                error: error.message
              });
              setShowReminderSelection(false);
              setSelectedReminderType(null);
            } finally {
              setSendingReminders(false);
            }
          }}
        />
      )}
      
      {/* Reminder Confirmation Modal */}
      {reminderConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setReminderConfirmation(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 text-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
                reminderConfirmation.sent === 0 ? 'bg-red-100' :
                reminderConfirmation.failed > 0 ? 'bg-amber-100' : 'bg-emerald-100'
              }`}>
                {reminderConfirmation.sent === 0 ? (
                  <XCircle size={32} className="text-red-600" />
                ) : reminderConfirmation.failed > 0 ? (
                  <AlertTriangle size={32} className="text-amber-600" />
                ) : (
                  <CheckCircle2 size={32} className="text-emerald-600" />
                )}
              </div>
              
              <h2 className="text-xl font-bold text-zinc-900 mb-2">
                {reminderConfirmation.sent === 0 && reminderConfirmation.failed > 0 
                  ? 'Failed to Send Reminders' 
                  : 'Reminders Sent!'}
              </h2>
              
              {reminderConfirmation.sent > 0 && (
                <p className="text-zinc-600 mb-2">
                  {reminderConfirmation.type === 'sms' && 'SMS reminders'}
                  {reminderConfirmation.type === 'email' && 'Email reminders'}
                  {reminderConfirmation.type === 'both' && 'SMS & Email reminders'}
                  {' '}sent to <strong>{reminderConfirmation.sent}</strong> customer{reminderConfirmation.sent !== 1 ? 's' : ''}.
                </p>
              )}
              
              {/* SMS specific note - shown subtly since we show success */}
              {reminderConfirmation.smsFailed > 0 && !reminderConfirmation.smsConfigured && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3">
                  <p className="text-amber-700 text-sm font-medium flex items-center gap-2">
                    <AlertTriangle size={16} />
                    SMS Credits Required
                  </p>
                  <p className="text-amber-600 text-xs mt-1">
                    SMS provider not configured. Check the notification bell for details.
                  </p>
                </div>
              )}
              
              {reminderConfirmation.failed > 0 && (
                <p className="text-amber-600 text-sm mb-2">
                  {reminderConfirmation.failed} reminder{reminderConfirmation.failed !== 1 ? 's' : ''} failed to send.
                  {reminderConfirmation.error && <span className="block text-xs mt-1">{reminderConfirmation.error}</span>}
                </p>
              )}
              
              {reminderConfirmation.sent === 0 && reminderConfirmation.error && (
                <p className="text-red-600 text-sm mb-4">
                  {reminderConfirmation.error}
                </p>
              )}
              
              <button
                onClick={() => setReminderConfirmation(null)}
                className="w-full py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors mt-4"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* SMS Compose Modal */}
      <SMSComposeModal
        isOpen={showSMSComposeModal}
        onClose={(success) => {
          setShowSMSComposeModal(false);
          setSmsRecipients([]);
          if (success) {
            setSelectedJobs(new Set());
            toast.success('SMS sent successfully');
          }
        }}
        recipients={smsRecipients}
        context={{
          service: 'your scheduled service'
        }}
      />
      
      {/* Navigation Modal */}
      {showNavigationModal && (
        <NavigationModal
          address={navigationTarget.address}
          lat={navigationTarget.lat}
          lng={navigationTarget.lng}
          savedNavApp={savedNavApp}
          savedNavMode={savedNavMode}
          onSavePreference={saveNavigationPreference}
          onClose={() => setShowNavigationModal(false)}
        />
      )}

      {/* Pin Correction Prompt — shown when worker GPS is 100-1000m from
          the saved customer pin after completing a job */}
      {pinCorrectionSuggestion && (
        <PinCorrectionPromptModal
          suggestion={pinCorrectionSuggestion}
          initialStage={pinCorrectionSuggestion._skip_confirm ? 'map' : 'confirm'}
          onClose={() => setPinCorrectionSuggestion(null)}
          onSaved={(newCoords) => {
            // Patch the new pin into any cached jobs for this customer so
            // the next render shows it correctly without a full re-fetch
            setWeekJobs(prev => prev.map(j =>
              j.customer_id === pinCorrectionSuggestion.customer_id
                ? { ...j, latitude: newCoords.lat, longitude: newCoords.lng }
                : j
            ));
            setPinCorrectionSuggestion(null);
          }}
        />
      )}
      
      {/* Day Note Modal */}
      {showDayNoteModal && (
        <DayNoteModal
          date={toLocalYMD(weekDates[selectedDayIndex])}
          onClose={() => setShowDayNoteModal(false)}
          onNoteSaved={() => {
            const startDate = toLocalYMD(weekDates[0]);
            const endDate = toLocalYMD(weekDates[6]);
            loadDayNotes(startDate, endDate);
          }}
        />
      )}
      
    </div>
  );
};

export default WorkPlanner;
